import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Input } from './ui/input';
import { Play, Pause, Square, Loader2, Crown, HelpCircle, TrendingUp, Trophy, Share2, Palette, LogOut, Clock, Gift, Moon, Sparkles, Mail, Bug, X, Star, MoreHorizontal, AlertTriangle } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from './ui/dropdown-menu';
import CollapsibleSection from './CollapsibleSection';
import MobileTransportBar from './MobileTransportBar';
import Timeline from './Timeline';
import PremiumFeatures from './PremiumFeatures';
import HelpManual from './HelpManual';
import AISessionCreator from './AISessionCreator';
import SessionStats from './SessionStats';
import { toast } from '../hooks/use-toast';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

// New modular components
import SynthAmbientPanel from './SynthAmbientPanel';
import BeatSettingsPanel from './BeatSettingsPanel';
import SessionQuickPickTiles from './SessionQuickPickTiles';
import HeartMonitorPanel from './HeartMonitorPanel';
import useHeartMonitor from '../hooks/useHeartMonitor';
import HrvDashboard from './HrvDashboard';
import ProUpgradeModal from './ProUpgradeModal';
import PostSessionMoodSheet from './PostSessionMoodSheet';
import EightDControlsPanel from './EightDControlsPanel';
import SessionTimerDisplay from './SessionTimerDisplay';
import RecordingControls from './RecordingControls';
import BrainwaveReferenceCard from './BrainwaveReferenceCard';
import ExportSaveDialog from './ExportSaveDialog';
import DeleteConfirmDialog from './DeleteConfirmDialog';
import SetupInstructionsModal from './SetupInstructionsModal';
import DeviceSelectorModal from './DeviceSelectorModal';
import LiveAudioPanel from './LiveAudioPanel';
import PresetManagementPanel from './PresetManagementPanel';
import AchievementsPanel, { ACHIEVEMENTS, computeProgress } from './AchievementsPanel';
import OnboardingTour from './OnboardingTour';
import WelcomeToast from './WelcomeToast';
import SafetyDisclaimer, { hasAcknowledgedSafety } from './SafetyDisclaimer';
import SocialSharePanel from './SocialSharePanel';
import ThemeBuilderModal from './ThemeBuilderModal';
import UpgradeModal from './UpgradeModal';
import ReferralPanel from './ReferralPanel';
import ReferralBanner from './ReferralBanner';
import RecentSessionsStrip from './RecentSessionsStrip';
import LoginPage from './LoginPage';
import EmailPreferencesModal from './EmailPreferencesModal';
import InstallAppButton from './InstallAppButton';
import { openFeedbackDialog } from '../lib/feedback';
import { useTheme } from '../contexts/ThemeContext';
import { SYNTH_PRESETS } from '../hooks/useSynthAmbient';
import useSynthAmbient from '../hooks/useSynthAmbient';
import { useSessionTimer } from '../hooks/useSessionTimer';
import { useRecording } from '../hooks/useRecording';
import { useSleepTimer } from '../hooks/useSleepTimer';
import { useSessionHistory } from '../hooks/useSessionHistory';
import { usePresetSharing } from '../hooks/usePresetSharing';
import { canDeletePreset as _canDeletePreset } from '../lib/presets';
import * as presetApi from '../services/presetApi';
import * as recentAudio from '../lib/recentBackgroundAudio';
import { pushRecent as pushRecentPreset } from '../lib/presetRecency';
import {
  BRAINWAVE_RANGES,
  getBrainwaveRange,
  getBrainwaveDescription,
  formatTime,
  generateExportFilename,
} from '../lib/brainwaves';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BrainwaveGenerator = () => {
  const { theme, themeId, cycleTheme } = useTheme();
  const { user, login, logout } = useAuth();

  // Onboarding: toast is non-blocking; clicking "Quick tour" opens the full tour.
  const [showOnboardingTour, setShowOnboardingTour] = useState(false);
  // Safety disclaimer: shown on first-ever start; re-openable from settings.
  const [showSafety, setShowSafety] = useState(false);
  const [safetyMode, setSafetyMode] = useState('gate'); // 'gate' (must accept) | 'view' (dismissible)
  const [showSocialShare, setShowSocialShare] = useState(false);
  const [showThemeBuilder, setShowThemeBuilder] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [showEmailPrefs, setShowEmailPrefs] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  // Auto-close login modal when user signs in
  useEffect(() => {
    if (user && showLoginModal) {
      setShowLoginModal(false);
    }
  }, [user, showLoginModal]);

  // Celebrate the applied referral bonus with a toast (dispatched by AuthContext)
  useEffect(() => {
    const onApplied = (e) => {
      const days = e?.detail?.bonus_days || 5;
      toast({
        title: `🎉 ${days} bonus days unlocked!`,
        description: `Your friend's referral code just added ${days} days of premium access.`,
      });
    };
    window.addEventListener('neurowave:referral-applied', onApplied);
    return () => window.removeEventListener('neurowave:referral-applied', onApplied);
  }, []);

  // One-time auto-upgrade: if a returning user has custom presets or non-default
  // beat settings, bump them to Advanced. Runs after the initial render so we
  // don't hit a TDZ on `presets` (declared later in the component).
  useEffect(() => {
    try {
      if (localStorage.getItem('neurowave_ui_mode_seen') === 'true') return;
      if (localStorage.getItem('neurowave_ui_mode')) {
        localStorage.setItem('neurowave_ui_mode_seen', 'true');
        return;
      }
    } catch { /* ignore */ }
    // Defer to next tick to pick up freshly-hydrated state
    const t = setTimeout(() => {
      const hasCustomSettings = beatType !== 'binaural' || baseFrequency !== 222;
      if (hasCustomSettings) setUiModePersisted('advanced');
      try { localStorage.setItem('neurowave_ui_mode_seen', 'true'); } catch { /* ignore */ }
    }, 1500);
    return () => clearTimeout(t);
     
  }, []);
  const [editingTheme, setEditingTheme] = useState(null);

  // UI mode — "simple" hides Background Audio + Beat Settings cards; "advanced"
  // shows everything (legacy behavior). Auto-upgrades existing power-users.
  const [uiMode, setUiMode] = useState(() => {
    try {
      const stored = localStorage.getItem('neurowave_ui_mode');
      if (stored === 'simple' || stored === 'advanced') return stored;
    } catch { /* ignore */ }
    return 'simple';
  });
  const setUiModePersisted = (mode) => {
    setUiMode(mode);
    try { localStorage.setItem('neurowave_ui_mode', mode); } catch { /* ignore */ }
  };

  const [isPlaying, setIsPlaying] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(300); // 5 minutes default
  const [volume, setVolume] = useState([0.1]); // 10% default volume
  
  // Sleep timer state — owned by useSleepTimer (Phase 3 refactor).
  const [beatType, setBeatType] = useState('binaural');
  const [baseFrequency, setBaseFrequency] = useState(222); // 222 Hz default (sacred frequency)
  
  // Session timer via hook - ref pattern avoids stale closures in the interval callback
  const autoStopRef = useRef(null);
  const timerCallback = React.useCallback((elapsedDuration) => {
    if (autoStopRef.current) autoStopRef.current(elapsedDuration);
  }, []);
  const sessionTimer = useSessionTimer(timerCallback);
  const currentPresetRef = useRef(null); // Track current preset name for history
  
  // Session history & stats — owned by useSessionHistory (Phase 3 refactor).
  const [showStats, setShowStats] = useState(false);
  const [timelineNodes, setTimelineNodes] = useState([
    { id: '1', time: 0, frequency: 10 },
    { id: '2', time: 60, frequency: 6 },
    { id: '3', time: 120, frequency: 2 },
    { id: '4', time: 180, frequency: 6 },
    { id: '5', time: 240, frequency: 10 },
    { id: '6', time: 300, frequency: 15 }
  ]);
  const [presets, setPresets] = useState([]);
  const [freePresets, setFreePresets] = useState([]);
  const [premiumPresets, setPremiumPresets] = useState([]);
  const [userPresets, setUserPresets] = useState([]);
  const [currentPreset, setCurrentPreset] = useState('');
  const [presetName, setPresetName] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [showPremiumModal, setShowPremiumModal] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('payment') === 'success' || params.get('payment') === 'cancelled';
  });
  const [pausedTime, setPausedTime] = useState(0);
  const [backgroundAudio, setBackgroundAudio] = useState(null);
  const [backgroundAudioVolume, setBackgroundAudioVolume] = useState([0.8]);
  const [backgroundAudioFile, setBackgroundAudioFile] = useState(null);
  const [backgroundAudioEnabled, setBackgroundAudioEnabled] = useState(false);
  const [liveAudioMode, setLiveAudioMode] = useState(false); // Toggle between file and live input
  const [liveAudioStream, setLiveAudioStream] = useState(null);
  const liveAudioSourceRef = useRef(null);
  const liveAnalyserRef = useRef(null);
  const liveAudioGainRef = useRef(null); // Separate gain node for live audio
  const liveAudioAnimationRef = useRef(null);
  const [liveAudioLevel, setLiveAudioLevel] = useState(0);
  const [showSetupInstructions, setShowSetupInstructions] = useState(false);
  const [showDeviceSelector, setShowDeviceSelector] = useState(false);
  const [availableDevices, setAvailableDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState(null);
  const [showHelpManual, setShowHelpManual] = useState(false);
  const [showAISessionCreator, setShowAISessionCreator] = useState(false);
  const [liveAudioMonitoring, setLiveAudioMonitoring] = useState(false); // Monitor live audio output
  
  // Audio device selection states
  const [audioInputDevices, setAudioInputDevices] = useState([]);
  const [audioOutputDevices, setAudioOutputDevices] = useState([]);
  const [selectedInputDevice, setSelectedInputDevice] = useState('default');
  const [selectedOutputDevice, setSelectedOutputDevice] = useState('default');
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  
  // Recording state for MP3 export
  // Recording lifecycle moved to useRecording hook (Phase 2 refactor).
  // Export format — value is one of:
  //   'mp3-192'  → MP3 @ 192 kbps (default — sweet spot)
  //   'mp3-256'  → MP3 @ 256 kbps (audiophile, ~30% larger)
  //   'webm'     → native WebM (smallest, fastest, may not play everywhere)
  const [exportFormat, setExportFormat] = useState(() => {
    const saved = localStorage.getItem('neurowave_export_format');
    // Migrate the old 'mp3' value to 'mp3-192'
    return saved === 'mp3' ? 'mp3-192' : (saved || 'mp3-192');
  });
  useEffect(() => {
    try { localStorage.setItem('neurowave_export_format', exportFormat); } catch { /* ignore */ }
  }, [exportFormat]);
  const [exportFilename, setExportFilename] = useState('');
  
  // Detect if user is on mobile device (Android/iOS) for live audio feature
  const isOnMobileDevice = useMemo(() => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    return /android|iphone|ipad|ipod|mobile/i.test(userAgent);
  }, []);
  const [is8DBackgroundAudio, setIs8DBackgroundAudio] = useState(false);
  const [backgroundAudioSource, setBackgroundAudioSource] = useState(null);
  const [eightDPanningSpeed, setEightDPanningSpeed] = useState([1.0]); // Panning speed multiplier
  const [eightDPath, setEightDPath] = useState('circular'); // circular, figure8, organic
  const [eightDRadius, setEightDRadius] = useState([0.35]); // Orbit radius (increased default for more dramatic effect)
  const [eightDReverbMix, setEightDReverbMix] = useState([0.6]); // Reverb wetness (increased for more spatial feel)
  const [eightDClarityBoost, setEightDClarityBoost] = useState([3]); // High-frequency clarity boost in dB (0-6)
  
  
  // Refs for real-time animation access (avoid closure issues)
  const eightDSpeedRef = useRef(eightDPanningSpeed[0]);
  const eightDPathRef = useRef(eightDPath);
  const eightDRadiusRef = useRef(eightDRadius[0]);
  const [performance8DMode, setPerformance8DMode] = useState(false); // Performance mode for mobile/low-end devices
  const [editingPreset, setEditingPreset] = useState(null); // Currently editing preset
  const [backgroundAudioCurrentTime, setBackgroundAudioCurrentTime] = useState(0);
  const [backgroundAudioDuration, setBackgroundAudioDuration] = useState(0);
  const [backgroundAudioSeeking, setBackgroundAudioSeeking] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [presetToDelete, setPresetToDelete] = useState(null);
  
  // Shareable preset link state — owned by usePresetSharing (Phase 4 refactor).
  const [bgDragActive, setBgDragActive] = useState(false);
  // Recently-used background audio (IndexedDB-backed). Metadata only — the
  // actual Blob is fetched on demand when the user picks a recent.
  const [recentBgAudio, setRecentBgAudio] = useState([]);
  
  // Achievements state — lastUnlockedCount lives in useSessionHistory now.
  const [showAchievements, setShowAchievements] = useState(false);
  
  // Volume automation nodes for timeline
  const [volumeNodes, setVolumeNodes] = useState([
    { id: 'v1', time: 0, volume: 0.5 },
    { id: 'v2', time: 300, volume: 0.5 },
  ]);
  const volumeNodesRef = useRef(volumeNodes);
  const volumeRef = useRef(volume[0]);
  // Refs the animation loop reads on every frame to drive live frequency
  // automation as the playhead advances. Without these, beats stay locked to
  // whatever frequency was current at startAudio() time and never follow the
  // timeline node curve (presets appeared "static" mid-session).
  const timelineNodesRef = useRef(timelineNodes);
  const baseFrequencyRef = useRef(baseFrequency);
  const beatTypeRef = useRef(beatType);
  const lastAppliedBeatFreqRef = useRef(null);
  
  // Subscription context
  const { 
    isPremium, 
    subscriptionStatus,
    trialDaysLeft,
    startTrial,
    checkSubscriptionStatus,
  } = useSubscription();
  
  const audioContextRef = useRef(null);
  const oscillatorsRef = useRef([]);
  const gainNodeRef = useRef(null);
  const limiterRef = useRef(null);
  const heartbeatBufferRef = useRef(null);
  // Holds the latest `startAudio` closure so deferred callers (setTimeout in
  // startCoherenceSession, URL ?coherence=1 auto-launch) invoke the freshly
  // re-rendered version that has updated `beatType` / `baseFrequency` /
  // `timelineNodes` in its closure. Without this, setTimeout captures the
  // pre-state-flush startAudio and starts the wrong beat type (e.g. binaural
  // instead of heartbeat). See bug fix: "try coherence starts as binaural".
  const startAudioRef = useRef(null);
  const animationFrameRef = useRef(null);
  const startTimeRef = useRef(0);
  const isStoppingRef = useRef(false);
  const animationPausedRef = useRef(false);
  const backgroundAudioRef = useRef(null);
  const backgroundGainRef = useRef(null);
  const background8DNodes = useRef(null);
  const background8DAnimation = useRef(null);

  // Persistent passthrough bus that sits between the synth ambient FX output
  // and ctx.destination. Lets startRecording() tap a single stable node so the
  // synth gets captured in MP3/WebM exports — survives preset changes mid-
  // recording (iter 25.5).
  const synthOutputBusRef = useRef(null);

  // WeakMap to track MediaElementSource connections per audio element
  const connectedElements = useRef(new WeakMap());

  // Custom hooks (must be after refs are declared)
  const synthAmbient = useSynthAmbient(audioContextRef, synthOutputBusRef);
  const recording = useRecording({ audioContextRef });
  // Aliases so existing JSX/handlers don't have to change.
  const isRecording = recording.isRecording;

  // Session history + derived stats (Phase 3 refactor).
  const { sessionHistory, totalStats, saveSession: saveSessionToHistory } =
    useSessionHistory({ ACHIEVEMENTS, computeProgress, toast });

  // Sleep timer (Phase 3 refactor). onExpire fires when countdown hits zero;
  // we defer the actual fade-and-stop to a ref so we can reference symbols
  // (stopAudio, synthAmbient) declared later in the file without TDZ pain.
  const sleepExpireRef = useRef(() => {});
  const sleep = useSleepTimer({ onExpire: () => sleepExpireRef.current?.() });
  const { sleepTimerMinutes, sleepTimerRemaining, startSleepTimer } = sleep;

  // Preset sharing flow (Phase 4 refactor) — owns shareLoading/copiedShareId.
  const { shareLoading, copiedShareId, sharePreset } = usePresetSharing({ toast });

  // Brainwave frequency ranges with 100% more vibrant colors
  // Brainwave frequency ranges + colors live in /lib/brainwaves.js. Aliased
  // here so existing JSX references don't change.
  const brainwaveRanges = BRAINWAVE_RANGES;

  // Detect mobile device for performance optimization
  const isMobile = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           window.innerWidth <= 768;
  };
  
  // Helper function to get or create single MediaElementSource per audio element
  const getSingleMediaElementSource = (audioElement) => {
    if (!audioContextRef.current || !audioElement) return null;
    
    const ctx = audioContextRef.current;
    
    // Check if this element already has a source - return existing one (single source rule)
    if (connectedElements.current.has(audioElement)) {
      const existingSource = connectedElements.current.get(audioElement);
      console.log('Reusing existing MediaElementSource (Web Audio API single-source rule)');
      return existingSource;
    }
    
    // Create new MediaElementSource ONLY if none exists for this element
    try {
      const sourceNode = ctx.createMediaElementSource(audioElement);
      connectedElements.current.set(audioElement, sourceNode);
      console.log('Created single MediaElementSource for audio element');
      return sourceNode;
    } catch (error) {
      console.error('Error creating MediaElementSource:', error);
      return null;
    }
  };

  // Create proper 8D audio effect using single source with branched routing
  const create8DBackgroundEffect = (audioElement, force8DState = null) => {
    const use8D = force8DState !== null ? force8DState : is8DBackgroundAudio;
    if (!use8D || !isPremium || !audioContextRef.current) return null;
    
    const ctx = audioContextRef.current;
    const mobile = isMobile();
    const performanceMode = mobile || performance8DMode; // Use performance mode on mobile or when manually enabled
    
    console.log('🎛️ Creating 8D effect with mode:', {
      mobile,
      performance8DMode,
      finalMode: performanceMode ? 'Performance (No Reverb)' : 'Full Quality (With Reverb)',
      hasAudioElement: !!audioElement
    });
    
    // If audioElement provided, get the MediaElementSource
    let source = null;
    if (audioElement) {
      source = getSingleMediaElementSource(audioElement);
      if (!source) {
        console.error('❌ Failed to get MediaElementSource for 8D audio');
        return null;
      }
      console.log('✅ Got MediaElementSource for 8D - NOT disconnecting yet (prevents audio cutoff)');
      setBackgroundAudioSource(source);
    } else {
      console.log('🎹 Creating 8D effect for synth ambient (no MediaElementSource needed)');
    }
    
    // Create single panner node for 8D effect
    const panner = ctx.createPanner();
    
    // Use HRTF for realistic spatial positioning
    panner.panningModel = performanceMode ? 'equalpower' : 'HRTF';
    panner.distanceModel = 'linear';
    panner.refDistance = performanceMode ? 1.5 : 1.0; // Closer distance = more dramatic panning
    panner.maxDistance = performanceMode ? 500 : 1000; 
    panner.rolloffFactor = performanceMode ? 0.8 : 1.0; // Higher rolloff for more noticeable spatial effect
    
    // Additional HRTF enhancements for full quality mode with gentler settings
    if (!performanceMode) {
      panner.coneInnerAngle = 360;
      panner.coneOuterAngle = 360;
      panner.coneOuterGain = 0.1; // Allow some sound even outside the cone
      console.log('Full quality mode: Balanced HRTF panning with gentler parameters');
    }
    
    // Create gain node for volume control
    const eightDGain = ctx.createGain();
    eightDGain.gain.value = 1.0;
    
    // Create output gain node for recording/capture
    const outputGain = ctx.createGain();
    outputGain.gain.value = 1.0;
    
    // SIMPLIFIED 8D: Light for all modes (performance mode even lighter)
    console.log(performanceMode ? 'Ultra-Light 8D Mode' : 'Standard 8D Mode (optimized for browsers)');
    
    // Create input gain as splitter
    const inputGain = ctx.createGain();
    inputGain.gain.value = 1.0;
    
    // Create center mix gain for balanced audio
    const centerMix = ctx.createGain();
    centerMix.gain.value = 0.35; // Reduced for more dramatic panning effect
    
    // Path 1: Panned audio
    inputGain.connect(panner);
    panner.connect(eightDGain);
    eightDGain.connect(outputGain);
    
    // Path 2: Center mix for balance
    inputGain.connect(centerMix);
    centerMix.connect(outputGain);
    
    // NOTE: Do NOT connect outputGain to destination yet
    // We'll connect it through clarity boost or directly depending on mode
    
    // Performance mode: Skip reverb but keep clarity boost for brightness
    if (performanceMode) {
      console.log('✅ Ultra-light 8D Mode (no reverb, but with clarity)');
      
      // Add clarity boost even in performance mode (minimal CPU cost, huge benefit)
      const clarityBoost = ctx.createBiquadFilter();
      clarityBoost.type = 'highshelf';
      clarityBoost.frequency.value = 4000;
      clarityBoost.gain.value = eightDClarityBoost[0];
      clarityBoost.Q.value = 0.7;
      
      // Simple path: outputGain → clarity → destination
      // Synth ambient routes directly to destination; uploaded audio goes through backgroundGainRef
      const finalDestination = audioElement ? backgroundGainRef.current : ctx.destination;
      outputGain.connect(clarityBoost);
      clarityBoost.connect(finalDestination);
      
      console.log('   ✨ Clarity boost active in performance mode:', eightDClarityBoost[0].toFixed(1) + 'dB');
      console.log('   📢 Output destination:', audioElement ? 'backgroundGainRef' : 'destination (isolated)');
      
      return {
        inputGain,
        outputGain,
        panner,
        eightDGain,
        centerMix,
        clarityBoost, // Now included in performance mode!
        angle: 0
      };
    }
    
    // Standard mode: Enhanced spatial reverb with high-frequency preservation
    console.log('Standard 8D: Adding spatial reverb with clarity enhancement...');
    
    // HIGH-FREQUENCY PRESERVATION FILTER
    // Add high-shelf EQ to boost highs and preserve sparkle
    const clarityBoost = ctx.createBiquadFilter();
    clarityBoost.type = 'highshelf';
    clarityBoost.frequency.value = 4000; // Boost frequencies above 4kHz
    clarityBoost.gain.value = eightDClarityBoost[0]; // User-controlled clarity boost (0-6dB)
    clarityBoost.Q.value = 0.7; // Gentle slope
    
    // SIMPLE ROUTING: Same as Performance mode + reverb send
    // This ensures it works exactly like Low CPU mode (which works!)
    
    // Determine final destination - use destination directly for synth ambient, backgroundGainRef for audio element
    const finalDestination = audioElement ? backgroundGainRef.current : ctx.destination;
    
    // Step 1: Main path (IDENTICAL to Performance mode)
    outputGain.connect(clarityBoost);
    clarityBoost.connect(finalDestination);
    
    // Step 2: Add reverb as a simple send/return (parallel to main path)
    const simpleReverb = ctx.createConvolver();
    const reverbGain = ctx.createGain();
    reverbGain.gain.value = eightDReverbMix[0] * 0.15; // Very subtle (0-15%)
    
    // Create VERY SUBTLE impulse response
    const impulseBuffer = ctx.createBuffer(2, ctx.sampleRate * 0.4, ctx.sampleRate);
    for (let channel = 0; channel < 2; channel++) {
      const impulseData = impulseBuffer.getChannelData(channel);
      for (let i = 0; i < impulseData.length; i++) {
        // Extremely fast decay for subtlety
        const decay = Math.exp(-i / (ctx.sampleRate * 0.015));
        // VERY quiet impulse (10% amplitude)
        impulseData[i] = (Math.random() * 2 - 1) * decay * 0.1;
      }
    }
    simpleReverb.buffer = impulseBuffer;
    
    // Connect reverb send (from outputGain, before clarity)
    outputGain.connect(simpleReverb);
    simpleReverb.connect(reverbGain);
    reverbGain.connect(finalDestination);
    
    console.log('✅ Standard 8D active (Full Quality: same as Low CPU + reverb)');
    console.log('   🔊 Clarity boost: +' + eightDClarityBoost[0].toFixed(1) + 'dB @ 4kHz+');
    console.log('   🎵 Reverb: ' + Math.round(eightDReverbMix[0] * 15) + '% (very subtle)');
    console.log('   📊 Main: outputGain → clarity → ' + (audioElement ? 'backgroundGainRef' : 'destination (isolated)'));
    console.log('   📊 Reverb send: outputGain → reverb → ' + (audioElement ? 'backgroundGainRef' : 'destination (isolated)'));
    
    return {
      inputGain,
      outputGain,
      panner,
      eightDGain,
      centerMix,
      simpleReverb,
      reverbGain,
      clarityBoost,
      angle: 0
    }
  };

  // Animate proper 8D background audio effect with mobile optimization
  const animate8DBackground = (force8DEnabled = null) => {
    const eightDEnabled = force8DEnabled !== null ? force8DEnabled : is8DBackgroundAudio;
    
    if (!eightDEnabled || !isPremium || !background8DNodes.current || !background8DNodes.current.panner) {
      // Stop animation if 8D is disabled or nodes are null
      if (background8DAnimation.current) {
        cancelAnimationFrame(background8DAnimation.current);
        background8DAnimation.current = null;
        console.log('8D animation stopped - 8D disabled or nodes missing');
      }
      return;
    }
    
    const mobile = isMobile();
    const performanceMode = mobile || performance8DMode; // Use performance mode on mobile or when manually enabled
    let frameSkipCounter = 0;
    
    const animate = () => {
      // Double-check that nodes still exist during animation
      if (!background8DNodes.current || !background8DNodes.current.panner || !eightDEnabled) {
        console.log('8D animation stopped - nodes null or 8D disabled');
        return;
      }
      
      // Skip frames in performance mode for better performance (30fps instead of 60fps)
      if (performanceMode) {
        frameSkipCounter++;
        if (frameSkipCounter < 2) {
          background8DAnimation.current = requestAnimationFrame(animate);
          return;
        }
        frameSkipCounter = 0;
      }
      
      // 8D effect: multiple path types with adjustable speed
      const speedMultiplier = eightDSpeedRef.current; // Use ref for real-time updates
      const angleIncrement = (performanceMode ? 0.015 : 0.02) * speedMultiplier;
      background8DNodes.current.angle += angleIncrement;
      
      // User-controlled radius (use ref for real-time updates)
      const radius = eightDRadiusRef.current;
      const angle = background8DNodes.current.angle;
      
      let rawX, rawZ;
      
      // Different path types (CPU-efficient) - use ref for real-time updates
      switch (eightDPathRef.current) {
        case 'figure8':
          // Figure-8 (lemniscate) - smooth infinity symbol
          const scale = Math.sin(angle);
          rawX = Math.sin(angle) * Math.cos(angle); // Figure-8 X
          rawZ = Math.sin(angle); // Figure-8 Z
          break;
          
        case 'organic':
          // Organic drift - natural wandering motion
          rawX = Math.sin(angle) + Math.sin(angle * 0.7) * 0.3;
          rawZ = Math.cos(angle) + Math.cos(angle * 0.5) * 0.3;
          break;
          
        case 'circular':
        default:
          // Circular orbit (default)
          rawX = Math.sin(angle);
          rawZ = Math.cos(angle);
          break;
      }
      
      // Apply spatial positioning (more dramatic for noticeable 8D effect)
      const x = rawX * radius; // Direct positioning for stronger effect
      const y = 0; // Keep at ear level  
      const z = rawZ * radius; // Direct positioning for stronger effect
      
      // Apply 3D position for circular motion around listener
      try {
        const currentTime = audioContextRef.current.currentTime;
        background8DNodes.current.panner.positionX.setValueAtTime(x, currentTime);
        background8DNodes.current.panner.positionY.setValueAtTime(y, currentTime);
        background8DNodes.current.panner.positionZ.setValueAtTime(z, currentTime);
        
        // Premium spatial modulation in full quality mode for immersive experience
        if (!performanceMode && background8DNodes.current.eightDGain) {
          const currentTime = audioContextRef.current.currentTime;
          const angle = background8DNodes.current.angle;
          
          // Enhanced volume variation for spatial movement
          const volumeVariation = 0.88 + (Math.sin(angle * 2) * 0.12); // More dynamic range
          background8DNodes.current.eightDGain.gain.setValueAtTime(volumeVariation, currentTime);
          
          // Simple reverb modulation for spatial depth (only if reverb exists)
          if (background8DNodes.current.reverbGain) {
            const reverbVariation = 0.4 + (Math.sin(angle * 1.5) * 0.2);
            background8DNodes.current.reverbGain.gain.setValueAtTime(reverbVariation, currentTime);
          }
          
          // EQ filters removed in optimized version for better CPU efficiency
        }
      } catch (error) {
        console.warn('Error updating 8D position:', error);
      }
      
      // Continue animation if conditions are met (use forced state if provided)
      if ((backgroundAudioEnabled || liveAudioMode) && eightDEnabled && isPremium && background8DNodes.current) {
        background8DAnimation.current = requestAnimationFrame(animate);
      } else {
        console.log('8D animation stopped - conditions not met:', {
          backgroundAudioEnabled,
          liveAudioMode,
          eightDEnabled,
          isPremium,
          hasNodes: !!background8DNodes.current
        });
      }
    };
    
    // Start the animation loop immediately and robustly
    console.log('Starting 8D animation loop with conditions:', {
      eightDEnabled,
      isPremium,
      hasNodes: !!background8DNodes.current,
      backgroundAudioEnabled
    });
    
    // Force immediate first frame
    animate();
    
    // Also schedule next frame to ensure loop continues
    setTimeout(() => {
      if (background8DNodes.current && eightDEnabled) {
        console.log('Ensuring 8D animation loop continuation...');
        animate();
      }
    }, 50);
  };

  // Format time for display (mm:ss)
  // formatTime helper moved to lib/brainwaves.js (Phase 1 refactor).

  // Handle background audio seeking
  const handleBackgroundAudioSeek = (newTime) => {
    if (backgroundAudio && backgroundAudioDuration > 0) {
      setBackgroundAudioSeeking(true);
      const seekTime = (newTime[0] / 100) * backgroundAudioDuration;
      backgroundAudio.currentTime = seekTime;
      setBackgroundAudioCurrentTime(seekTime);
      
      // Re-enable time updates after a short delay
      setTimeout(() => {
        setBackgroundAudioSeeking(false);
      }, 100);
    }
  };

  // Get current progress as percentage
  const getBackgroundAudioProgress = () => {
    if (backgroundAudioDuration === 0) return [0];
    return [(backgroundAudioCurrentTime / backgroundAudioDuration) * 100];
  };

  // Delete preset — Phase 4: API call moved to services/presetApi.js.
  const deletePreset = async (presetId, presetName) => {
    if (!isPremium) {
      setShowPremiumModal(true);
      toast({
        title: "Premium Feature",
        description: "Preset management is available for Premium users only.",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      const ok = await presetApi.deletePresetById(presetId);
      if (ok) {
        setPresets(presets.filter(p => p.id !== presetId));
        if (currentPreset === presetId) setCurrentPreset('');
        toast({
          title: "Preset Deleted",
          description: `"${presetName}" has been removed from your presets`,
        });
        setShowDeleteConfirm(false);
        setPresetToDelete(null);
      }
    } catch (error) {
      console.error('Error deleting preset:', error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete preset. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirmation
  const handleDeletePreset = (preset) => {
    setPresetToDelete(preset);
    setShowDeleteConfirm(true);
  };

  // Handle edit preset
  const handleEditPreset = (preset) => {
    if (preset.is_builtin) {
      // For builtin presets, load the data for editing and prepare for save-as-user
      setEditingPreset(preset);
      setPresetName(`${preset.name} (Copy)`);
      loadPreset(preset.id, preset);
    } else {
      // For user presets, allow direct editing
      setEditingPreset(preset);
      setPresetName(preset.name);
      loadPreset(preset.id, preset);
    }
  };

  // Share preset — Phase 4: thin wrapper around usePresetSharing hook.
  const handleSharePreset = (preset) => sharePreset(preset);

  // Handle AI-generated session
  const handleAISessionGenerated = (aiSession) => {
    try {
      // Apply AI-generated session to current state
      setPresetName(aiSession.name || 'AI Generated Session');
      setDuration(aiSession.duration || 1200);
      setBeatType(aiSession.beat_type || 'binaural');
      setBaseFrequency(aiSession.base_frequency || 432);
      
      // Convert timeline nodes
      if (aiSession.timeline_nodes && aiSession.timeline_nodes.length > 0) {
        const nodes = aiSession.timeline_nodes.map((node, index) => ({
          id: `ai_${Date.now()}_${index}`,
          time: node.time,
          frequency: node.frequency
        }));
        setTimelineNodes(nodes);
      }
      
      // Apply 8D audio settings if specified
      if (aiSession['8d_audio_enabled']) {
        setIs8DBackgroundAudio(true);
        if (aiSession['8d_panning_speed']) {
          setEightDPanningSpeed([aiSession['8d_panning_speed']]);
        }
      }
      
      toast({
        title: "AI Session Loaded",
        description: aiSession.description || "AI-generated session has been applied. You can now play or modify it.",
      });
    } catch (error) {
      console.error('Error applying AI session:', error);
      toast({
        title: "Error",
        description: "Failed to apply AI-generated session",
        variant: "destructive",
      });
    }
  };

  // canDeletePreset moved to lib/presets.js (Phase 4 refactor).
  const canDeletePreset = _canDeletePreset;

  // Session-history hydration moved to useSessionHistory (Phase 3 refactor).

  // Hydrate the recent-background-audio list from IndexedDB on mount, and
  // listen for paste events so users can Cmd-V / Ctrl-V an audio file
  // straight onto the dashboard.
  useEffect(() => {
    let cancelled = false;
    recentAudio.getRecentMeta()
      .then((items) => { if (!cancelled) setRecentBgAudio(items); })
      .catch((e) => console.warn('Load recent audio failed:', e));

    const onPaste = (e) => {
      // Skip when the user is pasting into a real input/textarea/contenteditable.
      const t = e.target;
      const tag = t?.tagName;
      const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || t?.isContentEditable;
      if (isTyping) return;

      const files = e.clipboardData?.files;
      if (!files || files.length === 0) return;
      const file = files[0];
      const looksLikeAudio = file.type?.startsWith('audio/') ||
        /\.(mp3|wav|ogg|m4a|aac|flac|webm)$/i.test(file.name || '');
      if (!looksLikeAudio) return;

      e.preventDefault();
      processBackgroundAudioFile(file);
      toast({
        title: 'Pasted audio',
        description: `"${file.name}" loaded as background track.`,
      });
    };

    window.addEventListener('paste', onPaste);
    return () => {
      cancelled = true;
      window.removeEventListener('paste', onPaste);
    };
    // processBackgroundAudioFile is stable for this lifetime; toast is from
    // a stable hook. Mount-only is intentional.
  }, []);

  // Load a recent file by id — fetch the Blob from IDB, wrap in a File, and
  // route it through the standard upload pipeline.
  const loadRecentBackgroundAudio = async (id) => {
    try {
      const all = await recentAudio.getRecent();
      const entry = all.find((e) => e.id === id);
      if (!entry) {
        toast({
          title: 'Recent file not found',
          description: 'It may have been removed. Try uploading again.',
          variant: 'destructive',
        });
        setRecentBgAudio(await recentAudio.getRecentMeta());
        return;
      }
      // entry.blob is a stored Blob; wrap as File so existing code that
      // reads file.name/.type/.size keeps working unchanged.
      const file = entry.blob instanceof File
        ? entry.blob
        : new File([entry.blob], entry.name, { type: entry.type });
      processBackgroundAudioFile(file);
    } catch (e) {
      console.warn('Load recent audio failed:', e);
      toast({
        title: 'Could not load file',
        description: 'Storage may be unavailable. Try re-uploading.',
        variant: 'destructive',
      });
    }
  };

  const removeRecentBackgroundAudio = async (id, e) => {
    e?.stopPropagation();
    try {
      await recentAudio.removeRecent(id);
      setRecentBgAudio(await recentAudio.getRecentMeta());
    } catch (err) {
      console.warn('Remove recent failed:', err);
    }
  };

  const bgDragIdRef = useRef(null);
  const [bgDragOverId, setBgDragOverId] = useState(null);

  // Tracks whether the synth ambient was on when the user last hit Stop or
  // Pause, so global Play can re-start it (Option A coupling — synth follows
  // global play/stop fully).
  const synthShouldResumeRef = useRef(false);

  // Heartbeat "coherence" — HRV jitter percentage (0–10). Persisted so the
  // user's preferred organic-vs-strict feel sticks. The scheduler reads it
  // via coherenceRef when it spins up; live changes during playback go
  // through wrapper.setHrv() without rebuilding any audio nodes.
  const [coherence, setCoherence] = useState(() => {
    const saved = parseInt(localStorage.getItem('neurowave_heartbeat_coherence'), 10);
    return Number.isFinite(saved) && saved >= 0 && saved <= 10 ? saved : 4;
  });
  const coherenceRef = useRef(coherence);
  useEffect(() => {
    coherenceRef.current = coherence;
    try { localStorage.setItem('neurowave_heartbeat_coherence', String(coherence)); }
    catch { /* ignore */ }
    // Live-apply to a running heartbeat scheduler.
    if (beatType === 'heartbeat' && oscillatorsRef.current?.[0]?.setHrv) {
      oscillatorsRef.current[0].setHrv(coherence);
    }
  }, [coherence, beatType]);

  // Heart monitor (Web Bluetooth) — optional live HRV source. The hook is
  // always instantiated (cheap — does nothing until connect() is called)
  // but only renders UI on supported browsers. When connected AND the
  // "Sync heartbeat to my heart" toggle is on, the heartbeat scheduler
  // fires a beat on every R-peak instead of running its own BPM timer —
  // the audio literally beats in sync with the user's actual heart.
  const heartMonitor = useHeartMonitor({ windowMs: 60_000 });
  const [syncHeartbeatToUser, setSyncHeartbeatToUser] = useState(false);
  // Ref mirror for the R-peak stream because the scheduler is plain JS
  // (Web Audio timer), not inside React's render cycle.
  const syncHeartbeatToUserRef = useRef(false);
  useEffect(() => { syncHeartbeatToUserRef.current = syncHeartbeatToUser; }, [syncHeartbeatToUser]);
  // Per-session guard so `stopAudio` only posts one HRV snapshot per
  // session — reset on every `startAudio`. Prevents duplicates when
  // handleAutoStop calls stopAudio after fadeOut.
  const hrvSnapshotPostedRef = useRef(false);
  // Toggles the Pro upsell modal (opened from HR panel's pricing teaser and
  // from the subscription-status query `?upgrade=pro`).
  const [proModalOpen, setProModalOpen] = useState(false);
  // Post-session mood-rating sheet. `moodSheetContext` carries the session
  // we just finished so the rating row can be attributed properly. Triggers
  // fire from `handleAutoStop` only (manual Stop doesn't rate — users who
  // bail mid-session aren't good rating signal).
  const [moodSheetOpen, setMoodSheetOpen] = useState(false);
  const [moodSheetContext, setMoodSheetContext] = useState(null);

  // Propagate the sync toggle into the heartbeat scheduler wrapper so the
  // scheduled loop actually stops queuing beats when sync is on.
  useEffect(() => {
    const hb = oscillatorsRef.current?.[0];
    if (hb?.setSyncExternal) hb.setSyncExternal(syncHeartbeatToUser);
  }, [syncHeartbeatToUser, isPlaying]);

  // Subscribe the hook's R-peak stream to the heartbeat scheduler. On each
  // R-peak, if sync is on and the scheduler is running, fire an
  // immediate audio beat (if its wrapper exposes triggerBeatNow). The
  // scheduler was updated to expose this method alongside resync.
  useEffect(() => {
    heartMonitor.setOnRPeak((peakTs /*, rrMs */) => {
      if (!syncHeartbeatToUserRef.current) return;
      const hb = oscillatorsRef.current?.[0];
      if (hb?.triggerBeatNow) {
        try { hb.triggerBeatNow(); } catch (e) { /* ignore */ }
      }
    });
    return () => heartMonitor.setOnRPeak(null);
  }, [heartMonitor]);

  // Pro-checkout round-trip handler. When Stripe redirects back with
  // ?payment=success&pro=1&session_id=... we poll the payment-status
  // endpoint (which already knows to flip pro_status for the Pro product)
  // then refresh the subscription context so the UI reflects the new Pro
  // status immediately. Scrubs the query string on completion so refreshes
  // don't re-trigger. Runs once on mount.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('pro') !== '1') return;
    const payment = params.get('payment');
    const sessionId = params.get('session_id');
    if (payment === 'cancelled') {
      toast({ title: 'Pro checkout cancelled', description: 'You can try again anytime.' });
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    if (payment !== 'success' || !sessionId) return;

    let cancelled = false;
    let attempts = 0;
    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`,
          { credentials: 'include' }
        );
        const data = await res.json();
        if (data?.payment_status === 'paid') {
          toast({
            title: '💓 Welcome to NeuroWave Pro',
            description: 'Live Heart Monitor + HRV dashboard are yours forever.',
          });
          await checkSubscriptionStatus();
          window.history.replaceState({}, '', window.location.pathname);
          return;
        }
      } catch { /* ignore, retry */ }
      attempts += 1;
      if (attempts < 10) setTimeout(poll, 2000);
      else {
        toast({
          title: 'Payment processing…',
          description: 'Refresh in a moment to confirm — we\'re finalising with Stripe.',
        });
        window.history.replaceState({}, '', window.location.pathname);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  // Page Visibility — re-baseline the heartbeat scheduler when the tab
  // returns to the foreground.
  //
  // Why this matters on mobile: Chrome Android aggressively throttles
  // `setTimeout` in backgrounded tabs (and when the screen dims on battery
  // saver). When the user unlocks the phone or re-focuses the tab, the
  // scheduler's `nextBeatTime` may be stale — either already in the past
  // (because wake-ups were skipped) or bunched inside the upcoming
  // lookahead window. Either state produces an audible cluster of beats in
  // the first second after resume.
  //
  // Calling `resync()` clamps `nextBeatTime` back to `ctx.currentTime +
  // 50ms` but only if the drift is outside normal operating range (<0 or
  // >1s) — small routine drift is left alone so a brief screen-off period
  // during a meditation isn't re-baselined unnecessarily.
  //
  // We deliberately DO NOT call `ctx.suspend()` on visibility change. Many
  // mobile users play with the screen off via wired or Bluetooth
  // headphones — the browser already handles background audio correctly
  // for that use case, and pre-emptively suspending would introduce a
  // silence gap in exactly the scenario we want to protect.
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const hb = oscillatorsRef.current?.[0];
      if (hb?.resync) hb.resync();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, []);

  // Breath pacer — visual-only breathing guide. Persisted independently from
  // the heartbeat scheduler so the user can leave it set to their preferred
  // resonance frequency (commonly 5.5 bpm) and toggle it on whenever they
  // play heartbeat sessions.
  const [breathPacerEnabled, setBreathPacerEnabled] = useState(() => {
    return localStorage.getItem('neurowave_breath_pacer_enabled') === '1';
  });
  const [breathPacerBpm, setBreathPacerBpm] = useState(() => {
    const saved = parseFloat(localStorage.getItem('neurowave_breath_pacer_bpm'));
    return Number.isFinite(saved) && saved >= 3 && saved <= 10 ? saved : 5.5;
  });
  useEffect(() => {
    try { localStorage.setItem('neurowave_breath_pacer_enabled', breathPacerEnabled ? '1' : '0'); }
    catch { /* ignore */ }
  }, [breathPacerEnabled]);
  useEffect(() => {
    try { localStorage.setItem('neurowave_breath_pacer_bpm', String(breathPacerBpm)); }
    catch { /* ignore */ }
  }, [breathPacerBpm]);

  // RSA (respiratory sinus arrhythmia) — refs that mirror breath pacer state
  // so the heartbeat scheduler (which lives outside React's render cycle and
  // uses setTimeout/Web Audio) can read the latest values without re-renders.
  // The phase anchor resets whenever breath pacer toggles on or BPM changes,
  // matching BreathPacer.jsx's own internal startRef so the visual circle and
  // the audible heart-rate modulation stay in phase.
  const breathPacerEnabledRef = useRef(breathPacerEnabled);
  const breathPacerBpmRef = useRef(breathPacerBpm);
  const breathPhaseAnchorRef = useRef(performance.now());
  useEffect(() => {
    breathPacerEnabledRef.current = breathPacerEnabled;
    if (breathPacerEnabled) breathPhaseAnchorRef.current = performance.now();
  }, [breathPacerEnabled]);
  useEffect(() => {
    breathPacerBpmRef.current = breathPacerBpm;
    breathPhaseAnchorRef.current = performance.now();
  }, [breathPacerBpm]);

  // Intro pulse on the breath pacer — a 5-second highlight ring + "✨ Sync your
  // breath" banner shown only when an auto-launched Coherence preview begins
  // (URL ?coherence=1 entry). Helps first-time visitors arriving from the
  // landing-page hero CTA orient to the breathing guide. Self-clearing.
  const [breathPacerIntroPulse, setBreathPacerIntroPulse] = useState(false);
  const breathPacerIntroTimeoutRef = useRef(null);
  const triggerBreathPacerIntroPulse = () => {
    if (breathPacerIntroTimeoutRef.current) {
      clearTimeout(breathPacerIntroTimeoutRef.current);
    }
    setBreathPacerIntroPulse(true);
    breathPacerIntroTimeoutRef.current = setTimeout(() => {
      setBreathPacerIntroPulse(false);
      breathPacerIntroTimeoutRef.current = null;
    }, 5000);
  };
  useEffect(() => {
    return () => {
      if (breathPacerIntroTimeoutRef.current) {
        clearTimeout(breathPacerIntroTimeoutRef.current);
      }
    };
  }, []);

  const togglePinRecentBackgroundAudio = async (id, e) => {
    e?.stopPropagation();
    try {
      await recentAudio.togglePin(id);
      setRecentBgAudio(await recentAudio.getRecentMeta());
    } catch (err) {
      console.warn('Toggle pin failed:', err);
    }
  };

  // One-click "Coherence Session" launcher — applies the full HeartMath-style
  // setup in one tap: heartbeat at 60 BPM with realistic HRV jitter, breath
  // pacer at the resonance frequency (5.5 bpm), and a calm Alpha→Theta-edge
  // background over 10 minutes. State updates are React-batched so we wait one
  // tick before pressing Play (matches loadPreset's existing pattern).
  const startCoherenceSession = async ({ duration = 600 } = {}) => {
    // If already playing, fully await stopAudio() before reconfiguring — it
    // sets isStoppingRef.current=true plus a few async cleanups, and a
    // too-eager startAudio() would no-op while that's still in flight.
    if (isPlaying || isPaused) {
      try { await stopAudio(); }
      catch (e) { console.warn('Stop before coherence session failed:', e); }
    }

    // The /heartbeat-sample.wav fetch is started when the audio engine first
    // initialises, but on a slow network the buffer may not be ready by the
    // time we'd otherwise call startAudio. Without this gate, generateBeat()
    // hits its `if (!heartbeatBufferRef.current)` early-return, returns null,
    // startAudio throws "Failed to generate audio oscillators", and the user
    // sees a generic "Audio Error" toast even though the only thing wrong was
    // a slow network. Wait up to 4s — heartbeat samples are <100KB so this
    // is plenty even on a flaky 3G connection.
    if (!heartbeatBufferRef.current) {
      console.log('💓 Coherence: waiting for heartbeat sample to load…');
      const waitStart = Date.now();
      while (!heartbeatBufferRef.current && Date.now() - waitStart < 4000) {
        await new Promise((res) => setTimeout(res, 100));
      }
      if (!heartbeatBufferRef.current) {
        toast({
          title: 'Heartbeat sample not ready',
          description: 'Network looks slow — try again in a moment.',
          variant: 'destructive',
        });
        return;
      }
    }

    // Timeline scaled to the requested duration. The 2-min preview keeps the
    // same Alpha → Theta-edge shape, just compressed.
    const isPreview = duration < 300;
    const nodes = isPreview
      ? [
          { id: 'cp1', time: 0,                            frequency: 10 },
          { id: 'cp2', time: Math.round(duration / 2),     frequency: 9  },
          { id: 'cp3', time: duration,                     frequency: 8  },
        ]
      : [
          { id: 'c1', time: 0,   frequency: 10 },
          { id: 'c2', time: 240, frequency: 9  },
          { id: 'c3', time: 480, frequency: 8  },
          { id: 'c4', time: 600, frequency: 8  },
        ];

    setTimelineNodes(nodes);
    setBeatType('heartbeat');
    setBaseFrequency(60);     // 60 BPM resting heart
    setDuration(duration);
    setCoherence(4);          // organic ±4% HRV
    setBreathPacerEnabled(true);
    setBreathPacerBpm(5.5);   // peak HRV resonance

    toast({
      title: '💓 Coherence Mode',
      description: isPreview
        ? `${Math.round(duration / 60)}-min preview · sync your breath to the circle.`
        : 'Heartbeat at 60 BPM, breath at 5.5 bpm. Sync your breath to the circle.',
    });

    // Wait for state batch to flush, then start audio. The setTimeout callback
    // intentionally calls `startAudioRef.current` (not `startAudio` directly)
    // so it grabs the *latest* startAudio closure post-render — otherwise the
    // engine would generate the previous beat type (e.g. binaural instead of
    // heartbeat) because this function's closure was captured pre-flush.
    // 220ms matches the delay used by loadPreset() so the audio engine sees
    // the new state. Pass skipPlayingCheck=true because we just awaited
    // stopAudio() but React state (isPlaying) may not have updated yet.
    setTimeout(() => { startAudioRef.current?.(true); }, 220);
  };

  // Quick-pick session launchers powering the "What do you need right now?"
  // tile row. Each mirrors the proven startCoherenceSession pattern: optional
  // stop, state setters, toast, then a 220ms deferred startAudioRef.current()
  // call so React has flushed state before the audio engine reads it. The
  // protocols are evidence-based: Focus uses high-Beta, Calm uses Alpha at
  // the top of the "relaxed awareness" range, Sleep drops through Delta.
  const startFocusSession = async () => {
    if (isPlaying || isPaused) {
      try { await stopAudio(); } catch (e) { console.warn('Stop before focus session failed:', e); }
    }
    // Beta 14–16 Hz — the "engaged concentration" band. Studies (Hammond
    // 2011, Gruzelier 2014) repeatedly show Beta entrainment improves
    // sustained-attention task performance. Start at 16 Hz, settle to 14.
    setTimelineNodes([
      { id: 'f1', time: 0,    frequency: 16 },
      { id: 'f2', time: 600,  frequency: 15 },
      { id: 'f3', time: 1500, frequency: 14 },
    ]);
    setBeatType('binaural');
    setBaseFrequency(200);
    setDuration(1500); // 25 min — a Pomodoro
    setCoherence(0);
    setBreathPacerEnabled(false);
    toast({
      title: '🎯 Focus Mode',
      description: '25-min Beta session · binaural beats · for deep work.',
    });
    setTimeout(() => { startAudioRef.current?.(true); }, 220);
  };

  const startCalmSession = async () => {
    if (isPlaying || isPaused) {
      try { await stopAudio(); } catch (e) { console.warn('Stop before calm session failed:', e); }
    }
    // Alpha 8–10 Hz — the "relaxed awareness" band. 10 min is long enough
    // to shift state but short enough to fit between meetings.
    setTimelineNodes([
      { id: 'ca1', time: 0,   frequency: 10 },
      { id: 'ca2', time: 300, frequency: 9  },
      { id: 'ca3', time: 600, frequency: 8  },
    ]);
    setBeatType('binaural');
    setBaseFrequency(180);
    setDuration(600);
    setCoherence(0);
    setBreathPacerEnabled(true);
    setBreathPacerBpm(5.5);
    toast({
      title: '🌿 Calm Reset',
      description: '10-min Alpha session · breath at 5.5 bpm · slow down.',
    });
    setTimeout(() => { startAudioRef.current?.(true); }, 220);
  };

  const startSleepSession = async () => {
    if (isPlaying || isPaused) {
      try { await stopAudio(); } catch (e) { console.warn('Stop before sleep session failed:', e); }
    }
    // Delta 1–4 Hz — the deep-sleep band. Heartbeat at 55 BPM (below
    // resting baseline) signals "your heart is slowing, you can too." Long
    // 30-min timeline with a slow drift down; the sleep timer auto-fades
    // out so the audio doesn't wake the user once they're under.
    setTimelineNodes([
      { id: 's1', time: 0,    frequency: 4   },
      { id: 's2', time: 300,  frequency: 3   },
      { id: 's3', time: 900,  frequency: 2   },
      { id: 's4', time: 1800, frequency: 1   },
    ]);
    setBeatType('heartbeat');
    setBaseFrequency(55);
    setDuration(1800);
    setCoherence(2); // mild HRV, keeps the heartbeat organic
    setBreathPacerEnabled(false);
    // Arm the sleep timer at 30 min via useSleepTimer's startSleepTimer —
    // this triggers the existing sleep-timer effect which handles the
    // gentle fade-out when the countdown hits zero.
    if (typeof startSleepTimer === 'function') startSleepTimer(30);
    toast({
      title: '🌙 Sleep',
      description: '30-min Delta session · heartbeat · auto-fades out.',
    });
    setTimeout(() => { startAudioRef.current?.(true); }, 220);
  };

  // Auto-launch from a `?coherence=1` URL param. The homepage hero pill points
  // here so curious visitors land directly into a 2-min Coherence preview
  // without having to hunt for the in-app CTA. Runs once after the audio
  // engine is initialized so startAudio doesn't fire before refs are ready.
  const coherenceAutostartedRef = useRef(false);
  useEffect(() => {
    if (coherenceAutostartedRef.current) return;
    if (!audioContextRef.current || !gainNodeRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('coherence') !== '1') return;

    coherenceAutostartedRef.current = true;
    // Strip the param so a refresh doesn't auto-replay and the URL stays
    // clean as just /app.
    const cleaned = window.location.pathname + window.location.hash;
    window.history.replaceState({}, '', cleaned);

    // Force Advanced UI mode for the auto-launched preview so the Breath
    // Pacer + Coherence slider are actually visible. New visitors arrive in
    // simple mode by default, where BeatSettingsPanel (which hosts the
    // pacer) is hidden entirely. This is a one-shot switch — the user can
    // still flip back to Simple via the toggle whenever they want.
    setUiModePersisted('advanced');

    // Small delay so the toast lands alongside audio start, not before.
    setTimeout(() => {
      startCoherenceSession({ duration: 120 });
      // Highlight the breath pacer for 5s so first-time visitors landing
      // from the hero CTA know to follow the circle. Manual in-app CTA
      // clicks intentionally skip this — those users already see the UI.
      triggerBreathPacerIntroPulse();
    }, 400);
  }, [isStarting]);


  const clearUnpinnedBackgroundAudio = async () => {
    const unpinnedCount = recentBgAudio.filter((r) => !r.pinned).length;
    if (unpinnedCount === 0) return;
    // Native confirm is the right call here — wiping 5 items accidentally sucks.
    if (!window.confirm(`Clear ${unpinnedCount} unpinned recent audio file${unpinnedCount === 1 ? '' : 's'}? Pinned files are safe.`)) return;
    try {
      await recentAudio.clearUnpinned();
      setRecentBgAudio(await recentAudio.getRecentMeta());
      toast({ title: 'Cleared', description: `${unpinnedCount} unpinned recent file${unpinnedCount === 1 ? '' : 's'} removed.` });
    } catch (err) {
      console.warn('Clear unpinned failed:', err);
    }
  };

  // Drag-reorder handlers — only active on pinned pills.
  const handleRecentDragStart = (id, e) => {
    bgDragIdRef.current = id;
    setBgDragOverId(null);
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', id);
    } catch { /* Safari may throw */ }
  };

  const handleRecentDragOver = (id, e) => {
    if (!bgDragIdRef.current) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch { /* ignore */ }
    if (bgDragOverId !== id) setBgDragOverId(id);
  };

  const handleRecentDragEnd = () => {
    bgDragIdRef.current = null;
    setBgDragOverId(null);
  };

  const handleRecentDrop = async (targetId, e) => {
    e.preventDefault();
    const sourceId = bgDragIdRef.current;
    bgDragIdRef.current = null;
    setBgDragOverId(null);
    if (!sourceId || sourceId === targetId) return;

    const pinned = recentBgAudio.filter((r) => r.pinned);
    const source = pinned.find((r) => r.id === sourceId);
    const target = pinned.find((r) => r.id === targetId);
    // Only allow reorder when both source and target are pinned.
    if (!source || !target) return;

    const withoutSource = pinned.filter((r) => r.id !== sourceId);
    const insertAt = withoutSource.findIndex((r) => r.id === targetId);
    withoutSource.splice(insertAt < 0 ? withoutSource.length : insertAt, 0, source);

    try {
      await recentAudio.reorderPinned(withoutSource.map((r) => r.id));
      setRecentBgAudio(await recentAudio.getRecentMeta());
    } catch (err) {
      console.warn('Reorder failed:', err);
    }
  };


  // Apply referral code if present in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode && user) {
      const applyRef = async () => {
        try {
          const resp = await fetch(`${API}/referral/apply`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code: refCode }),
          });
          const data = await resp.json();
          if (resp.ok && data.success) {
            toast({ title: 'Referral Applied!', description: data.message });
          }
        } catch { /* silently ignore */ }
        window.history.replaceState({}, '', window.location.pathname);
      };
      applyRef();
    }
  }, [user]);
  
  // Calculate statistics from session history
  // calculateStats / checkNewAchievements / saveSessionToHistory all live in
  // useSessionHistory now (Phase 3 refactor). The component receives them via
  // the hook return, so callers (e.g. handleAutoStop) keep working.

  // Get current volume from volume automation nodes
  const getCurrentVolume = (time = currentTime) => {
    if (!volumeNodes || volumeNodes.length === 0) return volume[0];
    const sorted = [...volumeNodes].sort((a, b) => a.time - b.time);
    if (time <= sorted[0].time) return sorted[0].volume;
    if (time >= sorted[sorted.length - 1].time) return sorted[sorted.length - 1].volume;
    for (let i = 0; i < sorted.length - 1; i++) {
      if (time >= sorted[i].time && time <= sorted[i + 1].time) {
        const t = (time - sorted[i].time) / (sorted[i + 1].time - sorted[i].time);
        return sorted[i].volume + t * (sorted[i + 1].volume - sorted[i].volume);
      }
    }
    return volume[0];
  };

  // saveSessionToHistory now provided by useSessionHistory (Phase 3 refactor).
  
  // Initialize Web Audio API and load presets
  useEffect(() => {
    if (!audioContextRef.current) {
      // Create AudioContext with optimized settings to prevent glitches
      // Using 48kHz for better high-frequency preservation in recordings (Opus codec optimal)
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
        latencyHint: 'playback', // Prioritize smooth playback over low latency (larger buffer = fewer glitches)
        sampleRate: 48000 // 48kHz - Optimal for Opus codec, preserves high frequencies better
      });
      
      console.log('🎵 AudioContext created - latencyHint: playback, sampleRate:', audioContextRef.current.sampleRate);
      console.log('📊 Audio buffer optimized for glitch-free playback (may have slight delay)');
      
      // Create a limiter/compressor to prevent audio clipping and distortion
      limiterRef.current = audioContextRef.current.createDynamicsCompressor();
      limiterRef.current.threshold.setValueAtTime(-6, audioContextRef.current.currentTime); // Start compressing at -6dB (gentler)
      limiterRef.current.knee.setValueAtTime(6, audioContextRef.current.currentTime); // Softer knee for smoother compression
      limiterRef.current.ratio.setValueAtTime(4, audioContextRef.current.currentTime); // Lower ratio (less aggressive)
      limiterRef.current.attack.setValueAtTime(0.01, audioContextRef.current.currentTime); // Slower attack (10ms) - prevents crackling
      limiterRef.current.release.setValueAtTime(0.25, audioContextRef.current.currentTime); // Longer release for smoother sound
      limiterRef.current.connect(audioContextRef.current.destination);
      console.log('🔊 Audio limiter enabled (gentle settings to prevent crackling)');
      
      gainNodeRef.current = audioContextRef.current.createGain();
      gainNodeRef.current.connect(limiterRef.current);
      gainNodeRef.current.gain.value = 0; // Start silent, fade in on play
      
      // Create background audio gain node
      backgroundGainRef.current = audioContextRef.current.createGain();
      backgroundGainRef.current.connect(audioContextRef.current.destination);
      backgroundGainRef.current.gain.value = backgroundAudioVolume[0];

      // Synth ambient output bus — stable tap point for recording. The synth
      // hook connects its FX output here; this node bridges to destination.
      // Created once and reused so preset changes / start-stop cycles don't
      // break the recording connection.
      synthOutputBusRef.current = audioContextRef.current.createGain();
      synthOutputBusRef.current.gain.value = 1;
      synthOutputBusRef.current.connect(audioContextRef.current.destination);
      
      // Pre-load heartbeat sample
      fetch('/heartbeat-sample.wav')
        .then(r => r.arrayBuffer())
        .then(buf => audioContextRef.current.decodeAudioData(buf))
        .then(decoded => { heartbeatBufferRef.current = decoded; })
        .catch(err => console.warn('Heartbeat sample preload failed:', err));
    }
    
    // Load presets
    initializeBuiltinPresets();
    
    // Cleanup function
    return () => {
      // Clean up MediaElementSource connections when component unmounts
      if (connectedElements.current) {
        const elements = connectedElements.current;
        // Clear all connections (WeakMap will auto-clean when elements are garbage collected)
        console.log('Cleaning up MediaElementSource connections on component unmount');
        // Note: WeakMap entries are automatically garbage collected when keys are no longer referenced
      }
    };
  }, []);

  // Update volume ref when slider changes (animation loop reads this)
  useEffect(() => {
    volumeRef.current = volume[0];
  }, [volume]);

  // Sync volume nodes end time with duration changes
  useEffect(() => {
    setVolumeNodes(prev => {
      if (prev.length < 2) return prev;
      const sorted = [...prev].sort((a, b) => a.time - b.time);
      const last = sorted[sorted.length - 1];
      if (Math.abs(last.time - duration) > 1) {
        return sorted.map((n, i) => i === sorted.length - 1 ? { ...n, time: duration } : n);
      }
      return prev;
    });
  }, [duration]);

  // Keep volumeNodes ref in sync
  useEffect(() => {
    volumeNodesRef.current = volumeNodes;
  }, [volumeNodes]);

  // Keep timeline / carrier / beat-type refs in sync so the playback
  // animation loop reads fresh values every frame (mid-session edits to
  // nodes, carrier slider tweaks, and beat-type hot-swap all propagate).
  useEffect(() => { timelineNodesRef.current = timelineNodes; }, [timelineNodes]);
  useEffect(() => { baseFrequencyRef.current = baseFrequency; }, [baseFrequency]);
  useEffect(() => { beatTypeRef.current = beatType; }, [beatType]);

  // Update background audio volume
  // Handle volume changes for background audio
  useEffect(() => {
    if (backgroundGainRef.current) {
      backgroundGainRef.current.gain.value = backgroundAudioVolume[0];
    }
  }, [backgroundAudioVolume]);

  // Set appropriate default value when switching beat types
  useEffect(() => {
    if (beatType === 'heartbeat') {
      // Set default BPM for heartbeat (60 BPM is a normal resting heart rate)
      if (baseFrequency > 100 || baseFrequency < 10) {
        setBaseFrequency(60);
      }
    } else {
      // Set default frequency for other beat types
      if (baseFrequency < 20 || (baseFrequency >= 10 && baseFrequency <= 100)) {
        setBaseFrequency(222); // 222 Hz default for other beat types
      }
    }
  }, [beatType]); // React to beat type changes only

  // REMOVED: handleBPMChange function (heartbeat disabled)

  // COMPLETELY REMOVED ALL HEALTH CHECK CODE

  // REMOVED: Problematic auto-restart useEffect that caused multiple instances

  // ===== Real-time 8D parameter updates =====
  
  // Update reverb mix in real-time
  useEffect(() => {
    console.log('🔍 Reverb useEffect triggered:', {
      is8DBackgroundAudio,
      hasNodes: !!background8DNodes.current,
      hasReverbGain: !!background8DNodes.current?.reverbGain,
      reverbValue: eightDReverbMix[0],
      effectiveValue: eightDReverbMix[0] * 0.3
    });
    
    if (is8DBackgroundAudio && background8DNodes.current?.reverbGain && audioContextRef.current) {
      try {
        const newValue = eightDReverbMix[0] * 0.15; // Subtle (0-15%)
        const currentTime = audioContextRef.current.currentTime;
        
        // Cancel any scheduled changes first
        background8DNodes.current.reverbGain.gain.cancelScheduledValues(currentTime);
        
        // Use linearRamp for smoother, more noticeable transitions (50ms ramp)
        background8DNodes.current.reverbGain.gain.setValueAtTime(
          background8DNodes.current.reverbGain.gain.value,
          currentTime
        );
        background8DNodes.current.reverbGain.gain.linearRampToValueAtTime(
          newValue,
          currentTime + 0.05
        );
        
        console.log('✅ Reverb mix updated:', Math.round(eightDReverbMix[0] * 15) + '% (gain:', newValue.toFixed(3) + ')');
      } catch (error) {
        console.error('❌ Error updating reverb mix:', error);
      }
    } else {
      console.warn('⚠️ Reverb update skipped - conditions not met');
    }
  }, [eightDReverbMix[0], is8DBackgroundAudio]);
  
  // Update clarity boost in real-time
  useEffect(() => {
    console.log('🔍 Clarity useEffect triggered:', {
      is8DBackgroundAudio,
      hasNodes: !!background8DNodes.current,
      hasClarityBoost: !!background8DNodes.current?.clarityBoost,
      clarityValue: eightDClarityBoost[0]
    });
    
    if (is8DBackgroundAudio && background8DNodes.current?.clarityBoost && audioContextRef.current) {
      try {
        const newValue = eightDClarityBoost[0];
        const currentTime = audioContextRef.current.currentTime;
        
        // Cancel any scheduled changes first
        background8DNodes.current.clarityBoost.gain.cancelScheduledValues(currentTime);
        
        // Use linearRamp for smoother transitions (50ms ramp)
        background8DNodes.current.clarityBoost.gain.setValueAtTime(
          background8DNodes.current.clarityBoost.gain.value,
          currentTime
        );
        background8DNodes.current.clarityBoost.gain.linearRampToValueAtTime(
          newValue,
          currentTime + 0.05
        );
        
        console.log('✅ Clarity boost updated:', newValue.toFixed(1) + 'dB @ 4kHz+');
      } catch (error) {
        console.error('❌ Error updating clarity boost:', error);
      }
    } else {
      console.warn('⚠️ Clarity boost update skipped - conditions not met');
    }
  }, [eightDClarityBoost[0], is8DBackgroundAudio]);
  
  // Update refs when state changes (for real-time animation access)
  useEffect(() => {
    eightDPathRef.current = eightDPath;
    if (is8DBackgroundAudio) {
      console.log('🔄 8D Path changed to:', eightDPath);
    }
  }, [eightDPath]);
  
  useEffect(() => {
    eightDRadiusRef.current = eightDRadius[0];
    if (is8DBackgroundAudio) {
      console.log('📏 8D Radius updated to:', eightDRadius[0].toFixed(2) + 'm');
    }
  }, [eightDRadius[0]]);
  
  useEffect(() => {
    eightDSpeedRef.current = eightDPanningSpeed[0];
    if (is8DBackgroundAudio) {
      console.log('⚡ 8D Speed updated to:', eightDPanningSpeed[0].toFixed(2) + 'x');
    }
  }, [eightDPanningSpeed[0]]);
  
  // Path, radius, and speed update automatically via animation loop (no rebuilding needed)
  // Only reverb mix requires direct node manipulation

  // Set up audio routing when background audio, 8D state, or performance mode changes
  useEffect(() => {
    if (backgroundAudio && backgroundAudioEnabled && audioContextRef.current) {
      console.log('🔄 Rebuilding audio routing due to state change');
      setupAudioRouting();
    }
  }, [backgroundAudio, backgroundAudioEnabled, is8DBackgroundAudio, performance8DMode]);

  // OPTION A: Setup live audio routing when 8D state or performance mode changes
  // Live audio uses the same 8D nodes structure as background audio
  useEffect(() => {
    if (liveAudioMode && liveAudioSourceRef.current && audioContextRef.current) {
      console.log('🔄 Rebuilding live audio routing due to state change');
      setupLiveAudioRouting();
    }
  }, [liveAudioMode, is8DBackgroundAudio, performance8DMode]);
  
  // Update live audio gain when monitoring toggle changes
  useEffect(() => {
    if (liveAudioGainRef.current && audioContextRef.current) {
      const targetVolume = liveAudioMonitoring ? 0.8 : 0;
      // Use setValueAtTime for glitch-free volume changes
      liveAudioGainRef.current.gain.setValueAtTime(targetVolume, audioContextRef.current.currentTime);
      console.log(`🔊 Live audio monitoring ${liveAudioMonitoring ? 'ENABLED' : 'DISABLED'} (volume: ${targetVolume})`);
    }
  }, [liveAudioMonitoring]);
  
  // Update base frequency in real-time when changed (while playing)
  useEffect(() => {
    if (isPlaying && !isPaused && oscillatorsRef.current.length > 0 && audioContextRef.current) {
      const currentFreq = getCurrentFrequency();
      const ctx = audioContextRef.current;
      const currentTime = ctx.currentTime;
      
      console.log('🎵 Base frequency changed, updating oscillators in real-time:', baseFrequency);
      
      // Update oscillator frequencies based on beat type
      if (beatType === 'binaural' && oscillatorsRef.current.length >= 2) {
        // Left ear: base frequency, Right ear: base frequency + beat frequency
        oscillatorsRef.current[0].frequency.setValueAtTime(baseFrequency, currentTime);
        oscillatorsRef.current[1].frequency.setValueAtTime(baseFrequency + currentFreq, currentTime);
        console.log(`   Left: ${baseFrequency} Hz, Right: ${baseFrequency + currentFreq} Hz`);
      } else if (beatType === 'monaural' && oscillatorsRef.current.length >= 2) {
        // Both oscillators change with base frequency
        oscillatorsRef.current[0].frequency.setValueAtTime(baseFrequency, currentTime);
        oscillatorsRef.current[1].frequency.setValueAtTime(baseFrequency + currentFreq, currentTime);
        console.log(`   Both oscillators: ${baseFrequency} Hz and ${baseFrequency + currentFreq} Hz`);
      } else if (beatType === 'isochronic' && oscillatorsRef.current.length >= 1) {
        // Single oscillator at base frequency
        oscillatorsRef.current[0].frequency.setValueAtTime(baseFrequency, currentTime);
        console.log(`   Isochronic: ${baseFrequency} Hz`);
      } else if (beatType === 'heartbeat') {
        // Heartbeat baseFrequency is BPM. Live update via the scheduler's
        // setBPM — no oscillator rebuild needed, no audible glitch.
        const wrapper = oscillatorsRef.current[0];
        if (wrapper && typeof wrapper.setBPM === 'function') {
          wrapper.setBPM(baseFrequency);
        } else {
          console.warn('Heartbeat scheduler not available for live BPM update');
        }
      } else if (beatType === 'trypnaural' && oscillatorsRef.current.length >= 3) {
        // Three oscillators stacked: carrier, carrier+beat, carrier+2*beat.
        // Without this branch, live carrier changes are silently ignored in
        // trypnaural mode until the user pauses and resumes playback.
        oscillatorsRef.current[0].frequency.setValueAtTime(baseFrequency, currentTime);
        oscillatorsRef.current[1].frequency.setValueAtTime(baseFrequency + currentFreq, currentTime);
        oscillatorsRef.current[2].frequency.setValueAtTime(baseFrequency + (currentFreq * 2), currentTime);
        console.log(`   Trypnaural: ${baseFrequency} Hz, ${baseFrequency + currentFreq} Hz, ${baseFrequency + (currentFreq * 2)} Hz`);
      }
    }
  }, [baseFrequency, isPlaying, isPaused]);

  // Hot-swap oscillators when beatType changes during playback. Without this,
  // switching binaural↔monaural↔isochronic↔heartbeat updated only the UI
  // ("● Playing X") but not the actual oscillator topology — the indicator
  // lied until the user manually paused and resumed.
  //
  // Strategy: zero the master gain (setValueAtTime, instant), swap oscillators
  // synchronously, then let the existing animation loop's setTargetAtTime
  // ramp gain back to volume on its next tick (~16ms cadence, 20ms time
  // constant) — gives a barely-perceptible ~60ms re-fade with no clicks and
  // no need to coordinate with the animation loop or fade timers.
  useEffect(() => {
    if (!isPlaying || isPaused) return;
    if (oscillatorsRef.current.length === 0) return;
    if (!audioContextRef.current || !gainNodeRef.current) return;
    if (isStoppingRef.current || isStarting) return;

    try {
      const ctx = audioContextRef.current;
      // 1. Instant silence so the disconnect/reconnect doesn't click.
      gainNodeRef.current.gain.cancelScheduledValues(ctx.currentTime);
      gainNodeRef.current.gain.setValueAtTime(0, ctx.currentTime);

      // 2. Tear down old, build new with the freshly-selected beat type.
      cleanupOscillators();
      const currentFreq = getCurrentFrequency();
      const oscillators = generateBeat(currentFreq, beatType);
      if (oscillators && oscillators.length > 0) {
        oscillatorsRef.current = oscillators;
        // New oscillator graph — next animation frame should re-apply timeline freq.
        lastAppliedBeatFreqRef.current = null;
        // Start oscillators — but skip AudioBufferSourceNode (heartbeat) since
        // generateBeat already calls .start() on those (they can only start once).
        oscillators.forEach((osc) => {
          if (osc?.start && !(osc instanceof AudioBufferSourceNode)) {
            try { osc.start(); }
            catch (err) { console.warn('Hot-swap oscillator start failed:', err); }
          }
        });
        console.log(`🔁 Beat type hot-swapped to ${beatType} (${oscillators.length} osc)`);
      }
      // 3. Animation loop's next tick handles the gain ramp back via
      //    setTargetAtTime — no manual fade-in needed here.
    } catch (e) {
      console.warn('Beat type hot-swap failed:', e);
    }
    // Intentionally only react to beatType — adding the other refs would
    // re-trigger swaps on unrelated state changes.
  }, [beatType]);

  // ESC closes the login modal
  useEffect(() => {
    if (!showLoginModal) return;
    const onKey = (e) => { if (e.key === 'Escape') setShowLoginModal(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showLoginModal]);

  // Keyboard shortcuts: Space for play/pause, S for stop
  useEffect(() => {
    const handleKeyDown = (event) => {
      // Don't trigger shortcuts if user is typing in an input field
      if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
        return;
      }
      
      switch (event.key) {
        case ' ': // Spacebar
          event.preventDefault(); // Prevent page scroll
          if (isPlaying) {
            pauseAudio();
          } else {
            startAudio();
          }
          break;
        case 's':
        case 'S':
          event.preventDefault();
          stopAudio();
          break;
        default:
          break;
      }
    };

    // Add event listener
    window.addEventListener('keydown', handleKeyDown);

    // Cleanup event listener
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isPlaying]); // Depend on isPlaying to get current state

  // Load preset categories and counts

  // Load all presets — Phase 4: API call moved to services/presetApi.js.
  const loadAllPresets = async () => {
    try {
      const { free, premium, user, all } = await presetApi.fetchAllPresets();
      setFreePresets(free);
      setPremiumPresets(premium);
      setUserPresets(user);
      setPresets(all);

      console.log(`Loaded all presets: ${free.length} free + ${premium.length} premium + ${user.length} user = ${all.length} total`);

      // Auto-load "Wind Down" as the default preset
      if (free.length > 0 && !currentPreset) {
        const windDown = free.find(p => p.name.toLowerCase().includes('wind down'));
        if (windDown) {
          console.log('Auto-loading Wind Down preset as default');
          loadPreset(windDown.id, windDown);
        }
      }
    } catch (error) {
      console.error('Error loading all presets:', error);
    }
  };

  // Initialize builtin presets if needed (admin-only endpoint — skip for
  // regular users/guests so we don't spam 401s in the console)
  const initializeBuiltinPresets = async () => {
    try {
      if (localStorage.getItem('neurowave_admin_token')) {
        await presetApi.initializeBuiltinPresets();
      }
    } catch (error) {
      // Non-critical — seeding fails offline but presets may still load from cache
      console.warn('Builtin preset seed skipped:', error?.message);
    }
    // Always attempt to load presets (may come from localStorage cache when offline)
    await loadAllPresets();
    await loadSharedPresetFromURL();
  };

  // Load shared preset from URL parameter (?shared=abc123).
  // Stays in component because it applies preset settings to audio state.
  const loadSharedPresetFromURL = async () => {
    try {
      const params = new URLSearchParams(window.location.search);
      const shareId = params.get('shared');
      if (!shareId) return;

      const shared = await presetApi.fetchSharedPreset(shareId);

      // Apply the shared preset settings
      setTimelineNodes(shared.nodes);
      setBeatType(shared.beat_type);
      setBaseFrequency(shared.base_frequency);
      setDuration(shared.duration);
      setVolume([shared.volume]);
      setPresetName(shared.name);
      currentPresetRef.current = shared.name;

      // Clean the URL without reloading
      window.history.replaceState({}, '', window.location.pathname);

      toast({
        title: "Shared Preset Loaded",
        description: `"${shared.name}" has been loaded from a shared link.`,
      });
    } catch (error) {
      console.error('Error loading shared preset:', error);
      if (error.response?.status === 404) {
        toast({
          title: "Shared Preset Not Found",
          description: "This share link is invalid or has expired.",
          variant: "destructive",
        });
      }
      window.history.replaceState({}, '', window.location.pathname);
    }
  };

  // Create realistic heartbeat with authentic cardiac characteristics
  const createRealisticHeartbeat = (ctx, heartRate, beatInterval) => {
    // Simple heartbeat using standard oscillator pattern like other beat types
    // No complex scheduling or timeouts that could interfere with main audio
    
    // Create two oscillators for LUB and DUB sounds
    const lubOsc = ctx.createOscillator();
    const dubOsc = ctx.createOscillator();
    const lubGain = ctx.createGain();
    const dubGain = ctx.createGain();
    const lubFilter = ctx.createBiquadFilter();
    const dubFilter = ctx.createBiquadFilter();
    const mixer = ctx.createGain();
    
    // LUB configuration (30Hz, muffled)
    lubOsc.frequency.value = 30;
    lubOsc.type = 'triangle';
    lubFilter.type = 'lowpass';
    lubFilter.frequency.value = 80;
    lubFilter.Q.value = 0.5;
    lubGain.gain.value = 0.4;
    
    // DUB configuration (45Hz, muffled)
    dubOsc.frequency.value = 45;
    dubOsc.type = 'triangle';  
    dubFilter.type = 'lowpass';
    dubFilter.frequency.value = 80;
    dubFilter.Q.value = 0.5;
    dubGain.gain.value = 0.3;
    
    // Connect audio chain
    lubOsc.connect(lubFilter);
    lubFilter.connect(lubGain);
    lubGain.connect(mixer);
    
    dubOsc.connect(dubFilter);
    dubFilter.connect(dubGain);
    dubGain.connect(mixer);
    
    mixer.connect(gainNodeRef.current);
    
    // Simple amplitude modulation for heartbeat rhythm using LFO
    const lfo = ctx.createOscillator();
    const lfoGain = ctx.createGain();
    
    lfo.frequency.value = heartRate / 60; // Convert BPM to Hz
    lfo.type = 'sine';
    lfoGain.gain.value = 0.8; // Modulation depth
    
    // Use LFO to modulate mixer gain for heartbeat pattern
    lfo.connect(lfoGain);
    lfoGain.connect(mixer.gain);
    mixer.gain.value = 0.2; // Base level
    
    return [lubOsc, dubOsc, lfo];
  };

  // Fade audio in smoothly
  const fadeIn = () => {
    // No-op: animation loop handles fade-in from 0 via smoothing
  };
  
  // Fade audio out smoothly
  const fadeOut = (duration = 1000) => {
    return new Promise((resolve) => {
      if (!gainNodeRef.current) {
        resolve();
        return;
      }
      // Set volumeRef to 0 — the animation loop will smoothly bring gain down
      volumeRef.current = 0;
      setTimeout(() => {
        resolve();
      }, duration);
    });
  };
  
  // Handle auto-stop at end of session (called via hook callback through ref)
  const handleAutoStop = async (elapsedDuration) => {
    const actualDuration = elapsedDuration || duration;
    saveSessionToHistory({
      preset: currentPresetRef.current || 'Custom Session',
      duration: actualDuration,
      beatType,
      baseFrequency,
      completed: true,
    });
    
    await fadeOut(3000);
    stopAudio();
    
    toast({
      title: "Session Complete!",
      description: `Great work! You completed ${Math.round(actualDuration / 60)} minutes of focused time.`,
      duration: 5000,
    });

    // Post-session mood rating — only for signed-in users (can't attribute
    // a rating to anon) and meaningful sessions (≥3 min). The 3-min floor
    // is the same threshold the analytics industry uses for "intent-to-
    // complete" — below that it's usually a user sampling the UI, not a
    // real mindfulness session we want ratings for. 800ms delay lets the
    // completion toast land first so the sheet doesn't feel like an
    // interruption.
    if (user && actualDuration >= 180) {
      setMoodSheetContext({
        preset: currentPresetRef.current || 'Custom Session',
        beatType,
        durationSec: Math.round(actualDuration),
      });
      setTimeout(() => setMoodSheetOpen(true), 800);
    }
  };
  // Keep ref in sync with latest handleAutoStop closure
  autoStopRef.current = handleAutoStop;
  
  // ========== SLEEP TIMER ==========
  // State + 1Hz countdown moved to useSleepTimer (Phase 3 refactor). Wire the
  // expire policy here — fade beats, stop synth, kill audio. Stashing the
  // closure in a ref keeps useSleepTimer's onExpire stable while letting it
  // see fresh references to stopAudio/synthAmbient declared further below.
  sleepExpireRef.current = () => {
    if (gainNodeRef.current && audioContextRef.current) {
      const now = audioContextRef.current.currentTime;
      gainNodeRef.current.gain.cancelScheduledValues(now);
      gainNodeRef.current.gain.setTargetAtTime(0, now, 1.5); // ~5s fade
    }
    if (synthAmbient.isEnabled) synthAmbient.stop();
    setTimeout(() => { stopAudio(); }, 5000);
  };

  
  // ========== SYNTH AMBIENT WRAPPERS (via useSynthAmbient hook) ==========
  const toggleSynthAmbient = () => {
    if (!audioContextRef.current) {
      toast({ title: "Audio Not Ready", description: "Click Play first to initialize audio" });
      return;
    }
    if (synthAmbient.isEnabled) {
      synthAmbient.stop();
    } else {
      synthAmbient.start(synthAmbient.currentType);
    }
  };

  const changeSynthAmbientType = (newType) => {
    synthAmbient.changeType(newType);
  };

  const updateSynthAmbientVolume = (newVolume) => {
    synthAmbient.updateVolume(newVolume);
  };


  // Generate different types of beats
  const generateBeat = (frequency, type) => {
    const ctx = audioContextRef.current;
    if (!ctx) return null;

    switch (type) {
      case 'binaural':
        // Left ear: base frequency. Right ear: base + beat frequency.
        // Both ears must receive equal-amplitude sines for the brainstem to
        // produce the binaural beat at (right - left). Asymmetric gains
        // weaken the perceived beat — a 5% imbalance is enough to bias one
        // ear and degrade entrainment, which is why we hold both at 0.35.
        // Pure sine waves at similar carrier frequencies produce identical
        // RMS regardless of frequency, so no compensation is needed.
        const leftOsc = ctx.createOscillator();
        const rightOsc = ctx.createOscillator();
        const leftGain = ctx.createGain();
        const rightGain = ctx.createGain();
        const merger = ctx.createChannelMerger(2);
        
        leftOsc.frequency.value = baseFrequency;
        rightOsc.frequency.value = baseFrequency + frequency;
        
        // Symmetric per-ear gain — the master gain is downstream so we
        // budget 0.35 here; combined post-merger amplitude maxes at ~0.5
        // (sines RMS-add, not peak-add) which leaves comfortable headroom
        // for the limiter.
        leftGain.gain.value = 0.35;
        rightGain.gain.value = 0.35;
        
        leftOsc.connect(leftGain);
        rightOsc.connect(rightGain);
        leftGain.connect(merger, 0, 0); // Connect to left channel (0)
        rightGain.connect(merger, 0, 1); // Connect to right channel (1)
        merger.connect(gainNodeRef.current);
        
        return [leftOsc, rightOsc];
        
      case 'monaural':
        // Two oscillators mixed together before reaching the ears
        const osc1 = ctx.createOscillator();
        const osc2 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        const gain2 = ctx.createGain();
        const mixer = ctx.createGain();
        
        osc1.frequency.value = baseFrequency;
        osc2.frequency.value = baseFrequency + frequency;
        
        // Reduced gains to prevent clipping (0.35 + 0.35 = 0.7 max)
        gain1.gain.value = 0.35;
        gain2.gain.value = 0.35;
        
        osc1.connect(gain1);
        osc2.connect(gain2);
        gain1.connect(mixer);
        gain2.connect(mixer);
        mixer.connect(gainNodeRef.current);
        
        return [osc1, osc2];
        
      case 'isochronic':
        // Rhythmic pulses at beat frequency with smooth amplitude modulation
        const isoOsc = ctx.createOscillator();
        const isoGain = ctx.createGain();
        const lfo = ctx.createOscillator();
        const lfoGain = ctx.createGain();
        
        // Main carrier frequency
        isoOsc.frequency.value = baseFrequency;
        isoOsc.type = 'sine';
        
        // LFO for amplitude modulation
        lfo.frequency.value = frequency;
        lfo.type = 'sine';
        
        // Set up connections
        isoOsc.connect(isoGain);
        lfo.connect(lfoGain);
        
        // Reduced gains to prevent clipping
        lfoGain.gain.value = 0.3; // Modulation depth (reduced from 0.4)
        isoGain.gain.value = 0.45; // Base volume (reduced from 0.6)
        
        // Connect LFO to modulate the gain
        lfoGain.connect(isoGain.gain);
        
        // Connect to output
        isoGain.connect(gainNodeRef.current);
        
        return [isoOsc, lfo];
        
      case 'heartbeat': {
        // Schedule discrete one-shot BufferSources at exact intervals matching
        // the user's BPM, instead of looping a single source with a scaled
        // playbackRate (the old approach assumed the sample was 72 BPM and
        // pitch-shifted the heartbeat as a side-effect of changing tempo).
        // This way: accurate rate regardless of sample's native tempo, native
        // pitch preserved, and live BPM updates are trivial — just mutate
        // `wrapper.currentBpm` and the next scheduling tick uses it.
        //
        // Heart-rate variability (HRV / "Coherence"): a small ± random jitter
        // on each interval. Real resting RMSSD is ~3-5%, so 4% (default)
        // sounds organic without being arrhythmic. The user can tune this
        // 0-10% via the Coherence slider — `wrapper.setHrv(pct)` updates it
        // live without rebuilding any nodes.
        if (!heartbeatBufferRef.current) {
          console.error('Heartbeat sample not loaded yet');
          return null;
        }

        const initialBpm = Math.max(10, Math.min(180, baseFrequency));
        // Read coherence as a fraction (0.04 = 4%). Clamped 0..0.10 by the
        // UI slider; we re-clamp here defensively.
        const initialHrvFraction = Math.max(0, Math.min(0.10, (coherenceRef.current ?? 4) / 100));

        // Audio graph for natural heartbeat:
        //
        //   BufferSource → perBeatGain → lowPass → heartbeatBoost → master
        //
        // Why each stage exists:
        //   • perBeatGain (created per beat below): mimics the natural
        //     beat-to-beat amplitude variation of a real heart. Tuned
        //     ±15% — bigger than the timing HRV jitter because amplitude
        //     differences are how your ears perceive "alive vs metronome".
        //   • lowPass (single shared node): a gentle high-shelf trim above
        //     ~4.5kHz to soften any residual digital edge while preserving
        //     the natural mid-range body of the stethoscope recording.
        //     The sample itself (extracted from a real clinical heart-
        //     anatomy video at 66 BPM) is already inherently bass-heavy,
        //     so we don't need an aggressive lowpass anymore — that would
        //     muddy the clean lub-dub separation. Q=0.5 keeps the rolloff
        //     gentle.
        //   • heartbeatBoost: 3× gain stage to make the heart audible
        //     relative to the binaural beat layer (down from 4× because
        //     the new sample is already louder per beat than the old one).
        const lowPass = ctx.createBiquadFilter();
        lowPass.type = 'lowpass';
        lowPass.frequency.value = 4500;
        lowPass.Q.value = 0.5;

        const heartbeatBoost = ctx.createGain();
        heartbeatBoost.gain.value = 3.0;
        lowPass.connect(heartbeatBoost);
        heartbeatBoost.connect(gainNodeRef.current);

        // Track scheduled sources so a BPM change can cancel future beats
        // and avoid double-trigger glitches when slowing down.
        const scheduled = new Set();
        let stopped = false;
        let timerId = null;
        // Track how many beats have fired so we can suppress the S3
        // ("third heart sound") for the first ~2 seconds. S3 is anatomically
        // correct ("Kentucky rhythm" in cardiology) but on a cold start
        // the listener hasn't established the lub-dub baseline yet, so a
        // probabilistic extra "dub" on beat #1 sounds like a stutter
        // ("lub-dub-dub") rather than authentic. Once the rhythm is
        // established, S3 lands as a natural variation.
        let beatsFired = 0;
        // Lead-in for the very first beat. 50ms is theoretically enough but
        // in practice browsers need ~80-150ms to warm the audio graph after
        // a fresh `createBufferSource → connect → start()` chain — and
        // longer still on a hot-swap (binaural → heartbeat or preset
        // change), because a fade-out from the old beat type can still be
        // resolving while we're trying to schedule the first new beat.
        // 250ms gives the audio thread comfortable headroom and fully
        // eliminates the audible "stutter on first beat" without being
        // long enough for the user to notice a delay.
        let nextBeatTime = ctx.currentTime + 0.25;
        // Tracks the AudioContext time at which the most recent beat was
        // fired (from either the scheduler OR triggerBeatNow). The
        // scheduler uses this to detect "BLE has gone silent" and fall
        // back to internal beats so the user never hears extended silence
        // when the strap drops a few packets. Polar H10 specifically has
        // documented 2-5s notification gaps under motion/sweat/range, plus
        // OS-level BLE throttling can pause notifications periodically.
        let lastBeatAt = 0;
        const wrapper = { currentBpm: initialBpm, currentHrv: initialHrvFraction, syncExternal: false };

        const schedule = () => {
          if (stopped) return;
          // When syncExternal is on, beats normally fire from R-peaks via
          // triggerBeatNow(). BUT — we keep the scheduler running as a
          // *fallback*: if no R-peak has fired a beat in the last 1.2s,
          // the scheduler resumes firing beats at the last-known BPM until
          // R-peaks resume. This eliminates the audible cut-outs caused by
          // BLE notification gaps (common on chest straps under real-world
          // motion) without competing with the live signal when it's
          // healthy. 1.2s ≈ slightly longer than one beat at 50 BPM, so
          // even ultra-slow resting heart rates won't trigger spurious
          // fallback beats.
          if (wrapper.syncExternal) {
            const idleFor = ctx.currentTime - lastBeatAt;
            if (idleFor < 1.2) {
              timerId = setTimeout(schedule, 250);
              return;
            }
            // BLE silent — fall back. Anchor nextBeatTime to "now" so the
            // fallback beat lands immediately, not stale-in-the-past or
            // bunched-in-the-future.
            if (nextBeatTime < ctx.currentTime + 0.05) {
              nextBeatTime = ctx.currentTime + 0.05;
            }
          }
          // Lookahead of 500ms (was 200ms) — gives Chrome Android's throttled
          // setTimeout a much wider safety window before a late wake-up
          // starts missing beats. Paired with the 250ms wake-up interval
          // below, this is the standard "Two Clocks" tuning from Chris
          // Wilson's canonical Web Audio timing article. Net effect: JS
          // wake-up rate drops ~60% and mobile glitching reduces sharply,
          // with no audible tradeoff because beats are still locked to
          // sample-accurate ctx.currentTime.
          const lookahead = 0.5;
          const horizon = ctx.currentTime + lookahead;
          while (nextBeatTime < horizon) {
            fireBeatAt(nextBeatTime);
            // Apply HRV jitter to the *next* interval (this beat's start time
            // is already locked). Symmetric ± so long-run mean BPM stays
            // exactly at currentBpm. wrapper.currentHrv is mutable via
            // wrapper.setHrv() — coherence slider updates take effect on the
            // next iteration of this loop without rebuilding anything.
            //
            // RSA (respiratory sinus arrhythmia) — when the Breath Pacer is
            // on, modulate the period along the breath cycle: heart speeds
            // up during inhalation (period shortens), slows during exhalation
            // (period lengthens). 3% amplitude matches the depth observed in
            // healthy young adults / athletes.
            const basePeriod = 60 / wrapper.currentBpm;
            let rsaScale = 1;
            if (breathPacerEnabledRef.current) {
              const breathPeriodMs = (60 / breathPacerBpmRef.current) * 1000;
              const elapsedMs = performance.now() - breathPhaseAnchorRef.current;
              const phase = (elapsedMs % breathPeriodMs) / breathPeriodMs;
              rsaScale = 1 - 0.03 * Math.sin(phase * 2 * Math.PI);
            }
            const jitter = wrapper.currentHrv > 0
              ? basePeriod * wrapper.currentHrv * (Math.random() * 2 - 1)
              : 0;
            nextBeatTime += basePeriod * rsaScale + jitter;
          }
          // 250ms wake-up (was 100ms). 2.5× less JS scheduler pressure on
          // mobile. Paired with the 500ms lookahead above to keep beats
          // sample-accurate.
          timerId = setTimeout(schedule, 250);
        };

        // Fire one heartbeat at a specific ctx.currentTime. Factored out of
        // the scheduler loop so the "sync to user's R-peaks" path can reuse
        // the exact same audio graph (amplitude jitter, lowpass, S3 chance)
        // — no rebuild, no drift between the two beat-firing modes.
        const fireBeatAt = (beatTime) => {
          const src = ctx.createBufferSource();
          src.buffer = heartbeatBufferRef.current;

          const perBeatGain = ctx.createGain();
          perBeatGain.gain.value = 0.85 + Math.random() * 0.30;
          src.connect(perBeatGain);
          perBeatGain.connect(lowPass);

          src.start(beatTime);
          scheduled.add(src);
          beatsFired += 1;
          // Tracks the latest scheduled beat — used by the scheduler's
          // BLE-silence fallback (when syncExternal is on, the scheduler
          // only fires if more than ~1.2s has elapsed since the last beat
          // from any source). Stamping in beatTime order (rather than
          // ctx.currentTime) avoids race conditions when a triggerBeatNow
          // call lands within the scheduler's lookahead window.
          if (beatTime > lastBeatAt) lastBeatAt = beatTime;
          src.onended = () => {
            scheduled.delete(src);
            try { perBeatGain.disconnect(); } catch (e) { /* ignore */ }
          };

          // S3 (third heart sound) on ~14% of beats — but skip on the first
          // ~3 beats so the user's ears can establish the lub-dub rhythm
          // before we introduce S3 variations. Without this gate, a 14%
          // chance on beat #1 produces an audible "lub-dub-dub" stutter
          // before any rhythm has been heard.
          //
          // S3 must fire AFTER the WAV's S2 ("dub") — anatomically S3 is
          // heard 120-150ms after S2. Our heartbeat-sample.wav has this
          // landmark layout (verified via ffprobe silencedetect):
          //
          //   0.000-0.311s  leading silence
          //   0.311-0.479s  S1 ("lub")
          //   0.479-0.656s  silence
          //   0.656-0.754s  S2 ("dub")
          //   0.754-0.950s  trailing silence
          //
          // So S2 ends at beatTime + 0.754s. We fire S3 at
          // beatTime + 0.776 (~22ms after S2 ends, then ramps up over
          // 25ms — landing the perceived S3 onset ~50ms after S2 ends,
          // which matches the cardiology textbook 120-150ms post-S2 gap).
          // Previously this was hardcoded at beatTime + 0.15s, which fired
          // S3 BEFORE the WAV's S1 even started — producing the
          // "lub-lub-dub" stutter the user heard.
          if (beatsFired > 3 && Math.random() < 0.14) {
            const s3Osc = ctx.createOscillator();
            s3Osc.type = 'sine';
            s3Osc.frequency.value = 40;
            const s3Gain = ctx.createGain();
            const s3Start = beatTime + 0.776;
            const s3Peak = s3Start + 0.025;
            const s3End = s3Start + 0.16;
            s3Gain.gain.setValueAtTime(0.0001, s3Start);
            s3Gain.gain.exponentialRampToValueAtTime(0.05, s3Peak);
            s3Gain.gain.exponentialRampToValueAtTime(0.0001, s3End);
            s3Osc.connect(s3Gain);
            s3Gain.connect(lowPass);
            s3Osc.start(s3Start);
            s3Osc.stop(s3End + 0.05);
            scheduled.add(s3Osc);
            s3Osc.onended = () => {
              scheduled.delete(s3Osc);
              try { s3Gain.disconnect(); } catch (e) { /* ignore */ }
            };
          }
        };
        schedule();

        // Return an oscillator-like wrapper so cleanupOscillators (which calls
        // osc.stop() + osc.disconnect()) keeps working unchanged. The custom
        // `setBPM` is what the real-time updater useEffect calls when the user
        // moves the BPM input.
        const wrapperNode = {
          start: () => {}, // scheduler is already running
          stop: () => {
            stopped = true;
            if (timerId) { clearTimeout(timerId); timerId = null; }
            // Cancel future-scheduled beats that haven't started yet.
            scheduled.forEach((src) => {
              try { src.stop(); } catch (e) { /* already stopped */ }
            });
            scheduled.clear();
          },
          disconnect: () => {
            try { heartbeatBoost.disconnect(); } catch (e) { /* ignore */ }
            try { lowPass.disconnect(); } catch (e) { /* ignore */ }
          },
          setBPM: (bpm) => {
            const clamped = Math.max(10, Math.min(180, bpm));
            wrapper.currentBpm = clamped;
            // Re-anchor the schedule horizon to the new tempo so we don't get
            // a stale-tempo overlap when slowing down a lot. Any beats we've
            // already scheduled past the new period will still play (they
            // run on their own short BufferSource lifetimes), but the next
            // wave is computed at the new BPM.
            const now = ctx.currentTime;
            const newPeriod = 60 / clamped;
            const cutoff = now + newPeriod * 1.5;
            if (nextBeatTime > cutoff) nextBeatTime = now + newPeriod;
            console.log(`💓 Heartbeat BPM live-updated to ${clamped}`);
          },
          setHrv: (pct) => {
            // Accepts either fraction (0.04) or percent (4). Anything > 1
            // is interpreted as percent for ergonomics.
            const fraction = pct > 1 ? pct / 100 : pct;
            wrapper.currentHrv = Math.max(0, Math.min(0.10, fraction));
            console.log(`💓 Heartbeat coherence live-updated to ±${(wrapper.currentHrv * 100).toFixed(1)}%`);
          },
          // Re-baseline the scheduler's clock reference. Called when the
          // Page Visibility API reports the tab has become visible again
          // after being backgrounded — Chrome Android's throttled
          // setTimeout can leave `nextBeatTime` either significantly in the
          // past (scheduler missed wake-ups, next beat already overdue) or
          // bunched (many beats scheduled within the last lookahead
          // window). Clamping to ctx.currentTime + 50ms gives a clean
          // restart without any audible jump. Safe no-op if the drift is
          // small because the comparison gate filters sub-second cases.
          resync: () => {
            const now = ctx.currentTime;
            const drift = nextBeatTime - now;
            if (drift < 0 || drift > 1.0) {
              nextBeatTime = now + 0.25;
            }
          },
          // Fire a single beat right now — called by the heart-monitor R-peak
          // listener when "Sync heartbeat to my heart" is on. 60ms lead-in
          // gives the audio thread enough headroom to allocate the
          // BufferSource cleanly without producing a startup click on
          // mobile or under CPU load. (Was 30ms — too tight for some
          // devices, occasionally produced a quieter first beat after a
          // BLE silence gap.)
          //
          // Also: push the scheduler's nextBeatTime forward so the
          // fallback can't fire a competing beat right after this one.
          // Without this, after a BLE silence gap the scheduler's queued
          // fallback beat lands ~50-200ms after the resumed R-peak —
          // producing the "catch-up burst" / "double-tap" sound users
          // report every 8-10 seconds when strap contact is intermittent.
          triggerBeatNow: () => {
            if (stopped) return;
            const triggerAt = ctx.currentTime + 0.06;
            fireBeatAt(triggerAt);
            // Defer the scheduler's next candidate beat to ~one period
            // out from this externally-fired beat. The scheduler's
            // fallback check (`idleFor < 1.2`) will keep it silent in
            // the meantime; the +period gives us a clean slot if BLE
            // goes quiet again immediately after this peak.
            const period = 60.0 / Math.max(20, wrapper.currentBpm || 60);
            if (nextBeatTime < triggerAt + period) {
              nextBeatTime = triggerAt + period;
            }
          },
          // Toggle external-sync mode. When true, the scheduled while-loop
          // stops queuing beats — beats fire only from triggerBeatNow().
          // When flipping back to false, also re-baseline nextBeatTime so
          // the scheduler picks up cleanly from "now" rather than whatever
          // stale value it held.
          setSyncExternal: (on) => {
            wrapper.syncExternal = !!on;
            if (!on) nextBeatTime = ctx.currentTime + 0.25;
          },
          isHeartbeatScheduler: true,
        };

        return [wrapperNode];
      }

      case 'trypnaural':
        // Three oscillators for enhanced entrainment
        const try1 = ctx.createOscillator();
        const try2 = ctx.createOscillator();
        const try3 = ctx.createOscillator();
        const tryGain1 = ctx.createGain();
        const tryGain2 = ctx.createGain();
        const tryGain3 = ctx.createGain();
        const tryMixer = ctx.createGain();
        
        try1.frequency.value = baseFrequency;
        try2.frequency.value = baseFrequency + frequency;
        try3.frequency.value = baseFrequency + (frequency * 2);
        
        // Reduced gains to prevent clipping (0.25 + 0.2 + 0.2 = 0.65 max)
        tryGain1.gain.value = 0.25;
        tryGain2.gain.value = 0.2;
        tryGain3.gain.value = 0.2;
        
        try1.connect(tryGain1);
        try2.connect(tryGain2);
        try3.connect(tryGain3);
        tryGain1.connect(tryMixer);
        tryGain2.connect(tryMixer);
        tryGain3.connect(tryMixer);
        tryMixer.connect(gainNodeRef.current);
        
        return [try1, try2, try3];
        
      default:
        return null;
    }
  };

  // REMOVED: restartAudioSilently function that was potentially causing issues

  // Live frequency automation — shared by both rAF loops (startAudio and
  // resumeAnimationFrame). Reads ONLY from refs so it never sees stale
  // closure data, regardless of when the rAF callback was created.
  // Called once per animation frame with the current playback time.
  const applyLiveFrequencyAutomation = (clampedTime) => {
    if (
      oscillatorsRef.current.length === 0 ||
      !audioContextRef.current ||
      beatTypeRef.current === 'heartbeat'
    ) return;

    try {
      // Inline timeline interpolation against the live ref — robust to
      // node edits and beat-type changes during playback.
      const nodes = timelineNodesRef.current;
      let newBeatFreq;
      if (!nodes || nodes.length === 0) {
        newBeatFreq = 10;
      } else {
        const sorted = [...nodes].sort((a, b) => a.time - b.time);
        if (clampedTime <= sorted[0].time) {
          newBeatFreq = sorted[0].frequency;
        } else if (clampedTime >= sorted[sorted.length - 1].time) {
          newBeatFreq = sorted[sorted.length - 1].frequency;
        } else {
          newBeatFreq = sorted[0].frequency;
          for (let i = 0; i < sorted.length - 1; i++) {
            if (clampedTime >= sorted[i].time && clampedTime <= sorted[i + 1].time) {
              const span = sorted[i + 1].time - sorted[i].time;
              const progress = span > 0 ? (clampedTime - sorted[i].time) / span : 0;
              newBeatFreq = sorted[i].frequency + (sorted[i + 1].frequency - sorted[i].frequency) * progress;
              break;
            }
          }
        }
      }

      // Throttle: only re-schedule when the freq shifted meaningfully
      // (>0.05 Hz) to avoid spamming setValueAtTime at 60fps.
      const last = lastAppliedBeatFreqRef.current;
      if (last != null && Math.abs(newBeatFreq - last) <= 0.05) return;

      const ctx = audioContextRef.current;
      const now = ctx.currentTime;
      const carrier = baseFrequencyRef.current;
      const bt = beatTypeRef.current;
      if (bt === 'binaural' && oscillatorsRef.current.length >= 2) {
        oscillatorsRef.current[1].frequency.setValueAtTime(carrier + newBeatFreq, now);
      } else if (bt === 'monaural' && oscillatorsRef.current.length >= 2) {
        oscillatorsRef.current[1].frequency.setValueAtTime(carrier + newBeatFreq, now);
      } else if (bt === 'isochronic' && oscillatorsRef.current.length >= 2) {
        // [0] is carrier (baseFreq), [1] is the LFO that modulates gain at beat freq.
        oscillatorsRef.current[1].frequency.setValueAtTime(newBeatFreq, now);
      } else if (bt === 'trypnaural' && oscillatorsRef.current.length >= 3) {
        oscillatorsRef.current[1].frequency.setValueAtTime(carrier + newBeatFreq, now);
        oscillatorsRef.current[2].frequency.setValueAtTime(carrier + (newBeatFreq * 2), now);
      }
      lastAppliedBeatFreqRef.current = newBeatFreq;
    } catch (err) {
      // Don't let an oscillator hiccup take down the animation loop.
      console.warn('Live frequency automation skipped a frame:', err);
    }
  };

  const startAudio = async (skipPlayingCheck = false) => {
    // Prevent multiple simultaneous sessions
    // skipPlayingCheck is used when called from startCoherenceSession after awaiting stopAudio,
    // because React state (isPlaying) may not have updated yet even though stopAudio completed.
    if (isStoppingRef.current || isStarting || (!skipPlayingCheck && isPlaying)) return;

    // Safety gate: first-time users must acknowledge the safety / contraindications.
    if (!hasAcknowledgedSafety()) {
      setSafetyMode('gate');
      setShowSafety(true);
      return;
    }

    setIsStarting(true);
    // Fresh session — allow one HRV snapshot to be posted on this session's
    // stop. Resetting here (not in stopAudio) means user-initiated starts,
    // Coherence launcher, and preset-hot-swap all get a clean slate.
    hrvSnapshotPostedRef.current = false;
    
    try {
      if (audioContextRef.current?.state === 'suspended') {
        // Race ctx.resume() with a timeout. Without a user gesture, Chrome's
        // autoplay policy returns a Promise that never resolves — which would
        // leave isStarting=true forever and the Play button permanently
        // disabled. Bailing after 1.2s lets the UI recover and the user
        // simply taps Play (which, this time, *is* a user gesture and will
        // resume the context cleanly). We also surface a friendlier toast
        // than the generic "Audio Error" when this happens.
        const resumed = await Promise.race([
          audioContextRef.current.resume().then(() => true),
          new Promise((res) => setTimeout(() => res(false), 1200)),
        ]).catch(() => false);

        if (!resumed || audioContextRef.current?.state === 'suspended') {
          toast({
            title: '🔇 Tap Play to begin',
            description: 'Your browser blocked auto-start. One tap and you\'re in.',
          });
          return; // finally{} below resets isStarting
        }
      }
      
      // Stop any existing oscillators
      cleanupOscillators();
      
      // Create session in backend if not resuming
      if (!isPaused) {
        try {
          const sessionData = {
            preset_id: currentPreset || null,
            preset_name: presetName || 'Custom Session',
            nodes: timelineNodes.map(node => ({
              id: node.id,
              time: node.time,
              frequency: node.frequency
            })),
            beat_type: beatType,
            base_frequency: baseFrequency,
            duration: duration,
            volume: volume[0],
            session_notes: ''
          };
          
          const response = await axios.post(`${API}/sessions`, sessionData);
          setCurrentSessionId(response.data.id);
        } catch (error) {
          console.error('Error creating session:', error);
          // Continue with audio generation even if session creation fails
        }
      }
      
      // Start audio generation
      const currentFreq = getCurrentFrequency();
      const oscillators = generateBeat(currentFreq, beatType);
      
      if (oscillators && oscillators.length > 0) {
        oscillatorsRef.current = oscillators;
        // Reset so the very first animation-loop frame applies the timeline
        // frequency to the freshly-built oscillators.
        lastAppliedBeatFreqRef.current = null;
        
        // Start silent — use Web Audio API scheduling for click-free fade-in
        if (gainNodeRef.current && audioContextRef.current) {
          const ctx = audioContextRef.current;
          gainNodeRef.current.gain.cancelScheduledValues(ctx.currentTime);
          gainNodeRef.current.gain.setValueAtTime(0, ctx.currentTime);
        }
        
        // Start all oscillators
        oscillators.forEach(osc => {
          if (osc && osc.start) {
            try {
              osc.start();
            } catch (error) {
              console.warn('Error starting oscillator:', error);
            }
          }
        });
        
        setIsPlaying(true);
        setIsPaused(false);

        // Pin this preset to the "Recently used" list — only fires on the
        // actual Play action, NOT on mere selection. Previewing a preset
        // without committing to a session shouldn't pollute the recency
        // signal. The dispatched custom event refreshes any mounted
        // PresetManagementPanel in the same tab.
        if (currentPreset) {
          try {
            pushRecentPreset(currentPreset);
            window.dispatchEvent(new Event('neurowave:preset-recent-updated'));
          } catch { /* ignore */ }
        }

        // Synth ambient follows global Play (Option A coupling): if the
        // user had it running when they hit Stop/Pause, re-arm it now with
        // the same preset they last picked. Reset the resume flag so
        // re-pressing Play after a fresh Stop doesn't bring back synth
        // unless they explicitly turned it on again.
        if (synthShouldResumeRef.current && !synthAmbient.isEnabled) {
          try {
            synthAmbient.start(synthAmbient.currentType);
          } catch (e) {
            console.warn('Synth resume on global Play failed:', e);
          }
        }
        synthShouldResumeRef.current = false;
        
        // Start timer
        sessionTimer.start(duration);
        
        // Animation loop handles volume (no separate fadeIn call to avoid conflicts)
        
        startTimeRef.current = audioContextRef.current.currentTime - (isPaused ? pausedTime : currentTime);
        
        // Start animation loop for time tracking
        const updateTime = () => {
          if (isStoppingRef.current || animationPausedRef.current) return;
          
          const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
          const clampedTime = Math.min(elapsed, duration);
          setCurrentTime(clampedTime);
          
          // Live frequency automation — drive oscillators from the timeline
          // node curve as the playhead advances. Helper reads from refs so
          // it never sees stale closure data.
          applyLiveFrequencyAutomation(clampedTime);
          
          // Volume control via Web Audio API setTargetAtTime (click-free)
          if (gainNodeRef.current && audioContextRef.current) {
            let targetVol = volumeRef.current;
            
            // Apply volume automation if nodes exist
            if (volumeNodesRef.current && volumeNodesRef.current.length >= 2) {
              const sorted = [...volumeNodesRef.current].sort((a, b) => a.time - b.time);
              let automationVol = sorted[0].volume;
              if (clampedTime >= sorted[sorted.length - 1].time) {
                automationVol = sorted[sorted.length - 1].volume;
              } else {
                for (let i = 0; i < sorted.length - 1; i++) {
                  if (clampedTime >= sorted[i].time && clampedTime <= sorted[i + 1].time) {
                    const t = (clampedTime - sorted[i].time) / (sorted[i + 1].time - sorted[i].time);
                    automationVol = sorted[i].volume + t * (sorted[i + 1].volume - sorted[i].volume);
                    break;
                  }
                }
              }
              targetVol = automationVol * volumeRef.current;
            }
            
            // setTargetAtTime interpolates at audio sample rate (48kHz) — no clicks
            const now = audioContextRef.current.currentTime;
            gainNodeRef.current.gain.cancelScheduledValues(now);
            gainNodeRef.current.gain.setTargetAtTime(targetVol, now, 0.02);
          }
          
          if (elapsed < duration && !isStoppingRef.current && !animationPausedRef.current) {
            animationFrameRef.current = requestAnimationFrame(updateTime);
          } else if (elapsed >= duration) {
            stopAudio();
          }
        };
        
        // Start background audio if enabled
        if (backgroundAudioEnabled && backgroundAudio) {
          // If resuming from pause, keep background audio at its current position
          // If starting fresh, it should already be at the beginning or user's position
          backgroundAudio.play().catch(e => {
            console.warn('Error playing background audio:', e);
          });
        }
        
        animationFrameRef.current = requestAnimationFrame(updateTime);
      } else {
        throw new Error('Failed to generate audio oscillators');
      }
    } catch (error) {
      console.error('Error starting audio:', error);
      
      // Reset states on error
      setIsPlaying(false);
      setIsPaused(false);
      isStoppingRef.current = false;
      
      toast({
        title: "Audio Error",
        description: `Failed to start ${beatType} beats. Please try again.`,
        variant: "destructive",
      });
    } finally {
      setIsStarting(false);
    }
  };

  // Keep `startAudioRef` pointed at the latest `startAudio` closure on every
  // render. Deferred callers (Coherence Mode setTimeout, URL ?coherence=1
  // auto-launch) must invoke this ref instead of `startAudio` directly so the
  // engine sees the updated `beatType`/`baseFrequency`/`timelineNodes` after
  // React has flushed the state batch from `startCoherenceSession`.
  startAudioRef.current = startAudio;

  const cleanupOscillators = () => {
    try {
      oscillatorsRef.current.forEach(osc => {
        if (osc && osc.stop) {
          try {
            osc.stop();
          } catch (error) {
            // Oscillator might already be stopped
            console.warn('Error stopping oscillator:', error);
          }
        }
        
        if (osc && osc.disconnect) {
          try {
            osc.disconnect();
          } catch (error) {
            console.warn('Error disconnecting oscillator:', error);
          }
        }
      });
    } catch (error) {
      console.warn('Error cleaning up oscillators:', error);
    }
    oscillatorsRef.current = [];
  };

  const pauseAudio = () => {
    if (isStoppingRef.current || isStarting) return;
    
    if (isPlaying) {
      // Pause the audio
      isStoppingRef.current = true;
      
      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      // Cancel scheduled gain automation for clean pause
      if (gainNodeRef.current && audioContextRef.current) {
        gainNodeRef.current.gain.cancelScheduledValues(audioContextRef.current.currentTime);
        gainNodeRef.current.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      }
      
      // Store current time for resume
      setPausedTime(currentTime);
      
      // Pause background audio
      if (backgroundAudioEnabled && backgroundAudio) {
        backgroundAudio.pause();
      }
      
      // Stop oscillators
      cleanupOscillators();

      // Synth ambient follows global Pause: stop it (no native pause on the
      // synth hook) and remember it was on so global Play resumes it.
      if (synthAmbient.isEnabled) {
        synthShouldResumeRef.current = true;
        try { synthAmbient.stop(); } catch (e) { console.warn('Synth pause-stop failed:', e); }
      }

      setIsPlaying(false);
      setIsPaused(true);
      isStoppingRef.current = false;
    } else if (isPaused) {
      // Resume the audio
      startAudio();
    }
  };

  const stopAudio = async () => {
    if (isStoppingRef.current || isStarting) return;
    isStoppingRef.current = true;
    
    try {
      // Push an HRV snapshot if the heart monitor was paired and there was
      // at least a minimal session. Runs BEFORE the session timer resets so
      // we can compute a real duration. Silently no-ops for anon users
      // (endpoint returns 401) and for sessions without HR data.
      try {
        const hm = heartMonitor;
        const startedAt = sessionTimer.sessionStartTime;
        const durSec = startedAt ? Math.max(0, Math.round((Date.now() - startedAt) / 1000)) : 0;
        const hasAnyHr = hm?.bpm != null || hm?.rmssd != null || hm?.rhythmScore != null;
        if (hasAnyHr && durSec >= 10 && !hrvSnapshotPostedRef.current) {
          hrvSnapshotPostedRef.current = true;
          const payload = {
            avg_bpm: hm.bpm ?? null,
            rmssd: hm.rmssd ?? null,
            rhythm_score: hm.rhythmScore ?? null,
            duration_sec: durSec,
            sample_count: hm.sampleCount ?? 0,
            beat_type: beatType,
            preset_name: currentPresetRef.current || null,
            device_name: hm.deviceName || null,
            synced_to_user: !!syncHeartbeatToUserRef.current,
          };
          // Fire-and-forget — don't block stop on network.
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/hrv/snapshots`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }).catch(() => { /* ignore — snapshots are best-effort */ });
        }
      } catch { /* never let metrics block a stop */ }

      // Stop timer
      sessionTimer.stop();
      
      // If recording, stop and save the recording
      if (isRecording && exportFilename) {
        setLoading(true);
        toast({
          title: "Finalizing Recording...",
          description: "Processing and saving your audio file",
        });
        
        await stopRecordingAndDownload(exportFilename);
        setExportFilename(''); // Clear filename
        setLoading(false);
      }
      
      // Cancel animation frame
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      
      // Cancel any scheduled gain automation and silence immediately
      if (gainNodeRef.current && audioContextRef.current) {
        gainNodeRef.current.gain.cancelScheduledValues(audioContextRef.current.currentTime);
        gainNodeRef.current.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      }
      
      // Stop oscillators
      cleanupOscillators();

      // Synth ambient follows global Stop: kill it and remember the user's
      // intent so the next global Play can re-start it with the same preset.
      if (synthAmbient.isEnabled) {
        synthShouldResumeRef.current = true;
        try { synthAmbient.stop(); } catch (e) { console.warn('Synth global-stop failed:', e); }
      } else {
        synthShouldResumeRef.current = false;
      }

      // Stop background audio
      if (backgroundAudio) {
        backgroundAudio.pause();
        backgroundAudio.currentTime = 0;
      }
      
      // Reset states
      setIsPlaying(false);
      setIsPaused(false);
      setCurrentTime(0);
      setPausedTime(0);
      setBackgroundAudioEnabled(false);
      animationPausedRef.current = false;
      // Restore volumeRef to slider value (may have been zeroed by fadeOut)
      volumeRef.current = volume[0];
      
      // Complete session in backend
      if (currentSessionId) {
        try {
          await axios.put(`${API}/sessions/${currentSessionId}/complete`);
          setCurrentSessionId(null);
        } catch (error) {
          console.error('Error completing session:', error);
        }
      }
    } catch (error) {
      console.error('Error stopping audio:', error);
    } finally {
      isStoppingRef.current = false;
    }
  };

  // Animation frame control functions for timeline dragging
  const pauseAnimationFrame = () => {
    animationPausedRef.current = true;
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  };

  const resumeAnimationFrame = () => {
    if (!isPlaying) return;
    
    animationPausedRef.current = false;
    
    // Restart animation frame from current position (with volume control)
    const updateTime = () => {
      if (isStoppingRef.current || animationPausedRef.current) return;
      
      const elapsed = audioContextRef.current.currentTime - startTimeRef.current;
      const clampedTime = Math.min(elapsed, duration);
      setCurrentTime(clampedTime);
      
      // Same live frequency automation as the start-path rAF loop, so beats
      // continue tracking the timeline after a manual playhead drag/release.
      applyLiveFrequencyAutomation(clampedTime);
      
      // Volume control via Web Audio API setTargetAtTime (click-free)
      if (gainNodeRef.current && audioContextRef.current) {
        let targetVol = volumeRef.current;
        
        if (volumeNodesRef.current && volumeNodesRef.current.length >= 2) {
          const sorted = [...volumeNodesRef.current].sort((a, b) => a.time - b.time);
          let automationVol = sorted[0].volume;
          if (clampedTime >= sorted[sorted.length - 1].time) {
            automationVol = sorted[sorted.length - 1].volume;
          } else {
            for (let i = 0; i < sorted.length - 1; i++) {
              if (clampedTime >= sorted[i].time && clampedTime <= sorted[i + 1].time) {
                const t = (clampedTime - sorted[i].time) / (sorted[i + 1].time - sorted[i].time);
                automationVol = sorted[i].volume + t * (sorted[i + 1].volume - sorted[i].volume);
                break;
              }
            }
          }
          targetVol = automationVol * volumeRef.current;
        }
        
        const now = audioContextRef.current.currentTime;
        gainNodeRef.current.gain.cancelScheduledValues(now);
        gainNodeRef.current.gain.setTargetAtTime(targetVol, now, 0.02);
      }
      
      if (elapsed < duration && !isStoppingRef.current && !animationPausedRef.current) {
        animationFrameRef.current = requestAnimationFrame(updateTime);
      } else if (elapsed >= duration) {
        stopAudio();
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(updateTime);
  };

  // Update volume in real-time
  const updateVolume = (newVolume) => {
    setVolume(newVolume);
    // Animation loop reads volumeRef and smoothly applies the change
  };

  // Handle background audio file upload (input change OR drag-drop both
  // route through processBackgroundAudioFile).
  const handleBackgroundAudioUpload = (event) => {
    const file = event.target?.files?.[0];
    if (file) processBackgroundAudioFile(file);
  };

  // Validate + ingest a File. Reused by the file <input> and the dropzone.
  const processBackgroundAudioFile = (file) => {
    if (!file) return;

    // Drag-drop can hand us anything; reject non-audio early with a friendly toast.
    const looksLikeAudio = file.type.startsWith('audio/') ||
      /\.(mp3|wav|ogg|m4a|aac|flac|webm)$/i.test(file.name);
    if (!looksLikeAudio) {
      toast({
        title: 'Unsupported file type',
        description: `"${file.name}" doesn't look like an audio file. Try mp3, wav, m4a, ogg, or flac.`,
        variant: 'destructive',
      });
      return;
    }

    // Persist to IndexedDB so this file appears in "Recently used" across
    // sessions. Non-blocking — UX shouldn't wait on storage.
    recentAudio.addRecent(file)
      .then(() => recentAudio.getRecentMeta().then(setRecentBgAudio))
      .catch((e) => console.warn('Recent-audio persist failed:', e));

    {
      // iter 25.2: keep beats playing during upload. The background channel
      // is fully independent of the binaural oscillators in the audio graph,
      // so we only need to swap the background source — not pause everything.
      // A short gain ramp on backgroundGain hides the click from disconnecting
      // the old MediaElementSource.
      const ctx = audioContextRef.current;
      if (ctx && backgroundGainRef.current) {
        const now = ctx.currentTime;
        backgroundGainRef.current.gain.cancelScheduledValues(now);
        backgroundGainRef.current.gain.setTargetAtTime(0, now, 0.02);
      }
      
      setBackgroundAudioFile(file);
      
      // Create audio URL for playback
      const audioUrl = URL.createObjectURL(file);
      const audio = new Audio(audioUrl);
      audio.loop = true;
      audio.crossOrigin = "anonymous"; // Allow Web Audio API access
      audio.volume = backgroundAudioVolume[0]; // Set volume immediately
      
      // Add event listeners for time tracking
      audio.addEventListener('loadedmetadata', () => {
        setBackgroundAudioDuration(audio.duration);
        console.log('Background audio metadata loaded, duration:', audio.duration);
      });
      
      audio.addEventListener('timeupdate', () => {
        if (!backgroundAudioSeeking) {
          setBackgroundAudioCurrentTime(audio.currentTime);
        }
      });
      
      audio.addEventListener('ended', () => {
        setBackgroundAudioCurrentTime(0);
      });
      
      // Clean up previous audio routing (but preserve MediaElementSource for reuse)
      if (backgroundAudioSource) {
        try {
          backgroundAudioSource.disconnect();
          console.log('Disconnected background audio source for new file');
        } catch (e) {
          console.warn('Error disconnecting audio source:', e);
        }
      }
      
      // Clean up previous audio element and remove from tracking
      if (backgroundAudio) {
        try {
          backgroundAudio.pause();
          backgroundAudio.removeEventListener('loadedmetadata', () => {});
          backgroundAudio.removeEventListener('timeupdate', () => {});
          backgroundAudio.removeEventListener('ended', () => {});
          
          // Remove from MediaElementSource tracking for new file
          connectedElements.current.delete(backgroundAudio);
          console.log('Cleaned up previous background audio element and removed from tracking');
        } catch (e) {
          console.warn('Error cleaning up previous audio element:', e);
        }
      }
      
      // Clean up 8D effects routing
      if (background8DNodes.current) {
        try {
          // Disconnect all 8D nodes
          if (background8DNodes.current.panner) background8DNodes.current.panner.disconnect();
          if (background8DNodes.current.eightDGain) background8DNodes.current.eightDGain.disconnect();
          if (background8DNodes.current.inputGain) background8DNodes.current.inputGain.disconnect();
          console.log('Cleaned up previous 8D audio nodes');
        } catch (e) {
          console.warn('Error cleaning up 8D nodes:', e);
        }
        background8DNodes.current = null;
      }
      
      // CRITICAL: Connect to Web Audio IMMEDIATELY before setting state
      console.log('🔧 Connecting new audio to Web Audio BEFORE playback...');
      
      // RESUME AudioContext if suspended (critical for audio to play)
      if (audioContextRef.current?.state === 'suspended') {
        audioContextRef.current.resume().then(() => {
          console.log('✅ AudioContext resumed');
        });
      }
      
      const immediateSource = getSingleMediaElementSource(audio);
      if (immediateSource && audioContextRef.current && backgroundGainRef.current) {
        try {
          immediateSource.disconnect();
        } catch (e) {}
        try {
          immediateSource.connect(backgroundGainRef.current);
          console.log('✅ PRE-CONNECTED: source → backgroundGain → destination');
          console.log('📊 AudioContext state:', audioContextRef.current.state);
        } catch (e) {
          console.error('❌ Pre-connection failed:', e);
        }
      }
      
      // Set new background audio
      setBackgroundAudio(audio);
      
      // Auto-enable background audio and start playing
      setBackgroundAudioEnabled(true);

      // iter 25.3: if the user already had 8D ON before uploading, rebuild
      // the spatial chain for the NEW audio element automatically. Without
      // this, the new MediaElementSource was only connected to the dry
      // backgroundGain path and 8D animation silently no-op'd until the
      // user manually toggled the switch off and on again.
      if (is8DBackgroundAudio && isPremium && audioContextRef.current) {
        try {
          const effect8D = create8DBackgroundEffect(audio, true);
          if (effect8D) {
            background8DNodes.current = effect8D;
            animate8DBackground(true);
            console.log('✅ 8D auto-rebuilt for new audio file');
          }
        } catch (e) {
          console.warn('Failed to auto-rebuild 8D for uploaded audio:', e);
        }
      }
      
      // Auto-play the background audio
      setTimeout(() => {
        audio.play().then(() => {
          console.log('✅ Background audio playing (Web Audio already connected)');

          // Ramp background gain back up to its previous level so the swap
          // is heard as a smooth crossfade, not an abrupt cut. Beats kept
          // playing throughout — no oscillator restart needed.
          if (audioContextRef.current && backgroundGainRef.current) {
            const t = audioContextRef.current.currentTime;
            backgroundGainRef.current.gain.cancelScheduledValues(t);
            backgroundGainRef.current.gain.setTargetAtTime(
              backgroundAudioVolume[0],
              t,
              0.05 // ~50ms fade-in
            );
          }
        }).catch(error => {
          console.warn('Auto-play prevented by browser policy:', error);

          toast({
            title: "Background Audio Ready",
            description: `${file.name} loaded. Click play to start background audio.`,
          });
        });
      }, 200);
    }
  };

  // ===== COMPLETE REWRITE: Simple, Bulletproof 8D Routing =====
  const setupAudioRouting = () => {
    // Validation
    if (!backgroundAudio || !audioContextRef.current || !backgroundGainRef.current) {
      return;
    }

    console.log('🔧 setupAudioRouting - 8D enabled:', is8DBackgroundAudio);
    
    // Get source (create once, reuse)
    const source = getSingleMediaElementSource(backgroundAudio);
    if (!source) {
      console.error('Failed to get audio source');
      return;
    }
    
    // Clean up old animation
    if (background8DAnimation.current) {
      cancelAnimationFrame(background8DAnimation.current);
      background8DAnimation.current = null;
    }
    
    // Clean up old 8D nodes
    if (background8DNodes.current) {
      Object.values(background8DNodes.current).forEach(node => {
        if (node?.disconnect) try { node.disconnect(); } catch (e) {}
        if (node?.stop) try { node.stop(); } catch (e) {}
      });
      background8DNodes.current = null;
    }
    
    // Disconnect source
    try { source.disconnect(); } catch (e) {}
    
    // Route based on 8D state
    if (is8DBackgroundAudio && isPremium) {
      // Enable 8D
      const effect8D = create8DBackgroundEffect(backgroundAudio, true);
      if (effect8D?.inputGain) {
        source.connect(effect8D.inputGain);
        background8DNodes.current = effect8D;
        effect8D.angle = 0;
        animate8DBackground(true);
        console.log('✅ 8D active');
      } else {
        // Fallback to stereo
        source.connect(backgroundGainRef.current);
        console.log('⚠️ 8D failed, using stereo');
      }
    } else {
      // Standard stereo
      source.connect(backgroundGainRef.current);
      console.log('✅ Stereo active');
    }
  };

  // Setup live audio routing for 8D effects
  const setupLiveAudioRouting = () => {
    // Validation
    if (!liveAudioSourceRef.current || !audioContextRef.current || !liveAudioGainRef.current) {
      return;
    }

    console.log('🔧 setupLiveAudioRouting - 8D enabled:', is8DBackgroundAudio);
    
    const liveSource = liveAudioSourceRef.current;
    
    // Clean up old animation
    if (background8DAnimation.current) {
      cancelAnimationFrame(background8DAnimation.current);
      background8DAnimation.current = null;
    }
    
    // Clean up old 8D nodes (but not the source or analyser!)
    if (background8DNodes.current) {
      Object.values(background8DNodes.current).forEach(node => {
        // Don't disconnect the source or analyser
        if (node !== liveSource && node !== liveAnalyserRef.current) {
          if (node?.disconnect) try { node.disconnect(); } catch (e) {}
          if (node?.stop) try { node.stop(); } catch (e) {}
        }
      });
      background8DNodes.current = null;
    }
    
    // CRITICAL: Disconnect liveSource from everything EXCEPT the analyser
    // The analyser must stay connected for the level meter
    try {
      // Get all connections
      const connections = [];
      
      // Try to disconnect from previous non-analyser connections
      // We need to disconnect liveSource carefully
      if (liveAudioGainRef.current) {
        try { liveSource.disconnect(liveAudioGainRef.current); } catch (e) {}
      }
      
      // If there was a previous 8D effect, disconnect from its inputGain
      // (but we already cleaned up 8D nodes above)
      
    } catch (e) {
      console.warn('Cleanup warning (expected):', e.message);
    }
    
    // Ensure liveAudioGain is disconnected and reconnected fresh
    try {
      liveAudioGainRef.current.disconnect();
    } catch (e) {}
    
    // IMPORTANT: For live audio, we need signal to flow for:
    // 1. Level meter visualization (via analyser - ALWAYS CONNECTED, never touch it)
    // 2. Recording capture (via recording destination)
    // 3. 8D processing (needs a destination for signal flow)
    // 4. Monitoring output (through liveAudioGain with volume control)
    
    if (is8DBackgroundAudio && isPremium) {
      // Enable 8D for live audio
      console.log('🎧 Creating 8D effect for live audio...');
      const dummyAudio = new Audio();
      const effect8D = create8DBackgroundEffect(dummyAudio, true);
      
      if (effect8D?.inputGain && effect8D?.clarityBoost) {
        // CRITICAL: The 8D effect connects to backgroundGainRef by default
        // For live audio, ALL paths must be redirected to liveAudioGainRef for monitoring control
        
        // Disconnect and reconnect for live audio monitoring
        // Both modes now use IDENTICAL routing (performance + optional reverb send)
        console.log('🔧 Redirecting 8D audio paths for live input...');
        
        // Disconnect clarity from backgroundGain
        if (effect8D.clarityBoost) {
          try {
            effect8D.clarityBoost.disconnect(backgroundGainRef.current);
            console.log('   Disconnected clarity from backgroundGain');
          } catch (e) {}
          
          // Reconnect to liveAudioGain
          effect8D.clarityBoost.connect(liveAudioGainRef.current);
          console.log('   ✅ Main path: clarity → liveAudioGain');
        }
        
        // Disconnect reverb from backgroundGain (full quality only)
        if (effect8D.reverbGain) {
          try {
            effect8D.reverbGain.disconnect(backgroundGainRef.current);
            console.log('   Disconnected reverb from backgroundGain');
          } catch (e) {}
          
          // Reconnect to liveAudioGain
          effect8D.reverbGain.connect(liveAudioGainRef.current);
          console.log('   ✅ Reverb send: reverb → liveAudioGain');
        }
        
        // Set monitoring volume and connect to destination
        const monitorVolume = liveAudioMonitoring ? 0.8 : 0;
        liveAudioGainRef.current.gain.setValueAtTime(monitorVolume, audioContextRef.current.currentTime);
        liveAudioGainRef.current.connect(audioContextRef.current.destination);
        
        // Connect source to 8D input (analyser connection is already there, don't touch it!)
        liveSource.connect(effect8D.inputGain);
        
        // Store reference for recording
        background8DNodes.current = effect8D;
        effect8D.angle = 0;
        
        // Start animation
        animate8DBackground(true);
        
        console.log('✅ Live audio 8D routing complete (ALL PATHS through liveGain):');
        console.log('   Path 1: liveSource → analyser (level meter - always on)');
        console.log('   Path 2: liveSource → 8D (panner+center) → outputGain → clarityBoost → liveGain → speakers');
        if (effect8D.reverbGain) {
          console.log('   Path 3: clarityBoost → reverb → reverbGain → liveGain → speakers (send/return)');
        }
        console.log(`   Monitoring: ${liveAudioMonitoring ? 'ON (volume: ' + monitorVolume + ')' : 'OFF (muted)'}`);
        console.log('   🔇 NO audio leak when monitoring OFF');
      } else {
        console.error('❌ 8D effect creation failed!');
        // Fallback: direct stereo routing
        liveSource.connect(liveAudioGainRef.current);
        const monitorVolume = liveAudioMonitoring ? 0.8 : 0;
        liveAudioGainRef.current.gain.setValueAtTime(monitorVolume, audioContextRef.current.currentTime);
        liveAudioGainRef.current.connect(audioContextRef.current.destination);
        console.log('⚠️ Fallback to stereo routing');
      }
    } else {
      // Standard stereo - direct connection
      console.log('🔇 Setting up stereo routing for live audio');
      liveSource.connect(liveAudioGainRef.current);
      const monitorVolume = liveAudioMonitoring ? 0.8 : 0;
      liveAudioGainRef.current.gain.setValueAtTime(monitorVolume, audioContextRef.current.currentTime);
      liveAudioGainRef.current.connect(audioContextRef.current.destination);
      console.log(`✅ Live audio stereo routing complete (monitoring: ${liveAudioMonitoring ? 'ON' : 'OFF'})`);
    }
  };

  // Start/stop background audio
  const toggleBackgroundAudio = () => {
    if (backgroundAudio) {
      if (backgroundAudioEnabled) {
        // Stop background audio and cleanup 8D effects
        backgroundAudio.pause();
        setBackgroundAudioEnabled(false);
        
        // Stop 8D animation
        if (background8DAnimation.current) {
          cancelAnimationFrame(background8DAnimation.current);
          background8DAnimation.current = null;
        }
      } else {
        // OPTION A: Stop live audio if it's active (mutual exclusivity)
        if (liveAudioMode) {
          console.log('⏹️ Stopping live audio to enable background MP3');
          stopLiveAudioInput();
          setLiveAudioMode(false);
        }
        
        // Start background audio
        backgroundAudio.volume = backgroundAudioVolume[0];
        
        // Ensure audio context is running
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume();
        }
        
        // Start audio - routing will be set up by useEffect automatically
        backgroundAudio.play().catch(error => {
          console.error('Error starting background audio:', error);
        });
        
        setBackgroundAudioEnabled(true);
      }
    }
  };

  // Update background audio volume
  const updateBackgroundVolume = (newVolume) => {
    setBackgroundAudioVolume(newVolume);
    if (backgroundAudio) {
      backgroundAudio.volume = newVolume[0];
    }
    // Live audio is always muted to prevent feedback with loopback devices
    // Volume control only affects background MP3
  };

  // ===== LIVE AUDIO INPUT FUNCTIONS =====
  
  // Start live audio input capture
  const startLiveAudioInput = async (deviceId = null) => {
    try {
      // OPTION A: Stop background MP3 if it's playing (mutual exclusivity)
      if (backgroundAudio && backgroundAudioEnabled) {
        console.log('⏹️ Stopping background MP3 to enable live audio');
        backgroundAudio.pause();
        setBackgroundAudioEnabled(false);
        
        // Stop 8D animation
        if (background8DAnimation.current) {
          cancelAnimationFrame(background8DAnimation.current);
          background8DAnimation.current = null;
        }
      }
      
      // Build audio constraints
      const audioConstraints = {
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false
      };
      
      // Use selected input device, or specific device if requested, or default
      const deviceToUse = deviceId || selectedInputDevice;
      if (deviceToUse && deviceToUse !== 'default') {
        audioConstraints.deviceId = { exact: deviceToUse };
      }
      
      console.log('🎤 Requesting audio input with device:', deviceToUse || 'default');
      
      // Request audio input from user (will capture loopback device if configured)
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: false
      });
      
      console.log('🎤 Live audio stream captured');
      setLiveAudioStream(stream);
      
      // Create audio source from stream
      if (audioContextRef.current) {
        const liveSource = audioContextRef.current.createMediaStreamSource(stream);
        liveAudioSourceRef.current = liveSource;
        
        // Create analyser for level meter
        const analyser = audioContextRef.current.createAnalyser();
        analyser.fftSize = 256;
        analyser.smoothingTimeConstant = 0.8;
        liveAnalyserRef.current = analyser;
        
        // Create separate gain node for live audio
        const liveGain = audioContextRef.current.createGain();
        liveGain.gain.value = 0; // Muted
        liveAudioGainRef.current = liveGain;
        
        // CRITICAL: Connect analyser directly from source BEFORE any routing
        liveSource.connect(analyser);
        console.log('✅ Analyser connected directly to liveSource');
        
        // Test analyser immediately
        setTimeout(() => {
          const testData = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(testData);
          const testAvg = testData.reduce((a,b) => a+b, 0) / testData.length;
          console.log(`🧪 TEST: Analyser receiving data? Average level: ${testAvg.toFixed(2)}`);
        }, 1000);
        
        // Setup routing based on 8D state
        // This will connect liveSource → liveGain → destination (or through 8D effects)
        setupLiveAudioRouting();
        
        console.log('✅ Live audio connected - monitoring only (muted to prevent feedback)');
        
        // Start level meter animation
        startLevelMeterAnimation();
      }
      
    } catch (error) {
      console.error('Error starting live audio input:', error);
      
      let errorMessage = "Failed to access audio input. ";
      if (error.name === 'NotAllowedError') {
        errorMessage += "Please grant microphone permission.";
      } else if (error.name === 'NotFoundError') {
        errorMessage += "No audio input device found. Make sure your loopback device (BlackHole/VB-Cable) is configured.";
      } else {
        errorMessage += "Please check your audio setup.";
      }
      
      toast({
        title: "Live Input Error",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };
  
  // Stop live audio input
  const stopLiveAudioInput = () => {
    // Stop level meter animation
    if (liveAudioAnimationRef.current) {
      cancelAnimationFrame(liveAudioAnimationRef.current);
      liveAudioAnimationRef.current = null;
    }
    
    // Disconnect and clean up audio nodes
    if (liveAudioSourceRef.current) {
      try {
        liveAudioSourceRef.current.disconnect();
      } catch (e) {
        console.warn('Error disconnecting live audio source:', e);
      }
      liveAudioSourceRef.current = null;
    }
    
    if (liveAnalyserRef.current) {
      try {
        liveAnalyserRef.current.disconnect();
      } catch (e) {
        console.warn('Error disconnecting analyser:', e);
      }
      liveAnalyserRef.current = null;
    }
    
    if (liveAudioGainRef.current) {
      try {
        liveAudioGainRef.current.disconnect();
      } catch (e) {
        console.warn('Error disconnecting live audio gain:', e);
      }
      liveAudioGainRef.current = null;
    }
    
    // Stop media stream tracks
    if (liveAudioStream) {
      liveAudioStream.getTracks().forEach(track => track.stop());
      setLiveAudioStream(null);
    }
    
    setLiveAudioLevel(0);
    console.log('🛑 Live audio input stopped');
  };
  
  // Toggle live audio mode
  const toggleLiveAudioMode = async () => {
    const newMode = !liveAudioMode;
    setLiveAudioMode(newMode);
    
    if (newMode) {
      // Enable live audio mode
      await startLiveAudioInput();
    } else {
      // Disable live audio mode
      stopLiveAudioInput();
    }
  };
  
  // Set output device for audio playback
  const setOutputDevice = async (deviceId) => {
    try {
      const ctx = audioContextRef.current;
      if (!ctx) {
        console.warn('AudioContext not initialized');
        return false;
      }

      // Check if setSinkId is supported on AudioContext
      if (typeof ctx.setSinkId === 'function') {
        // Modern browser with AudioContext.setSinkId support (Chrome 110+)
        await ctx.setSinkId(deviceId || '');
        setSelectedOutputDevice(deviceId || 'default');
        
        console.log('✅ Output device set to:', deviceId || 'default');
        
        const device = audioOutputDevices.find(d => d.deviceId === deviceId);
        toast({
          title: "Output Device Changed",
          description: `Now playing through: ${device?.label || 'Default device'}`,
        });
        
        return true;
      } else {
        // Fallback for browsers without AudioContext.setSinkId
        console.warn('setSinkId not supported on AudioContext in this browser');
        toast({
          title: "Limited Support",
          description: "Output device selection is not fully supported in this browser. Using system default. Try Chrome 110+ for full support.",
          variant: "destructive",
        });
        return false;
      }
    } catch (error) {
      console.error('Error setting output device:', error);
      
      // Provide helpful error message
      let errorMsg = "Failed to change output device. ";
      if (error.name === 'NotFoundError') {
        errorMsg += "Device not found. It may have been disconnected.";
      } else if (error.name === 'NotAllowedError') {
        errorMsg += "Permission denied. Please allow audio output changes.";
      } else {
        errorMsg += error.message;
      }
      
      toast({
        title: "Output Device Error",
        description: errorMsg,
        variant: "destructive",
      });
      return false;
    }
  };
  
  // Enumerate available audio input and output devices
  const listAudioDevices = async () => {
    try {
      // IMPORTANT: Request permission first to get device labels
      // Without permission, enumerateDevices() returns devices but with empty labels
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop the stream immediately - we just needed permission
        stream.getTracks().forEach(track => track.stop());
      } catch (permError) {
        console.warn('Permission denied, device labels may be limited:', permError);
      }
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      const audioInputs = devices.filter(device => device.kind === 'audioinput');
      const audioOutputs = devices.filter(device => device.kind === 'audiooutput');
      
      console.log('📱 Available input devices:', audioInputs.map(d => ({
        id: d.deviceId,
        label: d.label || 'Unnamed device',
        groupId: d.groupId
      })));
      
      console.log('🔊 Available output devices:', audioOutputs.map(d => ({
        id: d.deviceId,
        label: d.label || 'Unnamed device',
        groupId: d.groupId
      })));
      
      setAvailableDevices(audioInputs);
      setAudioInputDevices(audioInputs);
      setAudioOutputDevices(audioOutputs);
      return { inputs: audioInputs, outputs: audioOutputs };
    } catch (error) {
      console.error('Error enumerating devices:', error);
      toast({
        title: "Device List Error",
        description: "Could not list available audio devices. Please grant microphone permission.",
        variant: "destructive",
      });
      return { inputs: [], outputs: [] };
    }
  };
  
  // Change live audio input device
  const changeLiveAudioDevice = async () => {
    // Get list of available devices
    const devices = await listAudioDevices();
    
    if (devices.length === 0) {
      toast({
        title: "No Devices Found",
        description: "No audio input devices available",
        variant: "destructive",
      });
      return;
    }
    
    // Show device selector modal
    setShowDeviceSelector(true);
  };
  
  // Select a specific device and switch to it
  const selectAudioDevice = async (deviceId) => {
    // Stop current stream but keep live mode active
    if (liveAudioStream) {
      liveAudioStream.getTracks().forEach(track => track.stop());
    }
    
    // Clean up current audio nodes
    if (liveAudioSourceRef.current) {
      try {
        liveAudioSourceRef.current.disconnect();
      } catch (e) {}
      liveAudioSourceRef.current = null;
    }
    
    if (liveAnalyserRef.current) {
      try {
        liveAnalyserRef.current.disconnect();
      } catch (e) {}
      liveAnalyserRef.current = null;
    }
    
    if (liveAudioGainRef.current) {
      try {
        liveAudioGainRef.current.disconnect();
      } catch (e) {}
      liveAudioGainRef.current = null;
    }
    
    // Stop level meter
    if (liveAudioAnimationRef.current) {
      cancelAnimationFrame(liveAudioAnimationRef.current);
      liveAudioAnimationRef.current = null;
    }
    
    setLiveAudioLevel(0);
    
    // Start with new device
    await startLiveAudioInput(deviceId);
    setSelectedDeviceId(deviceId);
    setShowDeviceSelector(false);
    
    toast({
      title: "Device Changed",
      description: "Switched to new audio input device",
    });
  };
  
  // Animate level meter
  const startLevelMeterAnimation = () => {
    console.log('🎬 Starting level meter animation...');
    
    const updateLevel = () => {
      if (liveAnalyserRef.current && liveAudioMode) {
        const dataArray = new Uint8Array(liveAnalyserRef.current.frequencyBinCount);
        liveAnalyserRef.current.getByteFrequencyData(dataArray);
        
        // Calculate average level
        const average = dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;
        const normalizedLevel = Math.min(average / 255, 1); // Normalize to 0-1 and cap at 1
        
        setLiveAudioLevel(normalizedLevel);
        
        // Debug log every 60 frames (~1 second)
        if (Math.random() < 0.016) {
          console.log(`📊 Live audio level: ${Math.round(normalizedLevel * 100)}%`);
        }
        
        // Continue animation loop
        liveAudioAnimationRef.current = requestAnimationFrame(updateLevel);
      } else {
        // If analyser is gone or live audio stopped, stop the loop
        console.log('⏹️ Level meter animation stopped');
        setLiveAudioLevel(0);
        liveAudioAnimationRef.current = null;
      }
    };
    
    // Cancel any existing animation before starting new one
    if (liveAudioAnimationRef.current) {
      cancelAnimationFrame(liveAudioAnimationRef.current);
      console.log('🔄 Cancelled existing level meter animation');
    }
    
    updateLevel();
    console.log('✅ Level meter animation started');
  };
  
  // OPTION A: Removed applyLiveAudio8D - live and background audio are mutually exclusive
  // Live audio uses simple stereo routing, background audio can use 8D effects

  // Update start time when manually setting current time during playback
  const updateStartTimeForManualSeek = (newTime) => {
    if (isPlaying && audioContextRef.current) {
      startTimeRef.current = audioContextRef.current.currentTime - newTime;
    }
  };

  const getCurrentFrequency = (time = currentTime) => {
    // For heartbeat, use baseFrequency as BPM instead of timeline frequency
    if (beatType === 'heartbeat') {
      const bpm = Math.max(10, Math.min(100, baseFrequency));
      return bpm; // Return BPM value
    }
    
    // Find the current frequency based on timeline position for other beat types
    const sortedNodes = [...timelineNodes].sort((a, b) => a.time - b.time);

    // Guard: an empty timeline is now reachable (user can delete every node).
    // Falling through would deref `undefined.time` and crash the whole render.
    // Return a sensible default so the app stays alive; user still sees the
    // Add-Node button and can rebuild.
    if (sortedNodes.length === 0) return 10;
    if (sortedNodes.length === 1) return sortedNodes[0].frequency;

    if (time === 0) return sortedNodes[0].frequency;
    
    // Find the two nodes to interpolate between
    let prevNode = sortedNodes[0];
    let nextNode = sortedNodes[sortedNodes.length - 1];
    
    for (let i = 0; i < sortedNodes.length - 1; i++) {
      if (time >= sortedNodes[i].time && time <= sortedNodes[i + 1].time) {
        prevNode = sortedNodes[i];
        nextNode = sortedNodes[i + 1];
        break;
      }
    }
    
    // Linear interpolation
    const timeDiff = nextNode.time - prevNode.time;
    const freqDiff = nextNode.frequency - prevNode.frequency;
    const progress = (time - prevNode.time) / timeDiff;
    
    return prevNode.frequency + (freqDiff * progress);
  };

  // Cleanup on unmount — kills everything (oscillators, animations, audio).
  // Empty deps array so this fires ONLY when the component is destroyed,
  // not every time backgroundAudio or liveAudioStream changes. The previous
  // version had [backgroundAudio, liveAudioStream] deps, which made the
  // cleanup fire on every audio upload — taking down the beats with it.
  useEffect(() => {
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (liveAudioAnimationRef.current) {
        cancelAnimationFrame(liveAudioAnimationRef.current);
      }
      cleanupOscillators();
    };
  }, []);

  // Stale-audio-element cleanup — runs when the user swaps to a new audio
  // file. Only touches the OLD audio element (pauses + frees its blob URL);
  // does NOT touch the oscillators. The new audio element is set up fresh
  // by the upload handler.
  useEffect(() => {
    const prevAudio = backgroundAudio;
    return () => {
      if (prevAudio && prevAudio !== backgroundAudio) {
        try {
          prevAudio.pause();
          prevAudio.src = '';
        } catch (e) { /* already gone */ }
      }
    };
  }, [backgroundAudio]);

  // Stale live-stream cleanup — same pattern for the microphone stream.
  useEffect(() => {
    const prevStream = liveAudioStream;
    return () => {
      if (prevStream && prevStream !== liveAudioStream) {
        try { prevStream.getTracks().forEach(t => t.stop()); } catch (e) { /* ignore */ }
      }
    };
  }, [liveAudioStream]);

  // Generate default export filename
  // Thin wrapper around lib/brainwaves.js#generateExportFilename so callers
  // don't have to thread the args themselves.
  const generateExportFilenameLocal = () =>
    generateExportFilename({ beatType, freq: currentFreq, duration });

  // Handle export with save dialog
  const handleExportClick = () => {
    if (!isPremium) {
      setShowPremiumModal(true);
      toast({
        title: "Premium Feature",
        description: "MP3 export is available for Premium users only.",
        variant: "destructive",
      });
      return;
    }

    if (!currentSessionId) {
      toast({
        title: "Error",
        description: "No active session to export",
        variant: "destructive",
      });
      return;
    }

    // Generate default filename and show save dialog
    const defaultFilename = generateExportFilenameLocal();
    setExportFilename(defaultFilename);
    setShowSaveDialog(true);
  };

  // Perform the actual export with real-time recording
  const exportAudio = async (filename) => {
    if (!isPlaying) {
      toast({
        title: "Cannot Export",
        description: "Please start playback first, then export to record the session",
        variant: "destructive",
      });
      return;
    }
    
    setLoading(true);
    
    try {
      toast({
        title: "Recording Started 🎙️",
        description: "Playing your session and recording audio. This will take the full session duration.",
      });
      
      // Start recording (now async)
      await startRecording();
      
      // Wait for the session to complete or stop button to be pressed
      // We'll record until manually stopped or session ends
      
      toast({
        title: "Recording in Progress...",
        description: "Press Stop button when ready to save the recording.",
      });
      
      setLoading(false);
      
    } catch (error) {
      console.error('Error starting export:', error);
      toast({
        title: "Export Failed",
        description: error.message || "Failed to start recording",
        variant: "destructive",
      });
      setLoading(false);
    }
  };

  // Start recording — thin wrapper around useRecording hook (Phase 2 refactor).
  // Hook owns lifecycle, MediaRecorder, timer, and tap disconnect bookkeeping.
  // We supply the audio nodes the hook should tap, plus a custom setup for
  // live audio (which has 8D-aware routing the generic taps API doesn't cover).
  const startRecording = async () => {
    if (!audioContextRef.current) {
      toast({
        title: "Audio Context Not Ready",
        description: "Please start playing audio first",
        variant: "destructive",
      });
      return;
    }
    if (!gainNodeRef.current) {
      toast({
        title: "Audio Not Ready",
        description: "Main gain node not initialized — start playback first.",
        variant: "destructive",
      });
      return;
    }

    try {
      await recording.startRecording({
        taps: [
          { name: 'beats',      getNode: () => gainNodeRef.current },
          { name: 'background', getNode: () => (backgroundAudioEnabled ? backgroundGainRef.current : null) },
          { name: 'synth-bus',  getNode: () => synthOutputBusRef.current },
        ],
        // Live audio is special — 8D path needs to capture from outputGain,
        // non-8D from the source directly. Returns its own cleanup fn.
        setupExtraTaps: (dest) => {
          if (!liveAudioMode || !liveAudioSourceRef.current) return null;

          const liveRecordingGain = audioContextRef.current.createGain();
          liveRecordingGain.gain.value = 0.8;

          const use8D = is8DBackgroundAudio && isPremium && background8DNodes.current?.outputGain;
          const upstream = use8D ? background8DNodes.current.outputGain : liveAudioSourceRef.current;

          try {
            upstream.connect(liveRecordingGain);
            liveRecordingGain.connect(dest);
            console.log(`✅ Live audio (${use8D ? '8D' : 'stereo'}) connected to recording`);
          } catch (e) {
            console.error('❌ Failed to connect live audio to recording:', e);
            return null;
          }

          return () => {
            try { liveRecordingGain.disconnect(dest); } catch { /* ignore */ }
            try { upstream.disconnect(liveRecordingGain); } catch { /* ignore */ }
          };
        },
      });
    } catch (error) {
      console.error('❌ Error starting recording:', error);
      toast({
        title: "Recording Error",
        description: error.message || "Failed to start audio recording. Try restarting the audio.",
        variant: "destructive",
      });
    }
  };
  
  // Stop recording and trigger download. Hook handles tap disconnects + state
  // reset; we just orchestrate encoding (WebM → MP3 if requested) + download UX.
  const stopRecordingAndDownload = async (filename) => {
    if (!recording.isRecording) return;
    try {
      const blob = await recording.stopRecording();
      console.log(`📦 Got recording blob: ${(blob.size / 1024 / 1024).toFixed(2)} MB`);
      await downloadAudioFile(blob, filename);
    } catch (e) {
      console.error('stopRecordingAndDownload failed:', e);
      toast({
        title: 'Recording Error',
        description: e.message || 'Failed to finalize recording.',
        variant: 'destructive',
      });
    }
  };
  
  // Download audio file with format options
  // iter 25.4: client-side MP3 encoding via lamejs. The previous
  // server-side path required ffmpeg + suffered ingress timeouts on long
  // recordings. Client-side runs in-browser, ~10x realtime, no server load.
  const downloadAudioFile = async (blob, filename, format = exportFormat) => {
    console.log(`📦 Audio blob ready: ${(blob.size / 1024 / 1024).toFixed(2)} MB → format=${format}`);

    // Verify blob has data
    if (blob.size === 0) {
      throw new Error('Recording failed: Empty audio file. Please ensure audio is playing and try again.');
    }

    try {
      if (format.startsWith('mp3')) {
        const bitrate = format === 'mp3-256' ? 256 : 192;
        toast({ title: `Encoding MP3 @ ${bitrate}k…`, description: 'Quick conversion in your browser. No upload.' });

        const { webmBlobToMp3 } = await import('../lib/mp3Encoder');
        const mp3Blob = await webmBlobToMp3(blob, {
          bitrate,
          onProgress: (p) => {
            if (Math.round(p * 10) % 2 === 0) {
              console.log(`MP3 ${bitrate}k: ${Math.round(p * 100)}%`);
            }
          },
        });
        console.log(`✅ MP3 ${bitrate}k ready: ${(mp3Blob.size / 1024 / 1024).toFixed(2)} MB`);
        await downloadFile(mp3Blob, `${filename}.mp3`, 'audio/mpeg');
        toast({
          title: 'Export Complete ✅',
          description: `Saved as ${filename}.mp3 — ${bitrate} kbps (${(mp3Blob.size / 1024 / 1024).toFixed(2)} MB)`,
        });
      } else {
        // WebM — direct blob download, no encoding
        console.log('💾 Saving as WebM (native, no conversion)');
        await downloadFile(blob, `${filename}.webm`, 'audio/webm');
        toast({
          title: 'Export Complete ✅',
          description: `Saved as ${filename}.webm (${(blob.size / 1024 / 1024).toFixed(2)} MB)`,
        });
      }
      
    } catch (error) {
      console.error('Error during export:', error);
      
      // Fallback: always save as WebM if anything fails
      console.log('⚠️ Falling back to WebM export');
      await downloadFile(blob, `${filename}.webm`, 'audio/webm');
      
      toast({
        title: "Saved as WebM",
        description: "MP3 conversion unavailable. WebM file saved successfully.",
      });
    }
  };
  
  // Helper function to download a file with Android-specific handling
  const downloadFile = async (blob, filename, mimeType) => {
    const isAndroid = /android/i.test(navigator.userAgent);
    const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
    
    console.log('📥 downloadFile called:', {
      platform: isAndroid ? 'Android' : isIOS ? 'iOS' : 'Desktop',
      filename,
      blobSize: `${(blob.size / 1024 / 1024).toFixed(2)} MB`,
      mimeType
    });
    
    // Android: Try Web Share API first (more reliable)
    if (isAndroid && navigator.share && navigator.canShare) {
      try {
        const file = new File([blob], filename, { type: mimeType });
        
        if (navigator.canShare({ files: [file] })) {
          console.log('📤 Using Web Share API on Android');
          
          await navigator.share({
            files: [file],
            title: 'NeuroWave Audio Export',
            text: 'Brainwave audio session'
          });
          
          toast({
            title: "✅ File Shared!",
            description: `${filename} ready to save or share`,
            duration: 5000,
          });
          return;
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.warn('Web Share API failed:', error);
        } else {
          console.log('User cancelled share');
          throw error; // Re-throw AbortError to prevent fallback
        }
      }
    }
    
    // Try modern File System Access API (Desktop Chrome, Edge)
    if (window.showSaveFilePicker && !isAndroid && !isIOS) {
      try {
        const extension = filename.split('.').pop();
        const fileHandle = await window.showSaveFilePicker({
          suggestedName: filename,
          types: [{
            description: 'Audio Files',
            accept: { [mimeType]: [`.${extension}`] }
          }]
        });
        const writable = await fileHandle.createWritable();
        await writable.write(blob);
        await writable.close();
        
        toast({
          title: "✅ File Saved!",
          description: `Saved to your chosen location: ${filename}`,
        });
        return;
      } catch (error) {
        if (error.name === 'AbortError') {
          toast({
            title: "Export Cancelled",
            description: "File save was cancelled",
          });
          throw error;
        }
        console.warn('File System API failed, using fallback:', error);
      }
    }
    
    // Mobile/Fallback: Standard download link method
    console.log('⬇️ Using standard download link method');
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    
    // Android-specific: Try to force download with additional attributes
    if (isAndroid) {
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      // Some Android browsers respect this better
      link.setAttribute('download', filename);
    }
    
    // Make link invisible but in DOM
    link.style.display = 'none';
    document.body.appendChild(link);
    
    console.log('🖱️ Triggering download click');
    
    // Trigger download with a delay for Android
    if (isAndroid) {
      setTimeout(() => link.click(), 100);
    } else {
      link.click();
    }
    
    // Cleanup with longer delay on Android
    setTimeout(() => {
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('🧹 Cleanup complete');
    }, isAndroid ? 1000 : 100);
    
    // Android-specific user guidance
    if (isAndroid) {
      toast({
        title: "✅ Download Triggered!",
        description: (
          <div className="space-y-2">
            <div>File: <strong>{filename}</strong></div>
            <div>Size: <strong>{(blob.size / 1024 / 1024).toFixed(2)} MB</strong></div>
            <div className="text-xs mt-2">📱 <strong>Where to find it:</strong></div>
            <div className="text-xs">• <strong>Downloads</strong> folder (Files app)</div>
            <div className="text-xs">• Pull down notification shade</div>
            <div className="text-xs">• Chrome menu → Downloads</div>
            <div className="text-xs mt-2 text-yellow-300">⚠️ <strong>If not found:</strong></div>
            <div className="text-xs text-yellow-300">• Check Chrome permissions (Settings → Apps → Chrome → Permissions → Storage)</div>
            <div className="text-xs text-yellow-300">• Or try using "Share" option instead</div>
          </div>
        ),
        duration: 15000, // Show for 15 seconds
      });
    } else if (isIOS) {
      toast({
        title: "✅ Download Started!",
        description: (
          <div className="space-y-2">
            <div>File: <strong>{filename}</strong></div>
            <div className="text-xs">📱 <strong>On iOS:</strong></div>
            <div className="text-xs">• Check <strong>Files app → Downloads</strong></div>
            <div className="text-xs">• Or look in browser's download manager</div>
          </div>
        ),
        duration: 10000,
      });
    } else {
      toast({
        title: "✅ Download Started!",
        description: `Check your Downloads folder for: ${filename}`,
        duration: 5000,
      });
    }
  };

  // Save preset function - handles both create and save-as-user
  const savePreset = async () => {
    if (!isPremium) {
      toast({
        title: "Premium Required",
        description: "Preset saving is available for premium users only.",
        variant: "destructive",
      });
      return;
    }

    if (!presetName.trim()) {
      toast({
        title: "Error",
        description: "Please enter a preset name",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const presetData = {
        name: presetName,
        nodes: timelineNodes,
        beat_type: beatType,
        base_frequency: baseFrequency,
        duration: duration,
        volume: volume[0],
        description: `Custom ${beatType} beats preset`,
        tags: ['custom', 'user-created']
      };

      let response;
      
      if (editingPreset && editingPreset.is_builtin) {
        // Save builtin preset as new user preset
        response = await axios.post(`${API}/presets/save-as-user/${editingPreset.id}`, presetData);
        
        if (response.status === 200) {
          toast({
            title: "Preset Saved",
            description: `"${presetName}" has been saved as your custom preset`,
          });
        }
      } else if (editingPreset && !editingPreset.is_builtin) {
        // Update existing user preset
        response = await axios.put(`${API}/presets/${editingPreset.id}`, presetData);
        
        if (response.status === 200) {
          toast({
            title: "Preset Updated",
            description: `"${presetName}" has been successfully updated`,
          });
        }
      } else {
        // Create new user preset
        response = await axios.post(`${API}/presets`, presetData);
        
        if (response.status === 200) {
          toast({
            title: "Preset Saved",
            description: `"${presetName}" has been saved successfully`,
          });
        }
      }

      // Clear editing state and refresh presets
      setEditingPreset(null);
      setPresetName('');
      
      // Refresh all presets
      loadAllPresets();

    } catch (error) {
      console.error('Error saving preset:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || 'Failed to save preset',
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePremiumUpgrade = async () => {
    setShowPremiumModal(true);
  };

  // Smooth preset change — when a preset is loaded mid-playback, the old
  // behaviour was to call stopAudio() and leave the user staring at a silent
  // app waiting to manually press Play again. Now we capture the wasPlaying
  // state, await the stop, apply the new preset, then auto-resume after a
  // 220ms state-flush window (same pattern as startCoherenceSession). The
  // brief gap is intentional: it gives the engine a clean tear-down without
  // running two oscillator graphs simultaneously, and it sounds like a deep
  // breath between sessions rather than a jarring cut.
  const loadPreset = async (presetId, preset = null) => {
    try {
      // Find the preset if not provided
      if (!preset) {
        preset = [...freePresets, ...premiumPresets, ...userPresets, ...presets]
          .find(p => p.id === presetId);
      }
      
      if (!preset) {
        toast({
          title: "Error",
          description: "Preset not found",
          variant: "destructive",
        });
        return;
      }

      // Capture playback intent BEFORE stopAudio resets isPlaying/isPaused so
      // we can auto-resume with the new preset.
      const wasPlaying = isPlaying || isPaused;

      if (wasPlaying) {
        console.log('🔁 Smooth preset change — stopping current audio before swap');
        try { await stopAudio(); }
        catch (e) { console.warn('Stop before preset load failed:', e); }
      }

      // Load preset data into the timeline
      setTimelineNodes(preset.nodes);
      setBeatType(preset.beat_type);
      setBaseFrequency(preset.base_frequency);
      setDuration(preset.duration);
      setVolume([preset.volume]);
      setCurrentPreset(presetId);
      
      // Track current preset for session history
      currentPresetRef.current = preset.name;

      // Increment usage count
      axios.post(`${API}/presets/${presetId}/use`).catch(console.error);

      // Auto-resume with the freshly-loaded preset. setTimeout deliberately
      // calls startAudioRef.current (not startAudio directly) so the closure
      // captured here doesn't carry stale beatType/baseFrequency from before
      // the React state batch flushed — same fix pattern as the Coherence
      // launcher. skipPlayingCheck=true because isPlaying may not yet be
      // false even though stopAudio finished.
      if (wasPlaying) {
        toast({
          title: '🎚️ Switching preset',
          description: `Loading ${preset.name}…`,
        });
        setTimeout(() => { startAudioRef.current?.(true); }, 220);
      }
    } catch (error) {
      console.error('Error loading preset:', error);
      toast({
        title: "Error",
        description: "Failed to load preset",
        variant: "destructive",
      });
    }
  };

  // Heartbeat is a synthetic non-band so we keep the special case inline.
  // Pure range lookup + descriptions live in lib/brainwaves.js.
  const getBrainwaveType = (frequency) => {
    if (beatType === 'heartbeat') {
      return { type: 'heartbeat', color: '#FF0044', name: 'Heartbeat', min: 10, max: 100 };
    }
    return getBrainwaveRange(frequency);
  };

  // getBrainwaveDescription moved to lib/brainwaves.js (Phase 1 refactor).

  const currentFreq = useMemo(() => {
    return getCurrentFrequency();
  }, [beatType, baseFrequency, currentTime, timelineNodes]);
  
  const currentBrainwave = useMemo(() => {
    return getBrainwaveType(currentFreq);
  }, [currentFreq, beatType]);

  // Theme-aware class helpers
  const cardClass = theme.cardBg;
  const textClass = 'text-white';
  const textMutedClass = theme.mutedText;

  return (
    <div className={`relative min-h-screen p-2 sm:p-4 transition-colors duration-500 overflow-x-hidden ${theme.bg}`} style={theme.bgStyle || undefined}>
      {/* Signature-theme ambient glow orbs — match the landing page aesthetic */}
      {themeId === 'signature' && (
        <div className="fixed inset-0 pointer-events-none z-0" aria-hidden="true">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-purple-600/10 blur-[150px]" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] rounded-full bg-cyan-600/[0.08] blur-[120px]" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full bg-blue-600/[0.06] blur-[100px]" />
        </div>
      )}
      <div className="relative z-10">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6 pb-24 md:pb-0">
        {/* Status Badge */}
        {!user ? (
          <div className="text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 bg-gradient-to-r from-purple-500/20 to-gold-500/20 border border-purple-500/30 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl backdrop-blur-md max-w-full">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-400 shrink-0" />
                <span className="font-medium text-sm sm:text-base">Want premium features?</span>
              </div>
              <Button
                onClick={() => setShowLoginModal(true)}
                className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap"
                data-testid="guest-signin-btn"
              >
                Sign In — 22 Days Free Trial
              </Button>
            </div>
          </div>
        ) : subscriptionStatus === 'trial' ? (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-purple-500/30 text-white px-4 py-2 rounded-full text-sm">
              <Clock className="w-4 h-4 text-cyan-400" />
              <span>Trial: <strong>{trialDaysLeft}</strong> days left</span>
              <span className="text-white/30">|</span>
              <button onClick={() => setShowPremiumModal(true)} className="text-purple-400 hover:text-purple-300 font-medium" data-testid="trial-upgrade-link">
                Upgrade $9.99
              </button>
            </div>
          </div>
        ) : isPremium ? (
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-purple-500 to-gold-500 text-white px-4 py-2 rounded-full text-sm">
              <Crown className="w-4 h-4" />
              Premium Active
            </div>
          </div>
        ) : subscriptionStatus === 'expired' ? (
          <div className="text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-500/30 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl backdrop-blur-md max-w-full">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-400 shrink-0" />
                <span className="font-medium text-sm sm:text-base">Trial Expired</span>
              </div>
              <Button
                onClick={() => setShowPremiumModal(true)}
                className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap"
                data-testid="upgrade-cta-btn"
              >
                Get Premium — $9.99 Lifetime
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="inline-flex flex-col sm:flex-row items-center gap-2 sm:gap-4 bg-gradient-to-r from-purple-500/20 to-gold-500/20 border border-purple-500/30 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-xl backdrop-blur-md max-w-full">
              <div className="flex items-center gap-2">
                <Crown className="w-5 h-5 text-yellow-400 shrink-0" />
                <span className="font-medium text-sm sm:text-base">Start Free Trial</span>
              </div>
              <Button
                onClick={async () => {
                  if (!user) { setShowLoginModal(true); return; }
                  const result = await startTrial();
                  if (result.success) {
                    toast({ title: 'Trial Started!', description: result.message });
                  } else {
                    toast({ title: 'Error', description: result.message, variant: 'destructive' });
                  }
                }}
                className="bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700 text-white font-semibold px-4 sm:px-6 py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap"
                data-testid="start-trial-btn"
              >
                22 Days Free — Start Now
              </Button>
            </div>
          </div>
        )}

        {/* Premium Upgrade Modal */}
        {showPremiumModal && (
          <UpgradeModal
            onClose={() => setShowPremiumModal(false)}
            onSuccess={() => {
              setShowPremiumModal(false);
              checkSubscriptionStatus();
            }}
          />
        )}

        {/* Compact header — brand · mode toggle · actions in one slim row */}
        <header className="py-2 sm:py-3 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="app-header">
          <h1
            className={`text-lg sm:text-2xl font-bold whitespace-nowrap ${theme.isCustom ? '' : `bg-gradient-to-r ${theme.headerGradient} bg-clip-text text-transparent`}`}
            style={theme.isCustom ? theme.headerGradientStyle : undefined}
            title="Advanced Brainwave Entrainment & Neural Synchronization"
          >
            NeuroWave One
          </h1>

          {/* Simple / Advanced mode toggle */}
          <div className="inline-flex items-center gap-0.5 bg-white/[0.04] border border-white/10 rounded-full p-0.5 text-xs" data-testid="ui-mode-toggle-wrapper">
            <button
              type="button"
              onClick={() => setUiModePersisted('simple')}
              className={`px-3 py-1 rounded-full transition-all ${
                uiMode === 'simple'
                  ? 'bg-gradient-to-r from-purple-600/90 to-cyan-600/90 text-white shadow-sm'
                  : 'text-white/55 hover:text-white/80'
              }`}
              data-testid="ui-mode-simple"
              title="Hide advanced panels — just preset + play"
            >
              Simple
            </button>
            <button
              type="button"
              onClick={() => setUiModePersisted('advanced')}
              className={`px-3 py-1 rounded-full transition-all ${
                uiMode === 'advanced'
                  ? 'bg-gradient-to-r from-purple-600/90 to-cyan-600/90 text-white shadow-sm'
                  : 'text-white/55 hover:text-white/80'
              }`}
              data-testid="ui-mode-advanced"
              title="Show all audio engine controls"
            >
              Advanced
            </button>
          </div>

          <div className="ml-auto flex items-center gap-1 sm:gap-1.5">
            <Button
              onClick={() => setShowAchievements(true)}
              variant="outline"
              size="sm"
              className={`${theme.badgeBtn} text-white hover:opacity-80 text-xs h-8 px-2 sm:px-2.5`}
              title="Badges"
              data-testid="achievements-btn"
            >
              <Trophy className="w-3.5 h-3.5 text-yellow-400 sm:mr-1" />
              <span className="hidden sm:inline">Badges</span>
            </Button>
            <Button
              onClick={() => setShowStats(true)}
              variant="outline"
              size="sm"
              className={`${theme.statsBtn} text-white hover:opacity-80 text-xs h-8 px-2 sm:px-2.5`}
              style={theme.isCustom ? theme.statsBtnStyle : undefined}
              title="Session stats"
              data-testid="stats-btn"
            >
              <TrendingUp className="w-3.5 h-3.5 sm:mr-1" />
              <span className="hidden sm:inline">Stats</span>
              {totalStats.totalSessions > 0 && (
                <span className="ml-1 px-1.5 py-0.5 bg-purple-600 rounded-full text-[10px]">
                  {totalStats.totalSessions}
                </span>
              )}
            </Button>
            {isPremium && (
              <Button
                onClick={() => setShowAISessionCreator(true)}
                className={`${theme.accentBtn} text-white text-xs h-8 px-2 sm:px-2.5`}
                style={theme.isCustom ? theme.accentBtnStyle : undefined}
                size="sm"
                title="AI Session Creator"
              >
                <Crown className="w-3.5 h-3.5 sm:mr-1" />
                <span className="hidden sm:inline">AI Creator</span>
              </Button>
            )}
            <Button
              onClick={() => setShowHelpManual(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white h-8 w-8 p-0 shrink-0"
              size="sm"
              title="Help & manual"
              data-testid="help-btn"
            >
              <HelpCircle className="w-4 h-4" />
            </Button>
            <InstallAppButton
              className={`${theme.toggleBg} text-white h-8 w-8 p-0 shrink-0`}
              title="Install as app"
            />
            {!user && (
              <Button
                onClick={() => setShowLoginModal(true)}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-8"
                size="sm"
                data-testid="header-signin-btn"
              >
                Sign In
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className={`${theme.toggleBg} text-white h-8 w-8 p-0 shrink-0`}
                  title="More options"
                  data-testid="header-more-btn"
                >
                  <MoreHorizontal className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" sideOffset={6} className="w-60 bg-[#0d1224]/95 backdrop-blur-md border-white/10 text-white z-[60]">
                <DropdownMenuItem onClick={cycleTheme} className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer" data-testid="theme-toggle-btn">
                  <span className="w-4 text-center">{theme.icon}</span> Theme: {theme.name}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (!user) { setShowLoginModal(true); return; }
                    if (!isPremium) { setShowPremiumModal(true); return; }
                    setEditingTheme(theme.isCustom ? theme : null);
                    setShowThemeBuilder(true);
                  }}
                  className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer"
                  data-testid="theme-builder-btn"
                >
                  <Palette className="w-4 h-4" /> Custom theme
                  {!isPremium && <Crown className="w-3.5 h-3.5 text-yellow-400 ml-auto" />}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowSocialShare(true)} className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer" data-testid="social-share-btn">
                  <Share2 className="w-4 h-4" /> Share progress
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => {
                    if (!user) { setShowLoginModal(true); return; }
                    setShowReferral(true);
                  }}
                  className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer"
                  data-testid="referral-btn"
                >
                  <Gift className="w-4 h-4" /> Refer a friend
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={() => setShowOnboardingTour(true)} className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer" data-testid="restart-tour-btn">
                  <Sparkles className="w-4 h-4" /> Restart tour
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => { setSafetyMode('view'); setShowSafety(true); }} className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer text-amber-300 focus:text-amber-200" data-testid="view-safety-btn">
                  <AlertTriangle className="w-4 h-4" /> Safety info
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={async () => {
                    const opened = await openFeedbackDialog();
                    if (!opened) {
                      window.location.href = 'mailto:selfheal222@msn.com?subject=NeuroWave%20bug%20report';
                    }
                  }}
                  className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer"
                  data-testid="report-bug-btn"
                >
                  <Bug className="w-4 h-4" /> Report a bug
                </DropdownMenuItem>
                {user && (
                  <>
                    <DropdownMenuSeparator className="bg-white/10" />
                    <DropdownMenuItem onClick={() => setShowEmailPrefs(true)} className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer" data-testid="email-prefs-btn">
                      <Mail className="w-4 h-4" /> Email preferences
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={logout} className="gap-2 focus:bg-white/10 focus:text-white cursor-pointer" data-testid="logout-btn">
                      <LogOut className="w-4 h-4" /> Sign out
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Referral banner — only shown to logged-in users with remaining invites */}
        <ReferralBanner user={user} onOpenPanel={() => setShowReferral(true)} />


        {/* Coherence Mode CTA — one-tap launcher for the Heartbeat + HRV +
            Breath Pacer combo. Visible in both Simple and Advanced modes. */}
        <div className="flex items-center justify-center mb-3">
          <button
            type="button"
            onClick={startCoherenceSession}
            data-testid="coherence-mode-cta"
            className="group inline-flex items-center gap-2 px-4 py-2 rounded-full border border-rose-300/40 bg-gradient-to-r from-rose-500/10 via-pink-500/15 to-rose-500/10 hover:border-rose-300/70 hover:from-rose-500/20 hover:via-pink-500/25 hover:to-rose-500/20 transition-all text-sm text-white shadow-sm"
            title="Heartbeat + Breath Pacer at resonance frequency, 10-minute session"
          >
            <span className="text-base leading-none">💓</span>
            <span className="font-medium">Try Coherence Mode</span>
            <span className="text-xs text-white/60 group-hover:text-white/80 hidden sm:inline">
              · 10 min · breath at 5.5 bpm
            </span>
          </button>
        </div>

        {/* "What do you need right now?" — three-tile quick-pick row.
            Collapses cold-visit-to-first-session from library browse to one
            tap. Time-of-day auto-suggests one tile with a "Recommended"
            pill. Hidden in Advanced mode — power users navigate via the
            preset library, not tiles. See SessionQuickPickTiles.jsx. */}
        {uiMode === 'simple' && (
          <SessionQuickPickTiles
            onStartFocus={startFocusSession}
            onStartCalm={startCalmSession}
            onStartSleep={startSleepSession}
          />
        )}

        {/* Main Controls + Timeline — simple mode pairs the card grid with the
            Timeline side-by-side on xl screens; advanced mode uses CSS columns
            (masonry) so cards take their natural height with no dead air */}
        <div className={uiMode === 'simple' ? 'space-y-6 xl:space-y-0 xl:grid xl:grid-cols-2 xl:gap-6 xl:items-start' : 'space-y-6'}>
        <div className={uiMode === 'simple'
          ? 'grid grid-cols-1 lg:grid-cols-2 gap-6 max-w-5xl mx-auto xl:max-w-none xl:mx-0'
          : 'columns-1 lg:columns-2 xl:columns-3 gap-6'}>
          {/* Preset Management - FIRST POSITION FOR PROMINENCE */}
          <Card className={`${cardClass} lg:order-1 ring-2 ${theme.cardAccentBorder} ${uiMode === 'advanced' ? 'break-inside-avoid mb-6' : ''}`} data-testid="preset-card" style={theme.isCustom ? { ...theme.cardBorderStyle, ...theme.cardAccentStyle } : undefined}>
            <CardHeader className={theme.presetHeader} style={theme.isCustom ? theme.presetHeaderStyle : undefined}>
              <CardTitle className={`${textClass} flex items-center gap-2 text-xl`}>
                <span className="text-2xl">🎵</span>
                Preset Library
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <RecentSessionsStrip
                history={sessionHistory}
                presets={presets}
                onQuickStart={(preset) => loadPreset(preset.id, preset)}
              />
              <PresetManagementPanel
                presets={presets}
                currentPreset={currentPreset}
                presetName={presetName}
                onPresetNameChange={setPresetName}
                loading={loading}
                canSavePresets={isPremium}
                canExportAudio={isPremium}
                canDeletePreset={canDeletePreset}
                onSavePreset={savePreset}
                onLoadPreset={loadPreset}
                onEditPreset={handleEditPreset}
                onDeletePreset={handleDeletePreset}
                onExportClick={handleExportClick}
                onSharePreset={handleSharePreset}
                shareLoading={shareLoading}
                copiedShareId={copiedShareId}
              />
            </CardContent>
          </Card>
          {/* Audio Controls */}
          <Card className={`${cardClass} lg:order-2 ${uiMode === 'advanced' ? 'break-inside-avoid mb-6' : ''}`} data-testid="audio-controls-card" style={theme.isCustom ? theme.cardBorderStyle : undefined}>
            <CardHeader>
              <CardTitle className={textClass}>Audio Controls</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4" id="audio-controls">
              {/* Recording Indicator */}
              {isRecording && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-3 mb-3 animate-pulse">
                  <div className="flex items-center gap-2 text-red-300">
                    <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-semibold">Recording in Progress</span>
                  </div>
                  <div className="text-xs text-red-200 mt-1">
                    Press Stop to save the recording
                  </div>
                </div>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                {/* Combined Play/Pause Button */}
                <Button
                  onClick={isPlaying ? pauseAudio : startAudio}
                  disabled={loading || isStarting}
                  className={`${isPlaying ? 'bg-yellow-600 hover:bg-yellow-700' : 'bg-green-600 hover:bg-green-700'} px-4 py-3 text-sm sm:text-base font-semibold whitespace-nowrap`}
                  data-testid="play-btn"
                >
                  {isPlaying ? (
                    <>
                      <Pause className="w-5 h-5 mr-1 sm:mr-2 flex-shrink-0" />
                      <span className="truncate">Pause</span>
                    </>
                  ) : (
                    <>
                      <Play className="w-5 h-5 mr-1 sm:mr-2 flex-shrink-0" />
                      <span className="truncate">{isPaused ? 'Resume' : 'Play'}</span>
                    </>
                  )}
                </Button>
                
                {/* Stop Button */}
                <Button
                  onClick={stopAudio}
                  disabled={(!isPlaying && !isPaused) || loading || isStarting}
                  className={`${isRecording ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-red-600 hover:bg-red-700'} text-white border-red-500 px-4 py-3 text-sm sm:text-base font-semibold whitespace-nowrap`}
                  data-testid="stop-btn"
                >
                  <Square className="w-5 h-5 mr-1 sm:mr-2 flex-shrink-0" />
                  <span className="truncate">{isRecording ? 'Stop & Save' : 'Stop'}</span>
                </Button>
              </div>
              
              {/* Session Timer Display */}
              <SessionTimerDisplay
                timeRemaining={sessionTimer.timeRemaining}
                duration={duration}
                isActive={isPlaying}
                progress={duration > 0 ? ((1 - sessionTimer.timeRemaining / duration) * 100) : 0}
                formattedTime={`${Math.floor(sessionTimer.timeRemaining / 60)}:${String(sessionTimer.timeRemaining % 60).padStart(2, '0')}`}
                formattedDuration={`${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}`}
              />
              
              <div className="space-y-2">
                <label className={textClass + ' text-sm'}>Volume</label>
                <Slider
                  value={volume}
                  onValueChange={updateVolume}
                  max={1}
                  min={0}
                  step={0.05}
                  className="text-white"
                />
                <div className="text-white/70 text-xs text-center">
                  {Math.round(volume[0] * 100)}%
                </div>
              </div>
              
              <div className="space-y-2">
                <label className={textClass + ' text-sm'}>Duration (minutes)</label>
                <Input
                  type="number"
                  value={Math.round(duration / 60 * 10) / 10}
                  onChange={(e) => setDuration(Number(e.target.value) * 60)}
                  onFocus={(e) => e.target.select()} // Auto-select all digits on focus
                  onClick={(e) => e.target.select()} // Also select on click
                  className="bg-white/20 border-white/30 text-white"
                  step="0.5"
                  min="0.5"
                />
              </div>

              {/* Sleep Timer — collapsible */}
              <CollapsibleSection
                id="sleep_timer"
                title="Sleep Timer"
                icon={<Moon className="w-3.5 h-3.5" />}
                testId="sleep-timer"
                badge={sleepTimerMinutes ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-indigo-500/30 text-indigo-200">{sleepTimerMinutes}m</span>
                ) : null}
              >
                <div className="flex gap-1.5 flex-wrap">
                  {[15, 30, 45, 60, 90].map(min => (
                    <button
                      key={min}
                      data-testid={`sleep-timer-${min}`}
                      onClick={() => startSleepTimer(sleepTimerMinutes === min ? null : min)}
                      className={`px-2.5 py-1 text-xs rounded-full transition-colors ${
                        sleepTimerMinutes === min
                          ? 'bg-indigo-500 text-white'
                          : 'bg-white/10 text-white/70 hover:bg-white/20'
                      }`}
                    >
                      {min}m
                    </button>
                  ))}
                </div>
                {sleepTimerRemaining != null && sleepTimerRemaining > 0 && (
                  <div className="flex items-center justify-between text-xs text-white/60 mt-1">
                    <span>Auto-sleep in</span>
                    <span className="font-mono text-white/80">
                      {Math.floor(sleepTimerRemaining / 60)}:{String(sleepTimerRemaining % 60).padStart(2, '0')}
                    </span>
                  </div>
                )}
              </CollapsibleSection>

              {/* Recording Controls */}
              <RecordingControls
                isRecording={isRecording}
                isPremium={isPremium}
                isPlaying={isPlaying}
                filename={exportFilename}
                formattedDuration={recording.formattedDuration}
                exportFormat={exportFormat}
                onFilenameChange={setExportFilename}
                onExportFormatChange={setExportFormat}
                onStartRecording={startRecording}
                onStopRecording={() => stopRecordingAndDownload(exportFilename || `neurowave-session-${Date.now()}`)}
                onDownload={() => stopRecordingAndDownload(exportFilename || `neurowave-session-${Date.now()}`)}
              />
            </CardContent>
          </Card>

          {/* Background Audio Controls — Advanced only */}
          {uiMode === 'advanced' && (
          <Card className={`${cardClass} lg:order-3 break-inside-avoid mb-6`} data-testid="bg-audio-card" style={theme.isCustom ? theme.cardBorderStyle : undefined}>
            <CardHeader>
              <CardTitle className={textClass}>Background Audio</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Upload / background file — collapsible */}
              <CollapsibleSection
                id="bg_upload"
                title="Upload Audio"
                testId="bg-upload-section"
                badge={backgroundAudioFile ? (
                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-200 truncate max-w-[8rem]">{backgroundAudioFile.name}</span>
                ) : null}
              >
              {/* File Upload Section — input + drag-and-drop dropzone */}
              <div className="space-y-2">
                <div
                  data-testid="background-audio-dropzone"
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setBgDragActive(true); }}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (!bgDragActive) setBgDragActive(true); }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    // Only deactivate when leaving the dropzone itself, not its children
                    if (e.currentTarget.contains(e.relatedTarget)) return;
                    setBgDragActive(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setBgDragActive(false);
                    const file = e.dataTransfer?.files?.[0];
                    if (file) processBackgroundAudioFile(file);
                  }}
                  className={`rounded-md border border-dashed transition-colors px-3 py-3 ${
                    bgDragActive
                      ? 'border-cyan-300 bg-cyan-300/10'
                      : 'border-white/30 bg-white/5'
                  }`}
                >
                  <Input
                    type="file"
                    accept="audio/*"
                    onChange={handleBackgroundAudioUpload}
                    className="bg-white/20 border-white/30 text-white file:bg-white/20 file:border-0 file:text-white file:rounded"
                    data-testid="background-audio-file-input"
                  />
                  <p className="mt-2 text-xs text-white/60 text-center">
                    {bgDragActive ? 'Drop your audio file to upload' : 'or drag & drop an audio file here · paste with ⌘V / Ctrl-V'}
                  </p>
                </div>

                {/* Recently used — IndexedDB-backed quick-switcher */}
                {recentBgAudio.length > 0 && (
                  <div className="space-y-1.5" data-testid="recent-background-audio">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-white/60">Recently used</div>
                      {recentBgAudio.some((r) => !r.pinned) && (
                        <button
                          type="button"
                          onClick={clearUnpinnedBackgroundAudio}
                          data-testid="recent-bg-clear-unpinned"
                          className="text-xs text-white/50 hover:text-white/90 underline-offset-2 hover:underline"
                        >
                          Clear unpinned
                        </button>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {recentBgAudio.map((r) => (
                        <div
                          key={r.id}
                          draggable={r.pinned}
                          onDragStart={r.pinned ? (e) => handleRecentDragStart(r.id, e) : undefined}
                          onDragOver={r.pinned ? (e) => handleRecentDragOver(r.id, e) : undefined}
                          onDragEnd={r.pinned ? handleRecentDragEnd : undefined}
                          onDrop={r.pinned ? (e) => handleRecentDrop(r.id, e) : undefined}
                          data-testid={`recent-bg-wrap-${r.id}`}
                          className={`group flex items-center rounded-full border transition-colors text-xs max-w-[14rem] ${
                            r.pinned
                              ? `border-amber-300/50 bg-amber-300/10 text-amber-100 hover:bg-amber-300/20 cursor-grab active:cursor-grabbing ${bgDragOverId === r.id ? 'ring-2 ring-amber-300' : ''}`
                              : 'border-white/20 bg-white/5 text-white/90 hover:bg-white/15 hover:border-white/40'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => loadRecentBackgroundAudio(r.id)}
                            data-testid={`recent-bg-pill-${r.id}`}
                            title={`${r.name} · ${(r.size / 1024 / 1024).toFixed(1)} MB${r.pinned ? ' · pinned (drag to reorder)' : ''}`}
                            className="truncate pl-2.5 pr-1 py-1 max-w-[10rem]"
                          >
                            {r.name}
                          </button>
                          <button
                            type="button"
                            aria-label={r.pinned ? `Unpin ${r.name}` : `Pin ${r.name} — won't be auto-evicted`}
                            aria-pressed={r.pinned}
                            onClick={(e) => togglePinRecentBackgroundAudio(r.id, e)}
                            data-testid={`recent-bg-pin-${r.id}`}
                            className={`rounded-full p-0.5 transition-colors ${
                              r.pinned
                                ? 'text-amber-300 hover:text-amber-200'
                                : 'text-white/40 hover:text-amber-300'
                            }`}
                          >
                            <Star className="h-3 w-3" fill={r.pinned ? 'currentColor' : 'none'} />
                          </button>
                          <button
                            type="button"
                            aria-label={`Remove ${r.name} from recents`}
                            onClick={(e) => removeRecentBackgroundAudio(r.id, e)}
                            data-testid={`recent-bg-remove-${r.id}`}
                            className="rounded-full p-0.5 mr-1 text-white/40 hover:text-white/90 hover:bg-white/10"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {backgroundAudioFile && (
                <div className="space-y-2">
                  <div className="text-white/80 text-sm">
                    📁 {backgroundAudioFile.name}
                  </div>
                  
                  <Button
                    onClick={toggleBackgroundAudio}
                    disabled={!backgroundAudio}
                    className={`w-full ${backgroundAudioEnabled ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                  >
                    {backgroundAudioEnabled ? 'Stop Background' : 'Play Background'}
                  </Button>
                  
                  <div className="space-y-2">
                    <label className={textClass + ' text-sm'}>
                      {liveAudioMode ? 'Live Audio Volume' : 'Background Volume'}
                      {liveAudioMode && <span className="text-xs text-gray-400 ml-2">(Set to 0 to prevent echo)</span>}
                    </label>
                    <Slider
                      value={backgroundAudioVolume}
                      onValueChange={updateBackgroundVolume}
                      max={1}
                      min={0}
                      step={0.1}
                      className="text-white"
                    />
                    <div className="text-white/70 text-xs text-center">
                      {Math.round(backgroundAudioVolume[0] * 100)}%
                    </div>
                  </div>
                  
                  {/* Background Audio Seek Control */}
                  {backgroundAudioFile && backgroundAudioDuration > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <label className={textClass + ' text-sm'}>Audio Position</label>
                        <span className="text-white/70 text-xs">
                          {formatTime(backgroundAudioCurrentTime)} / {formatTime(backgroundAudioDuration)}
                        </span>
                      </div>
                      <Slider
                        value={getBackgroundAudioProgress()}
                        onValueChange={handleBackgroundAudioSeek}
                        max={100}
                        min={0}
                        step={0.1}
                        className="text-blue-400"
                      />
                      <div className="flex justify-between text-white/60 text-xs">
                        <span>0:00</span>
                        <span className="text-blue-300">
                          {backgroundAudioEnabled ? '⏵️ Playing' : '⏸️ Paused'}
                        </span>
                        <span>{formatTime(backgroundAudioDuration)}</span>
                      </div>
                    </div>
                  )}
                  
                </div>
              )}
              
              {!backgroundAudioFile && (
                <div className="text-white/60 text-sm text-center py-2">
                  Upload an MP3 file to play ambient sounds during your brainwave session
                </div>
              )}
              </CollapsibleSection>

              {/* Synth Ambient Sound Generator - Using new component */}
              <SynthAmbientPanel
                isEnabled={synthAmbient.isEnabled}
                currentType={synthAmbient.currentType}
                volume={synthAmbient.volume}
                presets={SYNTH_PRESETS}
                onToggle={toggleSynthAmbient}
                onTypeChange={changeSynthAmbientType}
                onVolumeChange={updateSynthAmbientVolume}
                disabled={!audioContextRef.current}
              />
              
              {/* Live Audio Input Section */}
              <LiveAudioPanel
                isOnMobileDevice={isOnMobileDevice}
                liveAudioMode={liveAudioMode}
                onToggleLiveAudio={toggleLiveAudioMode}
                showDeviceSettings={showDeviceSettings}
                onToggleDeviceSettings={() => setShowDeviceSettings(!showDeviceSettings)}
                onShowSetupInstructions={() => setShowSetupInstructions(true)}
                liveInputLevel={liveAudioLevel}
                audioInputDevices={audioInputDevices}
                audioOutputDevices={audioOutputDevices}
                selectedInputDevice={selectedInputDevice}
                onInputDeviceChange={setSelectedInputDevice}
                selectedOutputDevice={selectedOutputDevice}
                onOutputDeviceChange={setOutputDevice}
                onChangeLiveDevice={changeLiveAudioDevice}
                onListAudioDevices={listAudioDevices}
                liveAudioMonitoring={liveAudioMonitoring}
                onToggleMonitoring={() => setLiveAudioMonitoring(!liveAudioMonitoring)}
                onDeviceChangeToast={() => toast({ title: "Device Changed", description: "Please restart live input to use the new device" })}
              />
              
              {/* 8D Audio Controls - Using new component */}
              <EightDControlsPanel
                isEnabled={is8DBackgroundAudio}
                isPremium={isPremium}
                hasAudioSource={!!backgroundAudioFile || liveAudioMode}
                pathType={eightDPath}
                panningSpeed={eightDPanningSpeed}
                radius={eightDRadius}
                reverbMix={eightDReverbMix}
                clarityBoost={eightDClarityBoost}
                performanceMode={performance8DMode}
                onToggle={() => {
                  if (!isPremium) {
                    setShowPremiumModal(true);
                    toast({
                      title: "Premium Feature",
                      description: "8D Audio effects are available for Premium users only.",
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  if (!backgroundAudio && !liveAudioMode) {
                    toast({
                      title: "No Audio Source",
                      description: "Please upload audio or enable live input first.",
                      variant: "destructive",
                    });
                    return;
                  }
                  
                  const newState = !is8DBackgroundAudio;
                  setIs8DBackgroundAudio(newState);
                  
                  // Handle routing (keeping existing logic)
                  if (backgroundAudio && backgroundAudioEnabled) {
                    const source = getSingleMediaElementSource(backgroundAudio);
                    if (source) {
                      source.disconnect();
                      if (background8DNodes.current) {
                        Object.values(background8DNodes.current).forEach(node => {
                          if (node?.disconnect) try { node.disconnect(); } catch (e) {}
                        });
                        background8DNodes.current = null;
                      }
                      
                      if (newState && isPremium) {
                        const effect8D = create8DBackgroundEffect(backgroundAudio, true);
                        if (effect8D?.inputGain) {
                          source.connect(effect8D.inputGain);
                          background8DNodes.current = effect8D;
                          effect8D.angle = 0;
                          animate8DBackground(true);
                        } else {
                          source.connect(backgroundGainRef.current);
                        }
                      } else {
                        source.connect(backgroundGainRef.current);
                      }
                    }
                  } else if (liveAudioMode) {
                    setupLiveAudioRouting();
                  }
                  
                }}
                onPathTypeChange={setEightDPath}
                onPanningSpeedChange={setEightDPanningSpeed}
                onRadiusChange={setEightDRadius}
                onReverbMixChange={setEightDReverbMix}
                onClarityBoostChange={setEightDClarityBoost}
                onPerformanceModeToggle={() => {
                  const newMode = !performance8DMode;
                  setPerformance8DMode(newMode);
                }}
              />
            </CardContent>
          </Card>
          )}

          {/* Beat Settings — Advanced only */}
          {uiMode === 'advanced' && (
          <Card className={`${cardClass} lg:order-4 break-inside-avoid mb-6`} data-testid="beat-settings-card" style={theme.isCustom ? theme.cardBorderStyle : undefined}>
            <CardHeader>
              <CardTitle className={textClass}>Beat Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <BeatSettingsPanel
                beatType={beatType}
                baseFrequency={baseFrequency}
                brainwaveState={{
                  name: currentBrainwave.name,
                  color: currentBrainwave.color,
                  frequency: currentFreq,
                  description: beatType === 'heartbeat' ? 'Cardiac rhythm entrainment' : getBrainwaveDescription(currentBrainwave.type)
                }}
                onBeatTypeChange={setBeatType}
                onBaseFrequencyChange={setBaseFrequency}
                coherence={coherence}
                onCoherenceChange={setCoherence}
                breathPacerEnabled={breathPacerEnabled}
                onBreathPacerToggle={setBreathPacerEnabled}
                breathPacerBpm={breathPacerBpm}
                onBreathPacerBpmChange={setBreathPacerBpm}
                breathPacerIntroPulse={breathPacerIntroPulse}
                isPlaying={isPlaying}
              />
              {/* Live heart-rate monitor (Web Bluetooth). Component is a no-op
                  on unsupported browsers (Safari/Firefox) so the card stays
                  clean for those users. */}
              <HeartMonitorPanel
                monitor={heartMonitor}
                beatType={beatType}
                syncHeartbeatToUser={syncHeartbeatToUser}
                onToggleSyncHeartbeatToUser={setSyncHeartbeatToUser}
                onUpgradeClick={() => setProModalOpen(true)}
              />
              <HrvDashboard />
            </CardContent>
          </Card>
          )}

        </div>

        {/* Timeline */}
        <Card className={cardClass} data-testid="timeline-card" style={theme.isCustom ? theme.cardBorderStyle : undefined}>
          <CardHeader>
            <CardTitle className={textClass}>Timeline</CardTitle>
          </CardHeader>
          <CardContent>
            <Timeline
              nodes={timelineNodes}
              setNodes={setTimelineNodes}
              duration={duration}
              currentTime={currentTime}
              setCurrentTime={setCurrentTime}
              brainwaveRanges={brainwaveRanges}
              isPlaying={isPlaying || isPaused}
              currentBrainwave={currentBrainwave}
              beatType={beatType}
              baseFrequency={baseFrequency}
              getCurrentFrequency={getCurrentFrequency}
              audioContextRef={audioContextRef}
              oscillatorsRef={oscillatorsRef}
              isPaused={isPaused}
              pauseAnimationFrame={pauseAnimationFrame}
              resumeAnimationFrame={resumeAnimationFrame}
              updateStartTimeForManualSeek={updateStartTimeForManualSeek}
              volumeNodes={volumeNodes}
              setVolumeNodes={setVolumeNodes}
              gainNodeRef={gainNodeRef}
            />
          </CardContent>
        </Card>
        </div>

        {/* Brainwave Reference — card background subtly tints toward the
            active band's colour so the whole panel takes on a "mood" that
            follows the brain state. Kept at very low alpha (≈6%) so it
            never overwhelms the content; 900ms ease-out transition smooths
            the hue shift between bands. Skipped in heartbeat mode (the
            band-tint metaphor doesn't apply to heart-rate zones). */}
        {(() => {
          const tintColor = beatType !== 'heartbeat' ? currentBrainwave?.color : null;
          const tintStyle = tintColor
            ? {
                backgroundColor: `${tintColor}0F`, // ~6% alpha
                transition: 'background-color 900ms ease-out, border-color 900ms ease-out',
              }
            : { transition: 'background-color 900ms ease-out, border-color 900ms ease-out' };
          const mergedStyle = theme.isCustom
            ? { ...theme.cardBorderStyle, ...tintStyle }
            : tintStyle;
          return (
            <Card
              className={cardClass}
              style={mergedStyle}
              data-testid="brainwave-reference-card"
            >
              <CardHeader>
                <CardTitle className={textClass}>
                  {beatType === 'heartbeat' ? 'Heart Rate Zones' : 'Brainwave States'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BrainwaveReferenceCard
                  beatType={beatType}
                  brainwaveRanges={brainwaveRanges}
                  currentBrainwave={currentBrainwave}
                  currentFreq={currentFreq}
                  getBrainwaveDescription={getBrainwaveDescription}
                />
              </CardContent>
            </Card>
          );
        })()}
      </div>

      {/* Export Save Dialog */}
      <ExportSaveDialog
        isVisible={showSaveDialog}
        filename={exportFilename}
        onFilenameChange={setExportFilename}
        onClose={() => setShowSaveDialog(false)}
        onExport={() => {
          setShowSaveDialog(false);
          exportAudio(exportFilename);
        }}
      />

      {/* Delete Preset Confirmation Dialog */}
      <DeleteConfirmDialog
        isVisible={showDeleteConfirm}
        preset={presetToDelete}
        loading={loading}
        onClose={() => {
          setShowDeleteConfirm(false);
          setPresetToDelete(null);
        }}
        onDelete={deletePreset}
      />
      
      {/* Mobile transport bar — always-visible Play/Stop + volume on phones */}
      <MobileTransportBar
        isPlaying={isPlaying}
        disabled={loading || isStarting}
        stopDisabled={(!isPlaying && !isPaused) || loading || isStarting}
        onPlayPause={isPlaying ? pauseAudio : startAudio}
        onStop={stopAudio}
        volume={volume}
        onVolumeChange={updateVolume}
        timeLabel={(isPlaying || isPaused)
          ? `${Math.floor(sessionTimer.timeRemaining / 60)}:${String(sessionTimer.timeRemaining % 60).padStart(2, '0')}`
          : `${Math.floor(duration / 60)}:${String(Math.round(duration % 60)).padStart(2, '0')}`}
        stateName={beatType === 'heartbeat' ? 'Heartbeat' : currentBrainwave?.name}
        stateColor={beatType === 'heartbeat' ? '#f43f5e' : currentBrainwave?.color}
      />
      
      {/* Help Manual */}
      {showHelpManual && (
        <HelpManual onClose={() => setShowHelpManual(false)} />
      )}
      
      {/* AI Session Creator */}
      {showAISessionCreator && (
        <AISessionCreator 
          onSessionGenerated={handleAISessionGenerated}
          onClose={() => setShowAISessionCreator(false)} 
        />
      )}
      
      {/* Session Stats Modal */}
      {showStats && (
        <SessionStats
          stats={totalStats}
          history={sessionHistory}
          onClose={() => setShowStats(false)}
        />
      )}
      
      {/* Device Selector Modal */}
      <DeviceSelectorModal
        isVisible={showDeviceSelector}
        devices={availableDevices}
        selectedDeviceId={selectedDeviceId}
        onClose={() => setShowDeviceSelector(false)}
        onSelectDevice={selectAudioDevice}
      />
      
      {/* Setup Instructions Modal */}
      <SetupInstructionsModal
        isVisible={showSetupInstructions}
        onClose={() => setShowSetupInstructions(false)}
      />
      
      {/* Achievements Modal */}
      {showAchievements && (
        <AchievementsPanel
          history={sessionHistory}
          onClose={() => setShowAchievements(false)}
        />
      )}

      {/* Social Share Modal */}
      {showSocialShare && (
        <SocialSharePanel
          stats={totalStats}
          achievements={ACHIEVEMENTS.filter(a => {
            const progress = computeProgress(sessionHistory);
            return (progress[a.type] || 0) >= a.target;
          })}
          onClose={() => setShowSocialShare(false)}
        />
      )}

      {/* Onboarding — non-blocking toast; full tour opens on click */}
      <WelcomeToast onTakeTour={() => setShowOnboardingTour(true)} />
      {showOnboardingTour && (
        <OnboardingTour onComplete={() => setShowOnboardingTour(false)} />
      )}

      {/* Safety disclaimer — first-time gate or re-openable from settings */}
      {showSafety && (
        <SafetyDisclaimer
          allowDismiss={safetyMode === 'view'}
          onAcknowledge={() => setShowSafety(false)}
          onClose={() => setShowSafety(false)}
        />
      )}

      {/* Theme Builder Modal */}
      {showThemeBuilder && (
        <ThemeBuilderModal
          onClose={() => { setShowThemeBuilder(false); setEditingTheme(null); }}
          editTheme={editingTheme}
        />
      )}

      {/* Referral Panel */}
      {showReferral && (
        <ReferralPanel onClose={() => setShowReferral(false)} />
      )}

      {/* Email Preferences */}
      <EmailPreferencesModal
        open={showEmailPrefs}
        onClose={() => setShowEmailPrefs(false)}
      />

      {/* Pro (HRV add-on) upsell — opens from HR panel's pricing teaser */}
      <ProUpgradeModal open={proModalOpen} onClose={() => setProModalOpen(false)} />

      {/* Post-session mood rating sheet — auto-opens on completed session */}
      <PostSessionMoodSheet
        open={moodSheetOpen}
        context={moodSheetContext}
        onClose={() => { setMoodSheetOpen(false); setMoodSheetContext(null); }}
        onSubmitted={() => {
          // Thin toast so the user knows it landed. Dashboard picks it up
          // on next expand — we don't proactively reload it here because
          // the user may not even have it open right now.
          toast({ title: 'Thanks for rating!', description: 'Your trend updates in the HRV dashboard.' });
        }}
      />

      {/* Login Modal */}
      {showLoginModal && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto"
          onClick={(e) => { if (e.target === e.currentTarget) setShowLoginModal(false); }}
          data-testid="login-modal-overlay"
        >
          <div className="relative w-full max-w-md">
            <button
              onClick={() => setShowLoginModal(false)}
              aria-label="Close login"
              className="absolute top-2 right-2 z-10 h-8 w-8 flex items-center justify-center rounded-full bg-black/40 text-white/60 hover:text-white hover:bg-black/60 transition-colors"
              data-testid="login-modal-close"
            >
              <X className="w-4 h-4" />
            </button>
            <LoginPage />
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default BrainwaveGenerator;
